export const KNOWLEDGE_BASE_DATA = [
  {
    "id": "dashboard",
    "title": "Dashboard",
    "description": "Central hub for daily clinic operations across Appointments, Requests, and Facilities.",
    "topics": [
      {
        "id": "topic-dashboard-overview",
        "title": "Overview",
        "description": "Primary navigation tabs for Appointments, Requests, and Facilities.",
        "groups": [
          {
            "id": "group-dashboard-overview",
            "title": "Primary Navigation",
            "description": "Top-level tabs that organize the Dashboard workspace.",
            "items": [
              {
                "id": "dashboard-overview",
                "title": "Dashboard Overview",
                "summary": "The Dashboard module serves as the central hub for managing daily clinic operations across Appointments, Requests, and Facilities.",
                "workflow": "Dashboard",
                "details": [
                  {
                    "type": "heading",
                    "title": "Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/outpatient-dashboard-01.png",
                    "alt": "Dashboard Overview"
                  },
                  {
                    "type": "text",
                    "content": "The Dashboard module serves as the central hub for managing daily clinic operations. It is divided into three primary sections, accessible via the clickable tabs in the top navigation row."
                  },
                  {
                    "type": "fields",
                    "title": "Primary Navigation Tabs",
                    "items": [
                      {
                        "name": "Appointments (Tab)",
                        "desc": "Opens the appointment management workspace. By default, this tab automatically opens to the \"Outpatient\" dashboard.",
                        "helper": ""
                      },
                      {
                        "name": "Requests (Tab)",
                        "desc": "Accesses the centralized list of system requests requiring review or approval.",
                        "helper": ""
                      },
                      {
                        "name": "Facilities (Tab)",
                        "desc": "Opens the facilities management workspace.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-dashboard-appointments",
        "title": "Appointments",
        "description": "Appointment dashboards, booking tools, and schedule view layouts.",
        "groups": [
          {
            "id": "group-dashboard-appointments-views",
            "title": "View Options",
            "description": "Switch how the daily schedule is displayed.",
            "items": [
              {
                "id": "dashboard-appointments-views",
                "title": "Appointments View Options",
                "summary": "Customize how you monitor the daily schedule with Bin/Bucket, Calendar, or Tabulated List layouts.",
                "workflow": "Dashboard → Appointments",
                "details": [
                  {
                    "type": "heading",
                    "title": "Appointments"
                  },
                  {
                    "type": "text",
                    "content": "This module houses all the specific appointment dashboards, the appointment booking tools, and multiple viewing layouts for managing the clinic's schedule."
                  },
                  {
                    "type": "fields",
                    "title": "View Options",
                    "items": [
                      {
                        "name": "Bin/Bucket (View Button)",
                        "desc": "Displays appointments grouped categorically based on their current status or workflow stage. This option displays all the specific dashboards (Outpatient, Inpatient, Virtual, and related queues).",
                        "helper": ""
                      },
                      {
                        "name": "Calendar (View Button)",
                        "desc": "Displays appointments on a standard time-based calendar layout.",
                        "helper": ""
                      },
                      {
                        "name": "Tabulated List (View Button)",
                        "desc": "Displays appointments in a structured, sortable data table.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "dashboard-outpatient",
                "title": "Outpatient",
                "summary": "Track a patient's complete journey from booking through finalized consultation.",
                "workflow": "Dashboard → Appointments → Outpatient",
                "details": [
                  {
                    "type": "heading",
                    "title": "Outpatient"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/outpatient-dashboard-01.png",
                    "alt": "Outpatient Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Outpatient dashboard provides a comprehensive view to track a patient's complete journey, from the initial appointment booking through to a finalized consultation."
                  },
                  {
                    "type": "fields",
                    "title": "Global Actions & Filters",
                    "items": [
                      {
                        "name": "Book appointment (Button)",
                        "desc": "Click to open the appointment booking window and schedule a new visit.",
                        "helper": ""
                      },
                      {
                        "name": "Vet Filter (Drop-Down)",
                        "desc": "By default, this is set to \"All\". Use this drop-down to filter the dashboard and view appointment summary cards assigned to a specific veterinarian.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Dashboard Sections (The Patient Journey)",
                    "items": [
                      {
                        "name": "Upcoming",
                        "desc": "Displays appointments that are scheduled for the future.",
                        "helper": ""
                      },
                      {
                        "name": "Checked-in",
                        "desc": "Displays patients who have arrived at the clinic and are waiting.",
                        "helper": ""
                      },
                      {
                        "name": "Consultation",
                        "desc": "Displays patients who are currently inside the examination room with the veterinarian.",
                        "helper": ""
                      },
                      {
                        "name": "Billing",
                        "desc": "Displays patients whose medical consultation is finished and are pending invoice generation or payment.",
                        "helper": ""
                      },
                      {
                        "name": "Completed",
                        "desc": "Displays patients who have successfully checked out of the clinic.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Every section includes its header name, a numeric count of the appointment cards currently within it, and a dedicated Search Field to locate specific appointment cards within that stage."
                  },
                  {
                    "type": "fields",
                    "title": "Appointment Card Layout",
                    "items": [
                      {
                        "name": "Patient Details (Display Fields)",
                        "desc": "Shows the pet's name, gender, species and breed, and current weight.",
                        "helper": ""
                      },
                      {
                        "name": "Owner Name (Display Field)",
                        "desc": "Shows the name of the pet parent.",
                        "helper": ""
                      },
                      {
                        "name": "Meeting (Display Field)",
                        "desc": "Displays the name of the attending doctor.",
                        "helper": ""
                      },
                      {
                        "name": "Facility (Display Field)",
                        "desc": "Displays the specific clinic location or facility name.",
                        "helper": ""
                      },
                      {
                        "name": "Date and Time (Display Field)",
                        "desc": "Shows the scheduled date and time of the consultation.",
                        "helper": ""
                      },
                      {
                        "name": "Dynamic Details (Display Field)",
                        "desc": "Additional details appear conditionally based on the section the card is in (e.g., \"Payment Status\" is displayed when the card enters the Billing section).",
                        "helper": ""
                      },
                      {
                        "name": "3-Dot Menu (Action Icon)",
                        "desc": "Located on every card, clicking this reveals a context-sensitive menu of actions unique to the section the card is currently in. Example: in Checked-in — Reschedule/Reassign, Send to Consult, Update Weight, Cancel, Admit, No Show.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "dashboard-inpatient",
                "title": "Inpatient",
                "summary": "Manage and track admitted patients from admission through discharge.",
                "workflow": "Dashboard → Appointments → Inpatient",
                "details": [
                  {
                    "type": "heading",
                    "title": "Inpatient Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_dashboard.png",
                    "alt": "Inpatient Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "Admitted patients automatically appear on the Inpatient Dashboard. This central hub allows you to manage and track the patient flow from admission through discharge."
                  },
                  {
                    "type": "fields",
                    "title": "Key Features",
                    "items": [
                      {
                        "name": "Book Appointment",
                        "desc": "A quick-access button that opens the standard appointment booking window directly from the dashboard.",
                        "helper": ""
                      },
                      {
                        "name": "Status Indicators",
                        "desc": "The dashboard utilizes helper text and color-coded tags so you can quickly identify a patient's condition at a glance: Inpatient (General Text), Critical, Stable, Monitoring, Isolation.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Inpatient Workflow Stages",
                    "items": [
                      {
                        "name": "1. Treatment & Recovery",
                        "desc": "Patient summary cards appear here immediately upon admission. Once medical care is complete and the bill is finalized, the card automatically moves to the next stage.",
                        "helper": ""
                      },
                      {
                        "name": "2. Billing & Discharge",
                        "desc": "Patients transition here from Treatment & Recovery once their bill is finalized. Once payment is settled and the bill is closed, the card moves to the final stage.",
                        "helper": ""
                      },
                      {
                        "name": "3. Completed",
                        "desc": "Patient cards land here after the billing process is completely closed, marking the end of the inpatient workflow.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Navigation Note: Every section title displays the total count of patients currently in that stage (e.g., Treatment & Recovery (65)). Each panel features a search bar to easily locate specific pets."
                  }
                ]
              },
              {
                "id": "dashboard-calendar",
                "title": "Calendar",
                "summary": "Time-based scheduling matrix for booking and managing appointments by vet or facility.",
                "workflow": "Dashboard → Appointments → Calendar",
                "details": [
                  {
                    "type": "heading",
                    "title": "Calendar"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-calendar-main.png",
                    "alt": "Appointments Calendar"
                  },
                  {
                    "type": "text",
                    "content": "The Calendar sub-module opens by default when the Appointments tab is clicked. It provides a comprehensive scheduling interface, displaying appointments as a functional matrix comparing time slots against either specific veterinarians or clinic facilities."
                  },
                  {
                    "type": "fields",
                    "title": "Navigation & View Controls",
                    "items": [
                      {
                        "name": "Day / Week (Toggle)",
                        "desc": "Switch the calendar layout between a single-day view and a full-week view.",
                        "helper": ""
                      },
                      {
                        "name": "Today (Button)",
                        "desc": "Click to instantly return the calendar matrix to the current day.",
                        "helper": ""
                      },
                      {
                        "name": "Date Navigation (Display & Arrows)",
                        "desc": "The center displays the currently viewed day and date. Use the left and right arrows to navigate to previous or future days and weeks.",
                        "helper": ""
                      },
                      {
                        "name": "Vet view / Facility view (Toggle)",
                        "desc": "Switch the horizontal axis of the matrix to organize the schedule by individual veterinarians or by specific clinic facilities.",
                        "helper": ""
                      },
                      {
                        "name": "Grid view / Table view (Toggle)",
                        "desc": "Switch the overall visual layout between the standard visual matrix (Grid) or a structured list format (Table).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-calendar-grid-view.png",
                    "alt": "Calendar Grid View"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-calendar-table-view.png",
                    "alt": "Calendar Table View"
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Show stats cards (Checkbox)",
                        "desc": "Check to display summary statistic cards on the calendar interface.",
                        "helper": ""
                      },
                      {
                        "name": "Show availability (Checkbox)",
                        "desc": "Check to highlight available time slots for booking.",
                        "helper": ""
                      },
                      {
                        "name": "Slot Size (Options)",
                        "desc": "Select the time interval increments for the grid (Options: 60 min, 30 min, 15 min).",
                        "helper": ""
                      },
                      {
                        "name": "Appointment Type (Checkboxes)",
                        "desc": "Select the consultation modes to display (Options: Physical, Virtual, Home Visit).",
                        "helper": ""
                      },
                      {
                        "name": "Service Type (Drop-Down with Checkboxes)",
                        "desc": "Filter by specific medical or grooming services (Options: All, or select specific listed services).",
                        "helper": ""
                      },
                      {
                        "name": "Roles (Drop-Down with Checkboxes)",
                        "desc": "Filter the schedule by staff roles (Options: Vet, Groomers, Lab/Radiology tech).",
                        "helper": ""
                      },
                      {
                        "name": "Vets/Staffs (Drop-Down with Checkboxes)",
                        "desc": "Filter to show only specific staff members (Options: All, or select specific listed names).",
                        "helper": ""
                      },
                      {
                        "name": "Appointment Status (Drop-Down with Checkboxes)",
                        "desc": "Filter by the patient's current journey stage (Options: All, Upcoming, Cancelled, Billing, etc.).",
                        "helper": ""
                      },
                      {
                        "name": "Reset All (Button)",
                        "desc": "Click to clear all selected filters and return the calendar to its default state.",
                        "helper": ""
                      },
                      {
                        "name": "Apply filters (Button)",
                        "desc": "Click to update the calendar view based on your selected configurations.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Booking & Managing Appointments (Grid View)",
                    "items": [
                      {
                        "name": "Blank Slot (Interactive Grid Area)",
                        "desc": "Hovering over any empty time slot in the matrix displays a \"Click to Book\" prompt. Clicking the slot automatically opens the \"Book an Appointment\" window, pre-filled for that specific time and vet/facility.",
                        "helper": ""
                      },
                      {
                        "name": "Card Display Fields",
                        "desc": "Each card summarizes the visit, showing the Patient Name, Owner Name, Facility Name, Consultation Mode, Appointment Status, and Check-in Time.",
                        "helper": ""
                      },
                      {
                        "name": "3-Dot Menu (Action Icon)",
                        "desc": "Located on every appointment card. Clicking this reveals a quick-action context menu with options to manage the appointment, such as Follow up, Cancel, or Reassign.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-dashboard-requests",
        "title": "Requests",
        "description": "Approve, reject, and review system requests from one place.",
        "groups": [
          {
            "id": "group-dashboard-requests",
            "title": "Request Review",
            "description": "Pending Approval and All Requests tabs with table actions.",
            "items": [
              {
                "id": "dashboard-requests",
                "title": "Requests",
                "summary": "Centralized access to approve, reject, and review system requests across Pending Approval and All Requests.",
                "workflow": "Dashboard → Requests",
                "details": [
                  {
                    "type": "heading",
                    "title": "Requests"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_request.png",
                    "alt": "Dashboard Requests"
                  },
                  {
                    "type": "text",
                    "content": "The Requests section lists all system requests submitted by users, providing centralized access to approve, reject, and review them. The module is organized into two primary tabs."
                  },
                  {
                    "type": "fields",
                    "title": "Pending Approval",
                    "items": [
                      {
                        "name": "Pending Approval (Tab)",
                        "desc": "Displays only the requests that are currently awaiting an approval or rejection decision. The tab title includes a dynamic count of the pending requests.",
                        "helper": "Workflow: Dashboard → Requests → Pending Approval"
                      },
                      {
                        "name": "Type (Dropdown)",
                        "desc": "Filter specific types of requests. Options include: All selected, Refund/Return, Item Creation, Stock Transfer, Stock Adjustment, Edit Consult, and Purchase Order.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "All Requests",
                    "items": [
                      {
                        "name": "All Requests (Tab)",
                        "desc": "Displays a comprehensive history of all system requests, regardless of their current status.",
                        "helper": "Workflow: Dashboard → Requests → All Requests"
                      },
                      {
                        "name": "Type (Dropdown)",
                        "desc": "Filter specific types of requests (same options as Pending Approval).",
                        "helper": ""
                      },
                      {
                        "name": "Status (Dropdown)",
                        "desc": "Narrow results by state: All selected, Pending, Approved, and Rejected.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Requests Table Layout",
                    "items": [
                      {
                        "name": "Type (Display Field)",
                        "desc": "The category of the request.",
                        "helper": ""
                      },
                      {
                        "name": "Requester (Display Field)",
                        "desc": "The staff member who initiated the request.",
                        "helper": ""
                      },
                      {
                        "name": "Summary (Display Field)",
                        "desc": "A brief overview of the request details.",
                        "helper": ""
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "The current state of the request (Pending, Approved, or Rejected).",
                        "helper": ""
                      },
                      {
                        "name": "Approval step (Display Field)",
                        "desc": "The current tier or stage in the approval workflow.",
                        "helper": ""
                      },
                      {
                        "name": "Submitted (Display Field)",
                        "desc": "The date and time the request was made.",
                        "helper": ""
                      },
                      {
                        "name": "Approved By (Display Field)",
                        "desc": "The name of the authorized staff member who processed the request.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Record Action Buttons",
                    "items": [
                      {
                        "name": "Approve (Button)",
                        "desc": "Opens the \"Approve request\" confirmation window (\"Are you sure you want to approve this request?\"). Remark defaults to \"Verified and approved\". Confirm with Yes / No.",
                        "helper": ""
                      },
                      {
                        "name": "Reject (Button)",
                        "desc": "Opens the \"Reject request\" confirmation window (\"Are you sure you want to reject this request?\"). Remark requires a mandatory reason. Confirm with Yes / No.",
                        "helper": ""
                      },
                      {
                        "name": "View (Button)",
                        "desc": "Opens a detailed summary window for the request. Pending requests still show Approve and Reject at the bottom; processed requests show Close.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-dashboard-facilities",
        "title": "Facilities",
        "description": "Create and manage clinic spaces such as consultation rooms and kennels.",
        "groups": [
          {
            "id": "group-dashboard-facilities",
            "title": "Facility Management",
            "description": "Search, create, and maintain facility records.",
            "items": [
              {
                "id": "dashboard-facilities",
                "title": "Facilities",
                "summary": "Create, configure, and manage operational spaces such as consultation rooms, kennels, or surgical suites.",
                "workflow": "Dashboard → Facilities",
                "details": [
                  {
                    "type": "heading",
                    "title": "Facilities"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-create-facility.png",
                    "alt": "Create Facility"
                  },
                  {
                    "type": "text",
                    "content": "The Facilities module enables administrators to create, configure, and manage specific operational spaces (such as consultation rooms, kennels, or surgical suites) within the clinic."
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filters",
                    "items": [
                      {
                        "name": "Search Field (Input Field)",
                        "desc": "Search for a specific facility by its exact name.",
                        "helper": ""
                      },
                      {
                        "name": "Facility Type Filter (Drop-Down)",
                        "desc": "Filter the table to display only specific types of facilities.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Facility Window",
                    "items": [
                      {
                        "name": "Create (Button)",
                        "desc": "Opens the Create window to set up a brand-new facility.",
                        "helper": ""
                      },
                      {
                        "name": "Facility Type (Drop-Down)",
                        "desc": "Mandatory. Select the overarching classification of the space (Options include: Consultation, Kennel, etc.).",
                        "helper": ""
                      },
                      {
                        "name": "Name (Input Field)",
                        "desc": "Mandatory. Enter the specific name, number, or identifier for the facility (e.g., Exam Room 1).",
                        "helper": ""
                      },
                      {
                        "name": "Inventory Access (Radio Buttons)",
                        "desc": "Mandatory. Define inventory and stock permissions for this space (Options: All permitted storage, By Category, Selected Locations).",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Save the configuration and officially create the new facility in the system.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Table Layout",
                    "items": [
                      {
                        "name": "Sr. No. (Display Field)",
                        "desc": "Shows the sequential serial number of the entry. All column headers are fully sortable.",
                        "helper": ""
                      },
                      {
                        "name": "Name (Display Field)",
                        "desc": "Displays the designated name of the facility.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Display Field)",
                        "desc": "Shows the classification category of the facility.",
                        "helper": ""
                      },
                      {
                        "name": "Created By (Display Field)",
                        "desc": "Displays the name of the staff member who created the entry.",
                        "helper": ""
                      },
                      {
                        "name": "Created Date (Display Field)",
                        "desc": "Shows the exact date the facility was added to the system.",
                        "helper": ""
                      },
                      {
                        "name": "Status (Toggle)",
                        "desc": "Interactive toggle on each row to change operational status (Options: Active, Under maintenance).",
                        "helper": ""
                      },
                      {
                        "name": "Edit (Button)",
                        "desc": "Open the configuration window and modify the facility's existing details.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Button)",
                        "desc": "Permanently remove the facility from the system.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "outpatient",
    "title": "Outpatient Dashboard",
    "topics": [
      {
        "id": "outpatient-dashboard",
        "title": "Outpatient Dashboard",
        "groups": [
          {
            "id": "outpatient-workflow-group",
            "items": [
              {
                "id": "outpatient-global-actions",
                "title": "Global Actions & Filters",
                "details": [
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/outpatient-dashboard-01.png",
                    "alt": "Outpatient Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Outpatient dashboard provides a comprehensive view to track a patient's complete journey, from the initial appointment booking through to a finalized consultation."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open the appointment booking window and schedule a new visit.",
                        "name": "Book appointment (Button)"
                      },
                      {
                        "desc": "By default, this is set to \"All\". Use this drop-down to filter the dashboard and view appointment summary cards assigned to a specific veterinarian.",
                        "name": "Vet Filter (Drop-Down)"
                      }
                    ],
                    "title": "Global Actions & Filters"
                  }
                ],
                "summary": "Quick access to schedule appointments and filter patient cards by veterinarian.",
                "workflow": "Outpatient → Book Appointment / Filter Vet"
              },
              {
                "id": "outpatient-stages",
                "title": "Dashboard Sections (The Patient Journey)",
                "details": [
                  {
                    "type": "text",
                    "content": "The dashboard is divided into progressive sections representing the patient's current status at the clinic. Every section includes its header name, a numeric count of the appointment cards currently within it, and a dedicated Search Field to locate specific appointment cards within that stage."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays appointments that are scheduled for the future.",
                        "name": "Upcoming"
                      },
                      {
                        "desc": "Displays patients who have arrived at the clinic and are waiting.",
                        "name": "Checked-in"
                      },
                      {
                        "desc": "Displays patients who are currently inside the examination room with the veterinarian.",
                        "name": "Consultation"
                      },
                      {
                        "desc": "Displays patients whose medical consultation is finished and are pending invoice generation or payment.",
                        "name": "Billing"
                      },
                      {
                        "desc": "Displays patients who have successfully checked out of the clinic.",
                        "name": "Completed"
                      }
                    ],
                    "title": "Workflow Stages"
                  }
                ],
                "summary": "Progressive sections representing the patient's current status at the clinic."
              },
              {
                "id": "outpatient-card-layout",
                "title": "Appointment Card Layout & Actions",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the pet's name, gender, species and breed, and current weight.",
                        "name": "Patient Details (Display Fields)"
                      },
                      {
                        "desc": "Shows the name of the pet parent.",
                        "name": "Owner Name (Display Field)"
                      },
                      {
                        "desc": "Displays the name of the attending doctor.",
                        "name": "Meeting (Display Field)"
                      },
                      {
                        "desc": "Displays the specific clinic location or facility name.",
                        "name": "Facility (Display Field)"
                      },
                      {
                        "desc": "Shows the scheduled date and time of the consultation.",
                        "name": "Date and Time (Display Field)"
                      },
                      {
                        "desc": "Additional details appear conditionally based on the section the card is in (e.g., \"Payment Status\" is displayed when the card enters the Billing section).",
                        "name": "Dynamic Details (Display Field)"
                      }
                    ],
                    "title": "Card Display Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Located on every card, clicking this reveals a context-sensitive menu of actions unique to the section the card is currently in.",
                        "name": "3-Dot Menu (Action Icon)"
                      },
                      {
                        "desc": "If the card is in the \"Checked-in\" section, the 3-dot menu displays the following actionable options: Reschedule/Reassign, Send to Consult, Update Weight, Cancel, Admit, No Show.",
                        "name": "Checked-in Section 3-Dot Actions Example"
                      }
                    ],
                    "title": "Card Action Menu"
                  }
                ],
                "summary": "Structured layout of individual appointment cards and context-sensitive 3-dot menu actions."
              }
            ],
            "title": "Outpatient Workflow & Actions",
            "description": "Global actions, filters, workflow stages, and card actions for outpatient care."
          }
        ],
        "description": "Comprehensive view to track patient flow across consultation stages."
      }
    ],
    "description": "Track a patient's complete journey, from initial appointment booking through to a finalized consultation."
  },
  {
    "id": "inpatient",
    "title": "Inpatient Dashboard",
    "topics": [
      {
        "id": "topic-Inpatient-Module",
        "title": "Inpatient Overview",
        "groups": [
          {
            "id": "Inpatient-Module-Overview",
            "items": [
              {
                "id": "Inpatient-Module-Overview-Description",
                "title": "Inpatient",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Dashboard → Inpatient"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_dashboard.png",
                    "alt": "Inpatient Dashboard"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/inpatient-overview-video.mp4",
                    "poster": "/assets/images/knowledgebase/inpatient_dashboard.png",
                    "alt": "Inpatient Dashboard Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Inpatient module provides a streamlined workflow for hospitalizing patients, allowing clinic staff to assign kennels, document provisional diagnoses, and securely capture digital consent and deposits. Once admitted, patients are monitored via a central dashboard that tracks their entire care journey from initial admission through final discharge."
                  }
                ],
                "summary": "Inpatient module is used when a patient is admitted for treatment. ",
                "workflow": "Dashboard → Inpatient"
              }
            ],
            "title": "Inpatient Module Overview",
            "description": "Inpatient module is used when a patient is admitted for treatment. "
          }
        ],
        "description": ""
      },
      {
        "id": "admit-patient-topic",
        "title": "Admit Patient",
        "groups": [
          {
            "id": "admit-patient-group",
            "items": [
              {
                "id": "admit-patient-form",
                "title": "Admission Details & Deposit Collection Form",
                "details": [
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click \"Admit Patient\" to initiate the inpatient admission process for hospitalization or extended care.",
                        "name": "Admit Patient (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Admit Patient"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_admit_patient.png",
                    "alt": "Admit Patient"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/inpatient-admit-patient-video.mp4",
                    "poster": "/assets/images/knowledgebase/inpatient_admit_patient.png",
                    "alt": "Admit Patient Video Guide"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows a quick overview of the patient's details.",
                        "name": "Summary Card (Display Panel)"
                      },
                      {
                        "desc": "Select the specific ward or kennel for the pet's stay.",
                        "name": "Assign Kennel (Drop-Down with Checkbox)"
                      },
                      {
                        "desc": "Select the pet's current condition (Options: stable, monitoring, critical, isolated).",
                        "name": "Initial Status (Drop-Down)"
                      },
                      {
                        "desc": "Assign the primary veterinarian overseeing the admitted pet.",
                        "name": "Attending Vet (Drop-Down)"
                      },
                      {
                        "desc": "Enter the medical reason for hospitalization.",
                        "name": "Reason for Admission / Provisional Diagnosis (Input Field)"
                      },
                      {
                        "desc": "Enter the initial base fee for the admission process.",
                        "name": "Admission Exam / Processing Fee (₹) (Input Field)"
                      }
                    ],
                    "title": "Admission Details Form"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Check this box if an upfront payment is required.\n\n\nNote: Selecting this dynamically displays the prompt text \"HOW MUCH DEPOSIT AMOUNT IS COLLECTED?\" followed by these additional fields:",
                        "name": "Collect Deposit (Check-Box)"
                      },
                      {
                        "desc": " Enter the numerical amount collected.",
                        "name": "Deposit Amount (Input Field)"
                      },
                      {
                        "desc": "Select how the deposit is being paid (e.g., Card, Cash).",
                        "name": "Payment Method (Drop-Down)"
                      },
                      {
                        "desc": "Check this if the deposit cannot be returned.",
                        "name": "Non-refundable (Check-Box)"
                      }
                    ],
                    "title": "Deposit Collection"
                  }
                ],
                "summary": "Form details for kennel assignment, initial status, fees, and optional deposit collection.",
                "workflow": "Patient Workbook → Shortcuts → Admit Patient → Fill Details → Deposit → Consent"
              },
              {
                "id": "admit-patient-consent",
                "title": "Consent & Digital Signature Workflow",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Owner informed of estimated cost and has consented to hospitalization * (Check-Box) : Must be checked to proceed.",
                        "name": "Checkbox",
                        "helper": ""
                      },
                      {
                        "desc": "Click to generate the official consent form.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Consent & Submit"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Example: Owner consent for this procedure.",
                        "name": "Displays the Consent form text.",
                        "helper": ""
                      },
                      {
                        "desc": "Display text:  \"I confirm I am the pet parent or authorized agent for [Pet Name] and give my informed consent for Vetinstant to proceed as described.\"",
                        "name": "Authorization * (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Display text: \"I understand and accept financial responsibility for the cost of this treatment.\"",
                        "name": "Financial Responsibility * (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "A digital whiteboard space for the owner to sign.",
                        "name": "Signature of Pet Parent / Authorized Agent * (Drawing Area)",
                        "helper": ""
                      }
                    ],
                    "title": "Consent Form Workflow"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_consent_form.png",
                    "alt": "Consent Form"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to erase the signature space and sign again.",
                        "name": "Clear Signature (Button) ",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize the admission.",
                        "name": "Confirm & Admit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Signature & Admit"
                  },
                  {
                    "type": "notification",
                    "title": "System Notifications",
                    "content": "The system will sequentially display “form added successfully” \n\n\nfollowed by “patient admitted successfully”."
                  }
                ],
                "summary": "Digital authorization, financial responsibility, and owner signature workflow."
              }
            ],
            "title": "Admission Form & Consent Workflow",
            "description": "Fields, deposit collection, consent form, and digital signature."
          }
        ],
        "description": "Initiate inpatient admission process for hospitalization or extended care."
      },
      {
        "id": "inpatient-dashboard-topic",
        "title": "Inpatient Dashboard",
        "groups": [
          {
            "id": "inpatient-dashboard-group",
            "items": [
              {
                "id": "inpatient-workflow-stages",
                "title": "Inpatient Dashboard Features & Workflow Stages",
                "details": [
                  {
                    "type": "text",
                    "content": "Admitted patients automatically appear on the Inpatient Dashboard. This central hub allows you to manage and track the patient flow from admission through discharge."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "A quick-access button that opens the standard appointment booking window directly from the dashboard.",
                        "name": "Book Appointment (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Book Appointment"
                  },
                  {
                    "type": "options",
                    "items": [
                      {
                        "desc": "The dashboard utilizes helper text and color-coded tags so you can quickly identify a patient's condition at a glance:\n\n\nInpatient (General Text)\n\n\nCritical\n\n\nStable\n\n\nMonitoring\n\n\nIsolation",
                        "name": "Status Indicator Tags",
                        "helper": ""
                      }
                    ],
                    "title": "Status Indicators"
                  },
                  {
                    "type": "text",
                    "content": "The dashboard is divided into three main workflow columns.\nNavigation Note: Every section title displays the total count of patients currently in that stage (e.g., Treatment & Recovery (65)). Each panel features a search bar to easily locate specific pets, and clicking on any patient summary card will immediately open that pet's Patient Workbook."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Entry Point: Patient summary cards appear in this section immediately upon admission. \n\n\nTransition: Once medical care is complete and the bill is finalized, the patient card automatically moves to the next stage.",
                        "name": "1. Treatment & Recovery"
                      },
                      {
                        "desc": "Entry Point: Patients transition here from the Treatment & Recovery section once their bill is finalized. \n\n\nTransition: Once the payment is settled and the bill is officially closed, the patient card moves to the final stage.",
                        "name": "2. Billing & Discharge"
                      },
                      {
                        "desc": "Entry Point: Patient cards land here from the Billing & Discharge section after the billing process is completely closed, marking the end of the inpatient workflow.",
                        "name": "3. Completed"
                      }
                    ],
                    "title": "Inpatient Workflow Stages"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_patient_tracking.png",
                    "alt": "Inpatient Dashboard / Patient Tracking"
                  }
                ],
                "summary": "Key features, status indicators, and 3 main workflow columns."
              }
            ],
            "title": "Workflow Stages & Status Indicators",
            "description": "Columns, search bars, patient count, and status tags for inpatient care."
          }
        ],
        "description": "Central hub to manage and track patient flow from admission through discharge."
      },
      {
        "id": "inpatient-care-topic",
        "title": "Patient Overview (Inpatient View)",
        "groups": [
          {
            "id": "inpatient-care-group",
            "items": [
              {
                "id": "inpatient-overview-tab",
                "title": "Inpatient Overview",
                "details": [
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_overview.png",
                    "alt": "Overview"
                  },
                  {
                    "type": "text",
                    "content": "The Overview module serves as the central hub for inpatient care, displaying critical patient information at a glance."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "A dedicated space to add and highlight initial red flags or critical warnings for the patient.",
                        "name": "Red Flags Tags",
                        "helper": ""
                      }
                    ],
                    "title": "Red Flags"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Allows you to add and review initial medical or care instructions for the veterinary staff.",
                        "name": "Instructions",
                        "helper": ""
                      }
                    ],
                    "title": "Instructions"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the most recent vital sign recordings, such as temperature, pulse, and weight.",
                        "name": "Latest Vitals Cards",
                        "helper": ""
                      },
                      {
                        "desc": "Click the \"Go to I/O Monitoring\" button to jump directly to the vitals module inside the Patient Workbook, where you can log new vital readings.",
                        "name": "Go to I/O Monitoring (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Latest Vitals"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the immediate treatment schedule for the patient.",
                        "name": "Tasks",
                        "helper": ""
                      },
                      {
                        "desc": "Click the \"Full schedule\" shortcut to navigate directly to the Treatment Plan module for a comprehensive view of all scheduled tasks.",
                        "name": "Full schedule (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Tasks"
                  }
                ],
                "summary": "Central hub for inpatient care, displaying critical patient information at a glance."
              },
              {
                "id": "inpatient-io-monitoring",
                "title": "Vitals & Intake, Output & Nursing Checks (I/O)",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module → Go to I/O Monitoring → Vitals"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/inpatient-io-monitoring-video.mp4",
                    "alt": "Intake, Output & Nursing Checks Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Vitals tab serves as a central hub for monitoring a patient's current health metrics, housing both general patient vitals and a specialized section for tracking continuous Intake, Output, and Nursing Checks."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the specific time the entry was recorded.",
                        "name": "Log Time (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the classification of the log.",
                        "name": "Category Tag (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific name of the administered item or check performed.",
                        "name": "Item Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the recorded amount or volume.",
                        "name": "Quantity (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the audit trail detailing who logged the entry and the exact date (e.g., Logged by Vetinstant on 03 Sep 2026).",
                        "name": "Subtext (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to modify the existing log entry.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to permanently remove the log entry.",
                        "name": "Delete (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Intake, Output & Nursing Checks"
                  },
                  {
                    "type": "text",
                    "content": "To record a new entry, click the Add Log button."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "This opens the \"Add Log\" window, which contains dynamic fields that adapt based on the type of log being created.",
                        "name": " Add Log (Button)",
                        "helper": ""
                      }
                    ],
                    "title": " Add Log"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_adding_log.png",
                    "alt": "Adding a Log"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select overarching classification: Intake, Output, Nursing Check.",
                        "name": "Type (Drop-Down)"
                      },
                      {
                        "desc": "Populates dynamically based on selected Type.",
                        "name": "Category (Drop-Down)"
                      },
                      {
                        "desc": "Fluid Type (Input Field): Enter specific fluid administered. \n\n\nRate (ml/hr) (Input Field): Enter speed of fluid administration. \n\n\nVolume Infused So Far (ml) (Input Field): Enter total volume administered thus far.",
                        "name": "Dynamic Fields (Example: Type = Intake, Category = IV Fluid)"
                      },
                      {
                        "desc": "Discard information. ",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Save details and add new log.",
                        "name": "Log Entry (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "\"Add Log\" window"
                  }
                ],
                "summary": "Central hub for monitoring health metrics and tracking continuous Intake, Output, and Nursing Checks.",
                "workflow": "Patient Module → Go to I/O Monitoring → Vitals"
              },
              {
                "id": "inpatient-treatment-plan",
                "title": "Treatment Plan Dashboard & Items",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Workflow 1: Inpatient Overview → Full Schedule"
                  },
                  {
                    "type": "workflow",
                    "content": "Workflow 2: Patient Workbook → Treatment Plan"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/inpatient-treatment-plan-video.mp4",
                    "alt": "Inpatient Treatment Plan Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Treatment Plan module allows veterinary staff to prescribe, schedule, and manage specific treatments for an admitted inpatient."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the classification of the treatment (e.g., Medication).",
                        "name": "Type Tag (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific name of the prescribed treatment or drug.",
                        "name": "Item Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows dosage, administration route, and frequency (e.g., 1 · Ear Drops · Three times daily).",
                        "name": "Other Details (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays exactly when the treatment began and its intended length (e.g., Started 01 Sep 2026, 10:00 · 6 days).",
                        "name": "Start Date, Time & Duration (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays any additional veterinarian notes or instructions regarding the treatment.",
                        "name": "Notes (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Treatment Plan Display Fields"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_treatment_plan_items.png",
                    "alt": "Treatment Plan Items"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to open the setup window and prescribe a new item for the patient's schedule.",
                        "name": "+ Add to Treatment Plan (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "+ Add to Treatment Plan"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_add_treatment_plan.png",
                    "alt": "Add to Treatment Plan"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select classification: Medication, Fluid Therapy, Monitoring Check, Diet, Procedure.",
                        "name": "Type (Drop-Down)"
                      },
                      {
                        "desc": "Drug Name (Input Field with Drop-Down) : Search for and select the specific medication to be administered.\n\n\nDose (Input Field) : Enter the prescribed dosage amount.\n\n\nRoute (Drop-Down) : Select the method of administration (Options: Ear drops, Eye drops, Intramuscular, etc.).\n\n\nFrequency (Drop-Down) : Select how often the treatment should be administered (Options: Morning, Afternoon, Night, Once daily, Twice daily, etc.).\n\n\nStart (Calendar Field) : Select the exact date and time the treatment plan should commence.\n\n\nDuration (Drop-Down) : Select the timeframe for the treatment (Options: Days, Until discontinued). Note: If \"Days\" is selected, an additional input field dynamically appears allowing you to enter the specific number of days.\n\n\nNotes (Input Field) : Enter any special instructions, warnings, or additional context for the nursing staff.",
                        "name": "Dynamic Fields (Example: Type = Medication)"
                      },
                      {
                        "desc": "Discard information. ",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Save details and add treatment to schedule.",
                        "name": "Add to Plan (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "\"Add to Treatment Plan\" Setup Window"
                  }
                ],
                "summary": "Prescribe, schedule, and manage specific treatments for an admitted inpatient.",
                "workflow": "Workflow 1: Inpatient Overview → Full Schedule | Workflow 2: Patient Workbook → Treatment Plan"
              },
              {
                "id": "inpatient-discharge",
                "title": "Discharge Module & Summary Window",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Discharge (Button)"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/inpatient-discharge-video.mp4",
                    "alt": "Inpatient Discharge Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Discharge module allows veterinary staff to finalize a patient's inpatient stay, document the ultimate medical outcome, and provide detailed post-care instructions before releasing the patient."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Clicking the \"Discharge\" button from the Patient Workbook opens a dedicated window to finalize the patient's medical record for their stay.",
                        "name": "Discharge (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Discharge"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inpatient_discharge.png",
                    "alt": "Discharge"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select final status of patient: Recovered - Discharged to owner, Transferred to another facility, Deceased. (* mandatory field).",
                        "name": "Patient Outcome (Drop-Down)*"
                      },
                      {
                        "desc": "Final Diagnosis (Input Field) :* Enter the ultimate medical diagnosis for the patient's condition.\n\n\nDiet Instructions (Input Field) : Enter any specific dietary requirements, changes, or restrictions for the patient's recovery at home.\n\n\nFollow-Up Date (Calendar Field) : Select the exact date for the patient's next required check-up or consultation.\n\n\nActivity Restrictions (Input Field) : Enter any limitations on the patient's physical activity (e.g., strictly cage rest, short leash walks only, no jumping).\n\n\nOwner Instructions / Warning Signs to Watch For (Input Field) : Enter detailed post-care instructions for the pet owner, including specific symptoms, behaviors, or complications that require immediate veterinary attention.",
                        "name": "Dynamic Fields (Example: Patient Outcome = \"Recovered - Discharged to owner\")"
                      },
                      {
                        "desc": "Click to save the entered discharge summary. Once saved, the medical record is updated, and the patient is officially ready to be discharged from the software's inpatient flow.",
                        "name": "Save Changes (Button)"
                      }
                    ],
                    "title": "Discharge Summary Window Fields"
                  }
                ],
                "summary": "Finalize stay, document ultimate medical outcome, and provide detailed post-care instructions.",
                "workflow": "Patient Workbook → Discharge (Button)"
              }
            ],
            "title": "Clinical Inpatient Care Modules",
            "description": "Comprehensive inpatient documentation tools."
          }
        ],
        "description": "Overview, Vitals / I/O Monitoring, Treatment Plan, and Discharge Summary."
      }
    ],
    "description": "Used when a patient is admitted for hospitalization, extended treatment, intake/output monitoring, and discharge."
  },
  {
    "id": "vets",
    "title": "Vets Module",
    "topics": [
      {
        "id": "vets-directory-topic",
        "title": "Vets Content Dashboard",
        "groups": [
          {
            "id": "vets-directory-group",
            "items": [
              {
                "id": "vets-content-overview",
                "title": "Content",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Vets → Content"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_content_dashboard.png",
                    "alt": "Vet Content Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Content Dashboard holds the basic details of all doctors and staff members in the clinic. This module consists of various search fields, action buttons, and a comprehensive table listing all vet and staff accounts, allowing administrators to create and edit user profiles."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name to search for a specific veterinarian or staff member.",
                        "name": "Vet Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": " Select the specific medical specialty \n\n\n(Options: Anesthesiology, behavioral medicine, cardiology, dentistry, dermatology, emergency and critical care, endocrinology and metabolism, gastroenterology, general medicine, Gynaecology, hematology/ immunology, hepatology, infectious disease, internal medicine, musculoskeletal, nephrology/ urology, neurology, nutrition, oncology, ophthalmology, orthopedic, radiology, respiratory, surgery, toxicology, zoological medicine).\n\n\nNote: This field is only available when the Role is set to \"Specialist\".",
                        "name": "Specialist (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the contact number to search for a specific user.",
                        "name": "Phone Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the email address to search for a specific user.",
                        "name": "Email (Input Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Search Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Type, Role, Department, Consultation Type.",
                        "name": "Filter options (checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the selected filter options to the search results.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filter Option"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all selected search constraints and filters.",
                        "name": "Reset (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Reset"
                  }
                ],
                "summary": "Locate vet or staff accounts using search inputs and multi-checkbox filters."
              },
              {
                "id": "create-user-account",
                "title": "Creating an Account (Vet / Other Staff)",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Vets → Content → Create"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/vets-create-account-video.mp4",
                    "alt": "Creating a Vet Account Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "Click the Create (Button) to open the form for generating a new user account. At the top of the form, use the Radio Buttons to select the type of account you wish to create: either \"Vet\" or \"Other\"."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the full name of the veterinarian.",
                        "name": "Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific veterinary role (Options: Assistant vet, Associate vet, Consultant vet, Psychotherapist, Senior vet, Specialist, Veterinarian).\n\n\nNote: When \"Specialist\" is selected from the Role drop-down, an additional field will dynamically appear.",
                        "name": "Role (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific medical specialty. (Options: Anesthesiology, behavioral medicine, cardiology, dentistry, dermatology, emergency and critical care, endocrinology and metabolism, gastroenterology, general medicine, Gynaecology, hematology/ immunology, hepatology, infectious disease, internal medicine, musculoskeletal, nephrology/ urology, neurology, nutrition, oncology, ophthalmology, orthopedic, radiology, respiratory, surgery, toxicology, zoological medicine). \n\n\nNote: This field is only available when the Role is set to \"Specialist\".",
                        "name": "Specialist (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the department the vet belongs to.",
                        "name": "Department (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the clinic branches the vet is assigned to.",
                        "name": "Associated Branches (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this box if the vet is available for booking appointments.\n\n\nNote: If this option is selected, the \"Consultation Type\" field will dynamically appear below it.",
                        "name": "Schedulable (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the types of appointments the vet accepts (Options: Physical, Virtual, Home visit).",
                        "name": "Consultation Type (Drop-Down with Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the vet's contact number.",
                        "name": "Phone Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the vet's official medical registration or license number.",
                        "name": "Registration Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Create a unique system login username for the vet.",
                        "name": "Username (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the vet's official email address.",
                        "name": "Email (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Manually enter a secure password for the account.",
                        "name": "Password (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this box to have the system automatically create a secure password instead of typing one manually.",
                        "name": "Auto Generate Password (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and create the vet account.",
                        "name": "Register (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create a Vet Account"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_vet_account.png",
                    "alt": "Create Vet Account"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“User created successfully” appears after clicking Register."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the appropriate title (e.g., Mr., Ms., Mrs.).",
                        "name": "Salutation (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the full name of the staff member.",
                        "name": "Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific staff role (Options: Admin billing, Frontdesk, Groomer, Inventory manager, Lab/radiology technician, Pharmacist, Veterinary technician/nurse).",
                        "name": "Role (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the department the staff member belongs to.",
                        "name": "Department (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the clinic branches the staff member is assigned to.",
                        "name": "Associated Branches (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this box if the staff member (like a groomer) can be booked for appointments.",
                        "name": "Schedulable (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the staff member's contact number.",
                        "name": "Phone Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter any applicable registration or employee ID number.",
                        "name": "Registration Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Create a unique system login username for the staff member.",
                        "name": "Username (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the staff member's official email address.",
                        "name": "Email (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Manually enter a secure password for the account.",
                        "name": "Password (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this box to have the system automatically create a secure password.",
                        "name": "Auto Generate Password (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and create the staff account.",
                        "name": "Register (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Other Staff Member Accounts"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_other_staff_account.png",
                    "alt": "Create Other Staff Member Accounts"
                  },
                  {
                    "type": "notification",
                    "title": "System NotificatioN",
                    "content": "“User created successfully” appears after clicking Register."
                  }
                ],
                "summary": "Registration windows for creating new Vet or non-vet staff user accounts.",
                "workflow": "Vets → Content → Create → Radio: Vet / Other"
              },
              {
                "id": "vets-user-record-table",
                "title": "User Record Table & Action Buttons",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays total count of user accounts currently listed (e.g., Number of Users: 107).",
                        "name": "Number of Users (Display Field)"
                      },
                      {
                        "desc": "Shows full name. Clicking name opens \"Edit Profile\" window (update Physical, Virtual, Home Visit Consulting Hours, Personal Exceptions, Manage Unavailability) -> click Update (Button) -> System Notification: \"user updated successfully\".",
                        "name": "Name (Display Field / Interactive)"
                      },
                      {
                        "desc": "Displays the specific medical specialty of the user, if applicable.",
                        "name": "Specialty (Display Field)"
                      },
                      {
                        "desc": "Shows the assigned role of the user (e.g., Veterinarian, Frontdesk).",
                        "name": "Role (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the accepted consultation types for the user (e.g., Physical, Virtual, Home visit).",
                        "name": "Consultation Type (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "User Record Table Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open \"Manage Availability\" section for that specific user.\n\nThis allows you to create and manage the following scheduling fields:\nPhysical Consulting Hours\nVirtual Consulting Hours\nHome Visit Consulting Hours\nPersonal Exceptions\nManage Unavailability",
                        "name": "Calendar (Icon)"
                      },
                      {
                        "desc": "Click to change operational status (active/inactive). \n\n\nSystem Notification: \"Vet status updated\" appears immediately.",
                        "name": "Activate/Deactivate (Toggle Icon)",
                        "helper": "\"Activate or Deactivate this record\""
                      }
                    ],
                    "title": "Row Action Buttons"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/manage_unavailability.png",
                    "alt": "Manage Unavailability"
                  }
                ],
                "summary": "List of all user accounts, profile editor modal, availability shortcut, and status toggle."
              }
            ],
            "title": "Content Dashboard & Account Registration",
            "description": "Directory filters and account creation forms for Vets and Other staff."
          }
        ],
        "description": "Search, filter, create, and edit veterinarian and staff user accounts."
      },
      {
        "id": "vets-profile-topic",
        "title": "Vet Profile Dashboard",
        "groups": [
          {
            "id": "vets-profile-group",
            "items": [
              {
                "id": "vet-profile-workspace",
                "title": "Vet Profile Workspace & Shortcuts",
                "details": [
                  {
                    "type": "workflow",
                    "content": "User Profile → View Account"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_view_account.png",
                    "alt": "View Account"
                  },
                  {
                    "type": "text",
                    "content": "The Vet Profile Dashboard provides a comprehensive view of a veterinarian's account, allowing users to manage schedules, view appointments, and update personal information. By default, opening the account lands the user on the \"My calendar\" central view."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the vet's uploaded profile picture.",
                        "name": "Profile Photo (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the full name of the vet (e.g., Dr. Tony).",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to quickly change the vet's operational availability.",
                        "name": "Availability Status (Toggle Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific appointment types the vet is configured to accept.",
                        "name": "Consultation Type (Display Tags)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the vet's official email address.",
                        "name": "Email (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the vet's contact number.",
                        "name": "Phone Number (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Profile Header Details"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Navigate here to update personal or professional details.",
                        "name": "Edit Profile (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "The default view; navigate here to see the central appointment schedule.",
                        "name": "My Calendar (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Navigate here to configure specific working hours and exceptions.",
                        "name": "Manage Availability (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Navigate here to manually schedule a new visit.",
                        "name": "Book an appointment (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Navigate here to update the account's security credentials.",
                        "name": "Change Password (Option)",
                        "helper": ""
                      }
                    ],
                    "title": "Shortcuts (Left Side Menu)"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "The central workspace displays a calendar matrix organized by days and weeks, visually plotting all of the vet's scheduled appointments.",
                        "name": "Calendar View (Display Panel)",
                        "helper": ""
                      }
                    ],
                    "title": "Center Workspace / My Calendar"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Lists all future scheduled visits and consultations that the veterinarian still needs to attend to.",
                        "name": "Upcoming Appointments",
                        "helper": ""
                      },
                      {
                        "desc": "Provides a historical record of all completed, missed, or canceled consultations for easy reference.",
                        "name": "Past Appointments",
                        "helper": ""
                      }
                    ],
                    "title": "Appointments Panel (Right Side Menu)"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the pet's name and gender.",
                        "name": "Patient Name (Gender) (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific medical or non-medical service booked.",
                        "name": "Service Type (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the scheduled time frame for the visit.",
                        "name": "Time Slot (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the medium of the visit (e.g., physical, virtual).",
                        "name": "Consultation Type (Display Tags)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the current state of the appointment (e.g., Upcoming).",
                        "name": "Status (Display Tag)",
                        "helper": ""
                      }
                    ],
                    "title": "Appointment Card (Interactive Card)"
                  }
                ],
                "summary": "Header details, shortcuts navigation menu, central calendar view, and right side appointments panel.",
                "workflow": "User Profile → View Account"
              },
              {
                "id": "edit-profile",
                "title": "Edit Profile",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Vet Profile → Shortcuts → Edit Profile"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_edit_profile.png",
                    "alt": "Edit Profile"
                  },
                  {
                    "type": "text",
                    "content": "This section allows you to modify and update the personal and professional details of the veterinarian's account.\n\n\nClicking on Edit Profile opens a window titled \"Edit Profile,\" allowing you to update the user information."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the full name of the veterinarian.",
                        "name": "Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific veterinary role (Options: Assistant vet, Associate vet, Consultant vet, Psychotherapist, Senior vet, Specialist, Veterinarian).\n\n\nNote: When \"Specialist\" is selected from the Role drop-down, an additional field will dynamically appear.",
                        "name": "Role (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific medical specialty. \n\n\nNote: This field is only available when the Role is set to \"Specialist\".",
                        "name": "Specialist (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "name": "Department (Drop-Down)",
                        "desc": "Select the department the vet belongs to.",
                        "helper": ""
                      },
                      {
                        "name": "Associated Branches (Drop-Down)",
                        "desc": "Select the clinic branches the vet is assigned to.",
                        "helper": ""
                      },
                      {
                        "name": "Schedulable (Check-Box)",
                        "desc": "Check this box if the vet is available for booking appointments.\n\n\nNote: If this option is selected, the \"Consultation Type\" field will dynamically appear below it.",
                        "helper": ""
                      },
                      {
                        "desc": "Select the types of appointments the vet accepts (Options: Physical, Virtual, Home visit).",
                        "name": "Consultation Type (Drop-Down with Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the vet's official medical registration or license number.",
                        "name": "Registration Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open a whiteboard window that allows you to draw and create the user's digital signature.",
                        "name": "Add Signature (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Profile Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to save the edited changes to the user's profile.",
                        "name": "Update (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Update"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“user updated successfully” appears after clicking Update."
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "Manage-Availability",
                "title": "Manage Availability",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Vet Profile → Shortcuts → Manage Availability"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_manage_availability.png",
                    "alt": "Manage Availability"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/vets-manage-availability-video.mp4",
                    "poster": "/assets/images/knowledgebase/vet_manage_availability.png",
                    "alt": "Manage Availability Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Manage Availability Dashboard allows the veterinarian to configure their working hours based on their accepted consultation types (physical, virtual, and home visits). It also provides tools to manage personal exceptions and block out specific periods of unavailability."
                  },
                  {
                    "type": "notification",
                    "title": "Consulting Hours Configuration (Note)",
                    "content": "The configuration process and available fields are identical for Physical Consulting Hours, Virtual Consulting Hours, and Home Visit Consulting Hours."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Available for Physical Consulting Hours only.  Turn this on to automatically fill the vet's availability with the standard clinic schedule. Turn it off to configure the availability manually.",
                        "name": "Use Clinic Schedule (Toggle)",
                        "helper": ""
                      }
                    ],
                    "title": "Use Clinic Schedule"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the specific day of the week.",
                        "name": "Day (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the beginning time of the availability shift.",
                        "name": "Start Time (Clock Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the ending time of the availability shift.",
                        "name": "End Time (Clock Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Located directly below the schedule table; enter the standard duration (in minutes) for each appointment slot.",
                        "name": "Slot Duration (Input Block)",
                        "helper": ""
                      }
                    ],
                    "title": "Manual Configuration Table"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to enable editing of the availability days and time slots.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "While in edit mode, click this to add multiple distinct time slots for the same day.",
                        "name": "+ (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the configured schedule changes.\n\n\nSystem Notification: “changes updated successfully!” appears immediately after clicking Save.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Manual Configuration Buttons"
                  },
                  {
                    "type": "text",
                    "content": "Use the following section to log specific date ranges where standard availability will be altered or halted (e.g., vacations, conferences)."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the beginning date of the exception period.",
                        "name": "Start Date (Calendar Input)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the ending date of the exception period.",
                        "name": "End Date (Calendar Input)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the reason or context for the personal exception.",
                        "name": "Remarks (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the personal exception information to the schedule.",
                        "name": "Save (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to remove an existing personal exception entry.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Personal Exceptions"
                  },
                  {
                    "type": "text",
                    "content": "Use the following section to block out specific, granular time windows on a particular date (e.g., a one-off meeting or personal errand)."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the specific date for the unavailability.",
                        "name": "Date (Calendar Input)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the exact time the unavailability block begins.",
                        "name": "Start Time (Clock Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the exact time the unavailability block ends.",
                        "name": "End Time (Clock Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the reason or context for blocking out this time.",
                        "name": "Remarks (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the unavailability information to the schedule.",
                        "name": "Save (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to remove an existing unavailability entry.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Manage Unavailability"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "book-appointment",
                "title": "Book Appointment",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Vet Profile → Shortcuts → Book an appointment"
                  },
                  {
                    "type": "text",
                    "content": "The Book an Appointment module allows clinic staff to quickly schedule a new consultation specifically for the selected veterinarian, directly from their profile.\n\n\nClicking on Book an appointment opens the booking window."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for the existing customer or pet for whom the appointment is being booked.",
                        "name": "Pet / Customer Search (Search Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the method of booking (Options: Walk in, Phone).",
                        "name": "Booking Type * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the medium of the consultation.\n\n\nNote: If the Booking Type is set to \"Walk in\", this defaults to \"Physical\". If set to \"Phone\", the options include Physical, Virtual, and Home visit.",
                        "name": "Consultation Mode * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific medical or non-medical service required.",
                        "name": "Service Type * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select whether this is a \"New\" visit or a \"Follow up\".",
                        "name": "Visit Type * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select a standard reason from the drop-down list, or manually enter a custom reason if it is not available.",
                        "name": "Reason (Input Field / Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Automatically displays the name of the veterinarian whose profile you are currently viewing.",
                        "name": "Vet Name * (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select additional clinic staff or users to assign to this specific case.",
                        "name": "Assigned Users (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the designated clinic facility, room, or branch.",
                        "name": "Facility (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the timing for the visit.\n\n\nImmediate : Select to book the appointment right now.\n\n\nLater : Select to schedule for the future. \n\n\nNote: Selecting this dynamically displays a calendar field to select the Date, followed by a range of available time slots that can be booked.",
                        "name": "Appointment * (Radio Buttons)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter any additional notes or instructions for the appointment.",
                        "name": "Remarks (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to process the entered details and proceed to the review stage.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Book an Appointment (Window)"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_create_appointment.png",
                    "alt": "Create an Appointment"
                  },
                  {
                    "type": "text",
                    "content": "Upon clicking Submit, a \"Review\" window opens, providing a read-only summary of the booked information"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the overarching scheduled time, vet, and customer info.",
                        "name": "Appointment Details (Display Panel)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific procedures or services requested.",
                        "name": "Service Details (Display Panel)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and confirm the booking.",
                        "name": "Done (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Review & Confirm"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_review_confirm.png",
                    "alt": "Review & Confirm"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“Appointment created successfully!” appears immediately after clicking Done."
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "change-password",
                "title": "Change Password",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Vet Profile → Shortcuts → Change Password"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vet_change_password.png",
                    "alt": "Change Password"
                  },
                  {
                    "type": "text",
                    "content": "The Change Password section provides a secure way for the veterinarian or staff member to update their account's login credentials."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the current password for security verification.",
                        "name": "Old Password (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the new desired password.",
                        "name": "New Password (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Re-enter the newly created password to confirm accuracy and prevent typos.",
                        "name": "Confirm Password (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the entries and successfully apply the new password to the account.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Change Password (Window)"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Profile Workspace & Schedules",
            "description": "Profile header, navigation shortcuts, availability configuration, and appointment booking."
          }
        ],
        "description": "Comprehensive view of a veterinarian's account, calendar, availability, booking, and security."
      }
    ],
    "description": "Manage clinic doctors and staff accounts, scheduling, availability, consulting hours, and vet profiles."
  },
  {
    "id": "module-Clinic-View-Account",
    "title": "Clinic - View Account",
    "topics": [
      {
        "id": "topic-Clinic-View-Account",
        "title": "View Account",
        "groups": [
          {
            "id": "group-Clinic-View-Account",
            "items": [
              {
                "id": "item-Clinic-View-Account",
                "title": "Clinic Account Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": "My Profile → View Account"
                  },
                  {
                    "type": "text",
                    "content": "This module provides the necessary tools to set up, configure, and manage the overarching clinic account, as well as all staff and branch accounts linked to it."
                  },
                  {
                    "type": "text",
                    "content": "When \"View Account\" is clicked, the system directs you to the clinic's central management module. By default, the Consultation sub-module opens in the main viewing area."
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Left-Menu",
                "title": "Left Menu (Account Profile)",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the clinic's primary information, including the uploaded Logo, Clinic Name, Address Line, Email, and Phone Number.",
                        "name": "Account Details (Display Card)",
                        "helper": ""
                      },
                      {
                        "desc": "A navigation menu providing access to Consultation, Edit Profile, Change Password, Access & Roles, and Branch Management.",
                        "name": "Module Shortcuts",
                        "helper": ""
                      }
                    ],
                    "title": "Account Profile"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Consultation",
                "title": "Consultation",
                "details": [
                  {
                    "type": "text",
                    "content": "Clinic Schedule Displays the clinic's weekly operating hours in a table matrix."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Day (Mon-Sun), Start Time, End Time, Slot Duration.",
                        "name": "Table Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open the table in an editable format, allowing you to adjust the specific open and close times for each day.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the new schedule. A pop-up confirming \"Changes updated successfully!\" will appear.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Clinic Schedule"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to add a new holiday. This reveals a Date field (defaulted to today) and an Occasion input field. Click the Save (Icon) to confirm. A pop-up will state \"Clinic Holidays Updated\".",
                        "name": "+ (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays a list of all configured holidays with fields for Date and Occasion.",
                        "name": "Holidays Table",
                        "helper": ""
                      },
                      {
                        "desc": "Present against each holiday entry. Clicking it removes the holiday and displays a \"Holiday deleted\" confirmation pop-up.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Holiday Calendar"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Edit-Profile",
                "title": "Edit Profile",
                "details": [
                  {
                    "type": "text",
                    "content": "Clicking this shortcut opens a dedicated window to update the clinic's core identity, billing structure, and operational entities."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Upload the clinic's primary logo.",
                        "name": "Logo (Image Input)",
                        "helper": ""
                      },
                      {
                        "desc": "Upload an image or graphic for document footers.",
                        "name": "Footer (Image Input)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the legal or operational name of the clinic.",
                        "name": "Entity Name (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the primary clinic phone number.",
                        "name": "Contact Number (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the primary clinic email address.",
                        "name": "Email (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the physical location of the clinic.",
                        "name": "Address (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select all applicable modes (Options: Virtual, Physical, Home Visit).\n",
                        "name": "Select the type of consultation you offer (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Set the invoicing structure (Options: Separate, Combined).",
                        "name": "Billing Mode (Toggle)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the tax identification number for the primary clinic.",
                        "name": "Clinic Tax ID (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter standard legal or policy text to appear on clinic documents.",
                        "name": "Disclaimer (Input Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Edit Profile Window"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to add operational sub-departments (like a Pharmacy or Pet Shop) to the clinic's profile. Doing so reveals dynamic fields.",
                        "name": "+ Add Entity (Clickable Text)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the entity name (e.g., Pharmacy or Petshop).",
                        "name": "Entity Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the specific tax ID for the new entity.",
                        "name": "Tax ID (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the specific location if different from the main clinic.",
                        "name": "Address (Input Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Adding New Entities"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to apply all profile changes.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Save"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Change-Password",
                "title": "Change Password",
                "details": [
                  {
                    "type": "text",
                    "content": "Clicking this shortcut opens a secure window to update the account's login credentials."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the current active password.",
                        "name": "Old Password (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the desired new password.",
                        "name": "New Password (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Re-enter the new password to ensure accuracy.",
                        "name": "Confirm Password (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Click to update the credentials.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Change Password Window"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Access-Control",
                "title": "Access & Control",
                "details": [
                  {
                    "type": "text",
                    "content": "This sub-module allows administrators to edit the permission levels and software roles for every user added under the clinic's account."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Name, Email ID, Role.",
                        "name": "Display Fields",
                        "helper": ""
                      }
                    ],
                    "title": "Table Layout"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Present against each user record. Clicking this opens a role-editing window with the following details. ",
                        "name": "Edit (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Identifies the user being modified.",
                        "name": "Name & Email (Display Fields)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the appropriate system permissions for the user (Options: Admin, Billing, Pharmacy Technician, Front Desk, PRM, Vet).",
                        "name": "Role (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the new access permissions to the user.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Managing User Roles"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Branch-Management",
                "title": "Branch Management",
                "details": [
                  {
                    "type": "text",
                    "content": "This sub-module manages multi-location clinics, displaying all registered branches and providing settings for data sharing between them."
                  },
                  {
                    "type": "text",
                    "content": "Branches Module (Default View) displays a table of all existing branches."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Name, Location, Administrator Name, Contact Number. Note: The primary location features a distinct \"Main\" tag next to its name.\nStatus (Toggle) : Present against each entry to instantly activate or deactivate a branch.\nEdit (Icon) : Click to modify the branch's specific information.",
                        "name": "Table Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Present against each entry to instantly activate or deactivate a branch.",
                        "name": "Status (Toggle)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to modify the branch's specific information.",
                        "name": "Edit (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Branches Module (Default View)"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open the setup window with the following fields:",
                        "name": "Create Branch (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the location details.",
                        "name": "Branch Name, Country, State, Address (Input Fields)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the details for the branch's primary point of contact.",
                        "name": "Administrator Name, Contact Number, Email (Input Fields)",
                        "helper": ""
                      },
                      {
                        "desc": "Create a login password for the branch.",
                        "name": "Password (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this to have the system automatically create and fill a secure password.",
                        "name": "Auto-Generate Password (Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and add the new branch.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Creating a New Branch"
                  },
                  {
                    "type": "text",
                    "content": "Switching to the Settings tab allows you to control how patient and clinic data flows between your various locations."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Use the On/Off toggles to grant or restrict cross-branch access for the following categories:",
                        "name": "Data Sharing Controls (Toggles)",
                        "helper": ""
                      },
                      {
                        "desc": "Allow pet medical data to be shared across all branches.",
                        "name": "Pets Data",
                        "helper": ""
                      },
                      {
                        "desc": "Allow veterinarian data to be shared across all branches.",
                        "name": "Vets Data",
                        "helper": ""
                      },
                      {
                        "desc": "Allow treatment plan data to be shared across all branches.",
                        "name": "Plan Data",
                        "helper": ""
                      },
                      {
                        "desc": "Allow inventory stock counts to be shared.",
                        "name": "Inventory - Stock Data",
                        "helper": ""
                      },
                      {
                        "desc": "Allow available services data to be shared.",
                        "name": "Inventory - Services Data",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the data-sharing configurations across the network.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Settings Module"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "View Account",
            "description": ""
          }
        ],
        "description": ""
      }
    ],
    "description": ""
  },
  {
    "id": "pets",
    "title": "Pets Module",
    "topics": [
      {
        "id": "topic-Pets-Module-Overview",
        "title": "Pets Module Overview",
        "groups": [
          {
            "id": "group-Pets-Overview",
            "items": [
              {
                "id": "Pets-Overview",
                "title": "Pets Overview",
                "details": [
                  {
                    "type": "text",
                    "content": "The Pets module serves as the central registry for managing all patient and owner profiles within the clinic. It features a streamlined two-step registration process, robust search filters, and a comprehensive dashboard to efficiently track records and book appointments."
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Pets Overview module",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "pets-directory-topic",
        "title": "Pet Directory & Registration",
        "groups": [
          {
            "id": "pets-directory-group",
            "items": [
              {
                "id": "item-content-overview",
                "title": "Content Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Pets → Content"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/pet_content_dashboard.png",
                    "alt": "Pet Content Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Content Dashboard within the Pet module acts as the central registry for all patients. It provides various search fields, advanced filters, a streamlined two-step registration process for new pets and their owners, and a comprehensive table displaying all recorded details."
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "pet-search-filter",
                "title": "Search & Filters",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the unique system ID to search for a specific pet.",
                        "name": "Patient ID (Input Field)"
                      },
                      {
                        "desc": "Enter the name of the pet.",
                        "name": "Pet Name (Input Field)"
                      },
                      {
                        "desc": "Enter the name of the pet's owner/parent.",
                        "name": "Customer Name (Input Field)"
                      },
                      {
                        "desc": "Enter the contact number associated with the customer.",
                        "name": "Phone Number (Input Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Search Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Ownership Type, Email, Species, Color, Gender, Reproductive Status, Blood Group, Temperament.",
                        "name": "Filter by (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the selected filter options to the table.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filter Option"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all entered search fields and applied filters.",
                        "name": "Reset (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Reset "
                  }
                ],
                "summary": "Search patient directory, apply filter checkboxes, and execute two-step registration.",
                "workflow": "Pet → Content → Create → Step 1: Customer → Step 2: Pet"
              },
              {
                "id": "Adding-New-Pet",
                "title": "Adding a New Pet",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Pets → Content → Create (Button)"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/pet-registration-video.mp4",
                    "alt": "Two-Step Pet Registration Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "Creating a new pet utilizes a quick two-step registration process: first, capturing or linking the owner's contact details, and second, logging the pet's specific clinical profile and identifying traits."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to open the two-step registration window for adding a new pet and customer.",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search and select a pre-registered parent. (OR) Register a new customer by filling in the following:",
                        "name": "Search Field (Input Field / Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the full name of the pet owner.",
                        "name": "Parent Name * (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the primary phone number.",
                        "name": "Contact Number * (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the owner's email address.",
                        "name": "Email * (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter an alternative contact number.",
                        "name": "Secondary Phone Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the owner's residential address.",
                        "name": "Address (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to proceed to Step 2.",
                        "name": "Next (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 1: Customer Registration"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“user added successfully!” appears after clicking Next."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the parent name, email, and phone number captured from Step 1.",
                        "name": "Summary Card (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to upload a display picture for the pet.",
                        "name": "Profile Picture (Input Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Select either \"Household Pet\" or \"Rescue/Shelter\".",
                        "name": "Ownership Type (Radio Buttons)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the pet's name.\n\n\nNote: If \"Household Pet\" is selected, the field remains empty for manual entry. If \"Rescue/Shelter\" is selected, the field automatically auto-fills with a generated ID (e.g., Stray-7742).",
                        "name": "Name * (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the pet's species (e.g., Dog, Cat).",
                        "name": "Species * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Choose how to enter the age using \"Date\" or \"Age\".\n\n\nIf Date: A calendar field appears to select the exact Date of Birth.\n\n\nIf Age: The Age In *** (Input Field) appears to enter the number, alongside the Age Type *** (Drop-Down) to specify \"years\" or \"months\".",
                        "name": "DOB * (Radio Buttons)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific breed.",
                        "name": "Breed * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the physical color or markings.",
                        "name": "Color (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select \"Female\", \"Male\", or \"Unknown\".",
                        "name": "Gender (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select \"Intact\", \"Neutered\", or \"Unknown\".",
                        "name": "Reproductive Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the pet's current weight.",
                        "name": "Weight (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the registered microchip ID, if applicable.",
                        "name": "Microchip Number (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the pet's blood type.",
                        "name": "Blood Group (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the behavioral temperament of the pet.",
                        "name": "Temperament (Drop-Down)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 2: Pet Registration"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/pet_registration_confirmation.png",
                    "alt": "Pet Registration / Final Confirmation"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to finalize the registration and save the pet's profile.",
                        "name": "Register (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize the registration and immediately open the booking window to schedule an appointment.",
                        "name": "Register+Book (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Register & Book"
                  }
                ],
                "summary": "",
                "workflow": "Pets → Content → Create (Button)"
              },
              {
                "id": "pets-record-table",
                "title": "Pet Record Table & Action Buttons",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays total count of registered pets (e.g., Number of Pets: 266).",
                        "name": "Number of Pets (Display Field)"
                      },
                      {
                        "desc": "Shows the unique system identifier for the pet.",
                        "name": "Patient ID (Display Field)"
                      },
                      {
                        "desc": "Displays the designated name of the pet.",
                        "name": "Pet Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the pet's specific breed.",
                        "name": "Breed (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the pet's gender.",
                        "name": "Gender (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the owner's name. A subtext directly beneath it displays the total count of pets owned by this customer (e.g., \"no. of pets: 2\").",
                        "name": "Customer Name (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Pet Record Table Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to change operational status of record.",
                        "name": "Activate / Deactivate (Toggle Button)"
                      },
                      {
                        "desc": "Click to start or continue direct messaging conversation with pet owner.",
                        "name": "Chat (Icon)"
                      },
                      {
                        "desc": "Click to add a new pet under this exact same customer. Bypasses Step 1 and opens Step 2 directly.",
                        "name": "+ Add Pet (Button)"
                      },
                      {
                        "desc": "Click to open appointment booking window and schedule visit for this pet.",
                        "name": "Book (Button)"
                      }
                    ],
                    "title": "Row Action Buttons"
                  }
                ],
                "summary": "Table layout display fields and row action buttons."
              }
            ],
            "title": "Content Dashboard & Two-Step Registration",
            "description": "Search fields, filters, and two-step registration process."
          }
        ],
        "description": "Search, filter, two-step customer and pet registration, and record table."
      },
      {
        "id": "pets-master-topic",
        "title": "Pet Master (Species)",
        "groups": [
          {
            "id": "pets-master-group",
            "items": [
              {
                "id": "species-master-workflow",
                "title": "Species Master & Record Table",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Pet → Master"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/pet_master.png",
                    "alt": "Pet Master"
                  },
                  {
                    "type": "text",
                    "content": "The Master module under the Pet section allows clinic administrators to configure foundational pet categories by searching, managing, and adding new species to the database."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter a name to search for a specific species.",
                        "name": "Search Bar (Input Field)"
                      }
                    ],
                    "title": "Search field"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click the Create (Button) to open the window and add a new species.",
                        "name": "Create (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the name of the new species.",
                        "name": "Species Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and add the new species to the system.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Species"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/pet_create_species.png",
                    "alt": "Create Species"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays total count of species (e.g., Total Species: 13).",
                        "name": "Total Species (Display Field)"
                      },
                      {
                        "desc": "The serial or sequence number of the record.",
                        "name": "S. No (Display Field)"
                      },
                      {
                        "desc": "Shows the designated name of the species.",
                        "name": "Species Name (Display Field)"
                      },
                      {
                        "desc": "Displays the total number of breeds associated with this specific species.",
                        "name": "No. of Breeds (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the user who created the species record.",
                        "name": "Created By (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the exact date the species record was created.",
                        "name": "Created Date (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open a window to update the species name. Click the Update (Button) within this window to save the changes.",
                        "name": "Edit (Icon) ",
                        "helper": ""
                      },
                      {
                        "desc": "Click to remove the species from the system.\n\n\nConfirmation Pop-Up: A window will appear displaying the message, “Are you sure you want to delete this record?” Click the Confirm (Button) to finalize deletion or the Cancel (Button) to abort the action.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Species Record Table"
                  }
                ],
                "summary": "Search, create, and manage species database records.",
                "workflow": "Pet → Master → Create Species"
              }
            ],
            "title": "Species Configuration",
            "description": "Add, edit, and delete species."
          }
        ],
        "description": "Configure foundational pet categories by adding and managing species."
      }
    ],
    "description": "Central registry for all patients, two-step registration, species master, and full Patient Module workspace."
  },
  {
    "id": "patient-workbook",
    "title": "Patient Workbook",
    "topics": [
      {
        "id": "patient-workbook",
        "title": "Patient Workbook Dashboard",
        "groups": [
          {
            "id": "patient-workbook-dashboard-section",
            "items": [
              {
                "id": "Patient-Work-Dashboard-Overview",
                "title": "Patient Workbook Dashboard Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": " Dashboard → Click on the patient's Appointment Card"
                  },
                  {
                    "type": "workflow",
                    "content": "Pet → Click on any specific Pet Record from the table"
                  },
                  {
                    "type": "text",
                    "content": "The Patient Module contains all the comprehensive details of a specific patient. It serves as the primary workspace for veterinarians and clinic staff to view the pet's history, edit information, manage billing, and actively record consultation details."
                  }
                ],
                "summary": "A comprehensive record of the patient’s care, including vitals, SOAP notes, diagnostics, procedures, clinical flow, forms, and records.",
                "workflow": ""
              },
              {
                "id": "layout-overview",
                "title": "Layout Overview",
                "details": [
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_layout_overview.png",
                    "alt": "Patient Module — Layout Overview"
                  },
                  {
                    "type": "text",
                    "content": "The Patient Module is divided into three main operational areas: the Left Menu, the Center Workspace, and the Right Side Panel."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the primary identification and profile information for the pet (e.g., name, breed, age, owner details).",
                        "name": "Pet Basic Details (Display Panel)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays any upcoming alerts, due dates, or scheduled follow-ups for the patient.",
                        "name": "Reminders (Section)",
                        "helper": ""
                      },
                      {
                        "desc": "Provides quick navigation links to frequently used actions or sections within the patient's profile.",
                        "name": "Shortcuts (Menu Options)",
                        "helper": ""
                      }
                    ],
                    "title": "Left Menu (Sidebar)"
                  },
                  {
                    "type": "text",
                    "content": "The center area acts as the primary interactive hub for the current visit or overall patient management. "
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "A dashboard summary providing a snapshot of the pet's current health status, recent activities, and visit context.",
                        "name": "Overview ",
                        "helper": ""
                      },
                      {
                        "desc": "The primary clinical workspace used by the vet to record active consultation notes, vitals, symptoms, diagnosis, and medical procedures.",
                        "name": "Patient Workbook",
                        "helper": ""
                      },
                      {
                        "desc": "The financial section used to generate, review, and manage invoices and transactions for the patient's visits.\n",
                        "name": "Billing",
                        "helper": ""
                      },
                      {
                        "desc": "A comprehensive, chronological log of the patient's past medical records, prescriptions, and previous consultations.",
                        "name": "History",
                        "helper": ""
                      }
                    ],
                    "title": "Center Workspace"
                  },
                  {
                    "type": "text",
                    "content": "The right-side menu provides quick access to internal administrative tools and financial snapshots."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the count of active appointments along with a “show all” option to view details.",
                        "name": "Active Appointments (display)",
                        "helper": ""
                      },
                      {
                        "desc": "Add or view custom labels used for internal clinic tracking and categorization of the patient.",
                        "name": "Internal Tags (Display / Input)",
                        "helper": ""
                      },
                      {
                        "desc": "A dedicated space to read or write internal staff notes regarding the patient or their owner (not visible on official medical records).",
                        "name": "Internal Notes (Input Area)",
                        "helper": ""
                      },
                      {
                        "desc": "A quick financial snapshot for the patient, featuring:\n\n\nBilling View (Button), Outstanding (Display Field), Credits (Display Field)",
                        "name": "Important Billing Details (Summary Card)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open a detailed breakdown of the billing history.",
                        "name": "Billing View (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the current unpaid balance (e.g., Rs 405.00).",
                        "name": "Outstanding (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows any advance payments or account credits available (e.g., Rs 100.00).\n",
                        "name": "Credits (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Right Side Panel"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Patient Workbook Dashboard",
            "description": "A comprehensive record of the patient’s care, including vitals, SOAP notes, diagnostics, procedures, clinical flow, forms, and records."
          }
        ],
        "description": "A comprehensive record of the patient’s care, including vitals, SOAP notes, diagnostics, procedures, clinical flow, forms, and records."
      },
      {
        "id": "patient-module-workspace-topic",
        "title": "Pet / Patient Module Workspace",
        "groups": [
          {
            "id": "patient-module-left-sidebar",
            "items": [
              {
                "id": "patient-left-menu",
                "title": "Left Menu (Sidebar)",
                "details": [
                  {
                    "type": "text",
                    "content": "The Left Menu serves as the persistent navigation and quick-reference panel for the patient's profile. It consists of the Summary Card, Chat, Reminders, and Shortcut Options."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the unique system identifier for the pet .",
                        "name": "Patient ID (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific breed classification.",
                        "name": "Breed (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the pet's uploaded display photo.",
                        "name": "Profile Picture (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the designated name of the pet .",
                        "name": "Patient Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the pet's gender.",
                        "name": "Gender (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Provides a quick concatenated overview of the breed, age, and weight .",
                        "name": "Demographics Summary (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the physical color or markings of the pet .",
                        "name": "Color (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open a window allowing you to update the customer's contact and personal details.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the full name of the pet owner.",
                        "name": "Customer Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the owner's registered email address.",
                        "name": "Customer Email (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the primary contact number.",
                        "name": "Phone Number (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Indicates whether the customer has downloaded and registered on the clinic's client-facing mobile application.",
                        "name": "App Status (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Summary card"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_left_menu_summary.png",
                    "alt": "Left Menu / Summary"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "patient-reminders",
                "title": "Reminders",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module ->Left Menu → Reminders"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/patient-reminders-video.mp4",
                    "alt": "Patient Reminders Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Reminders section within the left sidebar helps clinic staff track and manage upcoming or past-due health procedures for the pet. It categorizes reminders into three main status tabs: All, Due, and Overdue."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "All, Due, and Overdue.",
                        "name": "Main Status Tabs",
                        "helper": ""
                      },
                      {
                        "desc": "Preventive (e.g., Preventive (2)), Diagnostics (e.g., Diagnostics (1)), Care (e.g., Care (1))",
                        "name": "Categories",
                        "helper": ""
                      }
                    ],
                    "title": "Reminder Categories"
                  },
                  {
                    "type": "text",
                    "content": "Click the Add (Button) to manually create a new reminder. This opens a window titled \"Add\" with the configuration fields."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click the Add (Button) to manually create a new reminder.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Adding a New Reminder"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the category for the reminder (Options: preventive, diagnostic, care).",
                        "name": "Type * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Search and select the specific vaccine, test, or procedure.",
                        "name": "Item Name * (Input Field / Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific variant of the item.",
                        "name": "Variant * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the dose number if the procedure is part of a multi-dose series.",
                        "name": "Dose in Series * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the due date for the reminder.",
                        "name": "Date * (Calendar Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this box if the procedure has already been completed.\n\n\nNote: If selected, the item bypasses the active reminders section and is directly logged into the patient's \"History\" section.",
                        "name": "Mark as Done (Check-Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter any additional notes regarding the procedure.",
                        "name": "Remarks (Input Box)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to create the reminder.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Add Reminder Fields"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“Procedure added successfully” appears after clicking Submit."
                  },
                  {
                    "type": "text",
                    "content": "Under each category section (Preventive, Diagnostics, Care), individual reminders are displayed with the following details:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the name of the vaccine or procedure.",
                        "name": "Item Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the specific Due or Overdue date.",
                        "name": "Status Subtext (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open an action menu for that specific reminder, offering the following options: Edit, Add to Appointment, Delete",
                        "name": "3-Dot (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Select to open a window to update the reminder's details.\n\n\nClick the Submit (Button) to save changes.\n\n\nSystem Notification: “Procedure updated successfully!” appears.",
                        "name": "Edit (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Select to actively include this reminder in the current consultation.\n\n\nConfirmation Window: A window titled \"Add to Appointment\" opens with the text, “Are you sure you want to add it to the appointment?”\n\n\nClick No to close the window without changes, or click Yes to confirm.\n\n\nAction: Clicking Yes removes the item from the Reminders section and seamlessly moves it to the Procedure section within the central Patient Workbook module.\n\n\nSystem Notification: “Procedure updated successfully!” appears.",
                        "name": "Add to Appointment (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Select to remove the reminder from the system.\n\n\nConfirmation Window: A window titled \"Delete\" opens with the text, “Are you sure you want to delete this record?” Click Yes to confirm deletion or No to cancel.",
                        "name": "Delete (Option)",
                        "helper": ""
                      }
                    ],
                    "title": "Reminder Item Details & Actions"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Clicking this opens a comprehensive, full-screen window titled \"Schedules/Reminders\".\nThis window provides an expanded view allowing clinic staff to easily view, edit, delete, or add new reminders across all three categories (Preventive, Diagnostics, Care) simultaneously.",
                        "name": "View all (Link/Option)",
                        "helper": ""
                      }
                    ],
                    "title": "View All Schedules/Reminders"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-shortcuts",
                "title": "Shortcuts",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module ->Left Menu → Shortcuts"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_shortcuts_range.png",
                    "alt": "Shortcuts / Select Range"
                  },
                  {
                    "type": "text",
                    "content": "The Shortcuts section, located in the left side menu, provides quick access to frequently used actions, records, and administrative tools for the selected patient. "
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open a window titled \"Update,\" allowing you to modify and update the pet's existing clinical or identification information.",
                        "name": "Edit Profile (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and save the changes made to the pet's profile.\n\n\nSystem Notification: “Pet updated successfully!” appears immediately after clicking Update.",
                        "name": "Update (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Edit Profile"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open a window displaying graphical charts of the pet's recorded health metrics over time.",
                        "name": "Vital Trends (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Use this toggle to switch between viewing the Temperature trends graph and the Weight trends graph.",
                        "name": "View Toggle (Toggle Option)",
                        "helper": ""
                      },
                      {
                        "desc": "As vitals are logged over multiple visits, the graph visually plots all historical data. Hovering over or viewing the graph data points will display the exact date, time, and the actual numerical data instance for that specific record.",
                        "name": "Graph Display (Display Panel)",
                        "helper": ""
                      }
                    ],
                    "title": "Vital Trends"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_vital_trends.png",
                    "alt": "Vital Trends"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to immediately open the appointment booking window, allowing you to schedule a new consultation, follow-up, or service specifically for this pet.\n",
                        "name": "Book an Appointment",
                        "helper": ""
                      }
                    ],
                    "title": "Book an Appointment"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open a window to write or upload memos and external records for the pet. \n\n\nNote: Any memo or file submitted here will automatically sync and appear in the patient's main \"History\" module.",
                        "name": "Memo/Records (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "You can add a memo using two methods:\n\n\nText Area (Input Field) : Manually type out the memo or consultation notes in the provided space.\n\n\nFile Upload (Drop Zone / Option) : Select a file from your local system or drag and drop it into the designated area to attach it to the patient's record.",
                        "name": "Input Methods",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save and add the memo. It will be automatically logged and stamped with today's date.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Memo/Records"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_memo_records.png",
                    "alt": "Memo / Records"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to generate and print a comprehensive financial statement for the patient. This follows a simple two-step process.",
                        "name": "SOA (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "A window titled \"Statement of Accounts\" opens, allowing you to filter the financial timeline.\n",
                        "name": "Step 1: Select Range ",
                        "helper": ""
                      },
                      {
                        "desc": "Select the desired timeframe.\nAll : Select to pull the complete financial history for the patient.\nCustom : Select to define a specific timeframe. \n\nNote: Choosing this dynamically displays the Start Date and End Date (Calendar Inputs) to enter your custom range.\n",
                        "name": "Range (Radio Buttons)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to generate the statement and advance to Step 2.",
                        "name": "Proceed (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Review the displayed SOA records and proceed print.",
                        "name": "Step 2: Review and Print",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the generated financial statement based on the selected range.",
                        "name": "Record View (Display Panel)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to print the official Statement of Accounts.",
                        "name": "Print (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "SOA (Statement of Accounts)"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to manage the pet's schedules and due dates.\n\n\nAction : This shortcut functions identically to the \"View all\" button located in the sidebar's Reminders section. It opens the full-screen \"Schedules/Reminders\" window, displaying all existing reminders and providing the tools to view, edit, delete, or add new reminders across all categories.",
                        "name": "Reminders (Option)",
                        "helper": ""
                      }
                    ],
                    "title": "Reminders"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the specific document required (Options: Vaccine History, Travel certificate, Fitness certificate).",
                        "name": "Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the desired timeframe to include in the certificate.\n\n\nAll : Select to pull the complete history.\nCustom : Select to define a specific timeframe. Note: Choosing this dynamically displays the Start Date and End Date (Calendar Inputs) to enter your custom range.",
                        "name": "Range (Radio Buttons)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to generate a preview of the certificate. This opens the document view along with a Print (Button) to print the finalized certificate.",
                        "name": "Preview (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Certificates"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_certificates.png",
                    "alt": "Certificates"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Fill the admission form.",
                        "name": "Admission Details Form",
                        "helper": ""
                      },
                      {
                        "desc": "Sign the const form.",
                        "name": "Consent Form",
                        "helper": ""
                      }
                    ],
                    "title": "Admit Patient"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_admit_patient.png",
                    "alt": "Admit Patient"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_consent_form.png",
                    "alt": "Consent Form"
                  },
                  {
                    "type": "text",
                    "content": " Click \"Collect/Refund\" to quickly process direct financial transactions, advance payments, or account refunds independent of a specific invoice."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the owner's name (e.g., Sanjeev).",
                        "name": "Parent Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the current financial standing of the account (e.g., Rs 22017.00).",
                        "name": "Available Balance (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the nature of the transaction (Options: Collect, Refund).",
                        "name": "Credit Balance (Radio Buttons)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the specific monetary amount to be collected or refunded.",
                        "name": "Amount (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the method of transaction (e.g., Cash, Card, UPI).",
                        "name": "Payment Mode (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the context or reference for this transaction.",
                        "name": "Reason (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to execute and record the transaction.",
                        "name": "Pay (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Collect/Refund"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_collect_refund.png",
                    "alt": "Collect / Refund"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Left Menu (Sidebar)",
            "description": "Summary card, Reminders, and Shortcuts."
          },
          {
            "id": "patient-module-right",
            "items": [
              {
                "id": "item-right-side-panel",
                "title": "Right Side Panel",
                "details": [
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_right_side_panel.png",
                    "alt": "Right Side Panel"
                  },
                  {
                    "type": "text",
                    "content": "The right-side menu provides quick access to internal administrative tools, appointment tracking, family details, and financial snapshots for the patient."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the total count of currently active appointments for the pet.",
                        "name": "Active Appointments (Display Panel)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to view the specific details and schedule of these appointments.\n\n\nAction: Opens a window that lists all the past appointments for that specific pet.\nInteractive Element: Clicking on any past appointment from this list immediately directs the user to that specific appointment's Patient Workbook.",
                        "name": "Show All (Option)",
                        "helper": ""
                      }
                    ],
                    "title": "Active Appointments "
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_active_appointments.png",
                    "alt": "Active Appointments"
                  },
                  {
                    "type": "text",
                    "content": "Use the \"Internal Tags\" section to view or add custom labels for the patient or customer. Note: These tags are for internal clinic tracking only and are not visible to the customer."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to manage or add new tags. This opens a window titled \"Internal tags\".",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to choose a specific color highlight for the tag.",
                        "name": "Color (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter a new tag name or search for an existing one.",
                        "name": "Search / Write Tag (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to stage the newly written tag.",
                        "name": "Add (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize and apply the tags to the profile.\n\n\nSystem Notification: “Internal Tags added successfully!” appears after clicking Save.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Internal Tags"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_internal_tags.png",
                    "alt": "Internal Tags"
                  },
                  {
                    "type": "text",
                    "content": "\"Internal Notes\" is a dedicated space to read or write internal staff notes regarding the patient or their owner. Note: These notes are for clinic reference only and are not visible on official medical records."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to manage or add new notes. This opens a window titled \"Internal Notes\".",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays previously written notes, showing the writer’s name, the note content, and the exact date and time it was written.",
                        "name": "Note History (Display Panel)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the new internal note in this space.",
                        "name": "Write Note (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to submit and save the note.\n\n\nSystem Notification: “Internal Note added successfully!” appears immediately after clicking Add.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Internal Notes"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_internal_notes.png",
                    "alt": "Internal Notes"
                  },
                  {
                    "type": "text",
                    "content": "\"Billing\" is a quick financial snapshot for the patient's account."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the current unpaid balance (e.g., Rs 405.00).",
                        "name": "Outstanding (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows any advance payments or account credits available (e.g., Rs 100.00).",
                        "name": "Credits (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open a detailed Outstanding (Due) record. This expanded view lists all pending bill details and includes a View (Icon) on each line to see the full, itemized bill. At the bottom, it displays the Total due (e.g., Rs 497231.24) alongside a Pay (Button).",
                        "name": "View (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Billing"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_billing.png",
                    "alt": "Patient Billing"
                  },
                  {
                    "type": "text",
                    "content": "Clicking the Pay (Button) from the detailed billing view opens the \"Pay\" window to process a transaction."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select \"All\" to pay the entire balance, or select specific invoice numbers to pay them individually.",
                        "name": "Invoice Number * (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific amount owed based on the selected invoices.",
                        "name": "Total Balance Due (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the specific monetary amount being paid.",
                        "name": "Amount (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the method of transaction (e.g., Cash, Card).",
                        "name": "Payment Mode (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Defaults to today's date, but can be edited to log past payments.",
                        "name": "Collection Date (Calendar Input)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to execute and formally record the payment.",
                        "name": "Pay (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Pay Window Workflow"
                  },
                  {
                    "type": "text",
                    "content": "\"Other Pets\" section displays the profile pictures and names of any other pets currently registered under this same customer."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to immediately open the Step 2 \"Pet Registration\" window. This allows you to quickly register and add a brand-new pet under this existing customer's account without having to re-enter the owner's details.",
                        "name": "+ (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Other Pets"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Right Side Panel",
            "description": ""
          }
        ],
        "description": "Primary workspace accessed via Dashboard Appointment Card or Pet Record Table."
      },
      {
        "id": "topic-Overview",
        "title": "Overview",
        "groups": [
          {
            "id": "group-Overview",
            "items": [
              {
                "id": "item-overview",
                "title": "Overview",
                "details": [
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_overview.png",
                    "alt": "Overview"
                  },
                  {
                    "type": "text",
                    "content": "The Overview tab within the Center Workspace provides a comprehensive snapshot of the patient's current visit, active health conditions, and recent diagnostic results."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to modify the current appointment details.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the attending veterinarian assigned to the case (e.g., Dr. Luke Dunphy).",
                        "name": "Meeting (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the visit type, date, and exact time (e.g., Consultation · 31 Aug 2026, 11:56).",
                        "name": "Schedule (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the consultation mode and tags (e.g., Physical, Consultation).",
                        "name": "Tags (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays any preliminary notes or specific instructions regarding the appointment.",
                        "name": "Remarks (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Appointment Details"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays any logged items the pet is known to be allergic to (e.g., curd).",
                        "name": "Allergy List (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to manage or add new allergies. This opens a window to input a new allergy item. Click the Save (Button) within this window to apply the changes to the pet's medical profile.",
                        "name": "Tick (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Allergies"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_allergies.png",
                    "alt": "Allergies"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays a list of any current, ongoing medical prescriptions the patient is taking.",
                        "name": "Active Medications (Display Section)",
                        "helper": ""
                      }
                    ],
                    "title": "Active Medications"
                  },
                  {
                    "type": "text",
                    "content": "\"Diagnostics\" section displays a chronological grid of cards representing past and present diagnostic records for the patient."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "The name of the specific diagnostic procedure or test.",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "When the diagnostic was performed.",
                        "name": "Date & Time (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "The current state of the diagnostic (e.g., \"Review pending\").",
                        "name": "Status (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open the full diagnostic record for detailed review and editing.",
                        "name": "View (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Diagnostics"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the diagnostic name.",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the category or type of diagnostic.",
                        "name": "Type (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific variant used for the test.",
                        "name": "Variant (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the staff member who conducted the procedure.",
                        "name": "Performed By (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter, edit, or read the detailed clinical findings and report notes.",
                        "name": "Diagnostic Report Template (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the current medical stage of the results (Options: preliminary, final).",
                        "name": "Result Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the operational or workflow status of the diagnostic (Options: send for clinician’s review, pending patient review, completed).",
                        "name": "Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to update and save any changes made to the diagnostic record.\n\n\nSystem Notification: “Changes updated successfully!” appears immediately after clicking Save.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Diagnostic View"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Overview group",
            "description": "The Overview tab within the Center Workspace provides a comprehensive snapshot of the patient's current visit, active health conditions, and recent diagnostic results."
          }
        ],
        "description": "The Overview tab within the Center Workspace provides a comprehensive snapshot of the patient's current visit, active health conditions, and recent diagnostic results."
      },
      {
        "id": "vitals",
        "title": "Vitals",
        "groups": [
          {
            "id": "vitals-section",
            "items": [
              {
                "id": "vitals-overview",
                "title": "Vitals Overview & Record Table",
                "details": [
                  {
                    "type": "heading",
                    "title": "Add Vitals:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vitals_add_vitals.png",
                    "alt": "Vitals - Add Vitals"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/vitals-guide-video.mp4",
                    "poster": "/assets/images/knowledgebase/vitals_video_guide.jpg",
                    "alt": "Vitals Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Vitals Dashboard allows clinic staff to record, track, and manage essential health metrics for a patient during a consultation or visit."
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Vitals → Action → (+Add) → Fill in Vitals Details → Save"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the category or title of the vitals entry. By default, the name comes as \"Core Vitals\".",
                        "name": "Name (Display Field)"
                      },
                      {
                        "desc": "Shows the exact date and timestamp of when the vitals were logged into the system.",
                        "name": "Recorded Date/Time (Display Field)"
                      },
                      {
                        "desc": "Click to remove a specific vitals entry from the patient's record.",
                        "name": "Delete (Icon)"
                      }
                    ],
                    "title": "Vitals Record Table Fields"
                  }
                ],
                "summary": "The Vitals Dashboard allows clinic staff to record, track, and manage essential health metrics for a patient during a consultation or visit.",
                "workflow": "Patient Workbook → Vitals → Action → (+Add) → Fill in Vitals Details → Save"
              },
              {
                "id": "add-vitals",
                "title": "Add Vitals",
                "details": [
                  {
                    "type": "text",
                    "content": "Click (+Add) to open the form and create a new vitals entry for the patient. The vitals form allows you to add the following details:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/vitals_form_details.png",
                    "alt": "Vitals Form"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the patient's body weight in kg.",
                        "name": "Body Weight (Input Field)"
                      },
                      {
                        "desc": "Enter the patient's body temperature in °F.",
                        "name": "Body Temperature (Input Field)"
                      },
                      {
                        "desc": "Enter the patient's heart rate in bpm.",
                        "name": "Heart Rate (Input Field)"
                      },
                      {
                        "desc": "Enter the patient's respiratory rate in brpm.",
                        "name": "Respiratory Rate (Input Field)"
                      },
                      {
                        "desc": "Select the capillary refill time in seconds.",
                        "name": "Capillary Refill Time (Drop-Down)"
                      },
                      {
                        "desc": "Select the mucous membrane color.",
                        "name": "Mucous Membrane Color (Drop-Down)"
                      },
                      {
                        "desc": "Enter the body condition score on the provided scale.",
                        "name": "Body Condition Score (Input Field)"
                      },
                      {
                        "desc": "Select the patient's pain score from the scale.",
                        "name": "Pain Score (Drop-Down)"
                      },
                      {
                        "desc": "Select the patient's current mentation status.",
                        "name": "Mentation (Drop-Down)"
                      },
                      {
                        "desc": "Select the patient's hydration level.",
                        "name": "Hydration Status (Drop-Down)"
                      },
                      {
                        "desc": "Select the patient's pulse quality.",
                        "name": "Pulse Quality (Drop-Down)"
                      }
                    ],
                    "title": "Form Fields & Details"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Present at the bottom of the Vitals form; click to save the entered vitals data.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  }
                ],
                "summary": "Click (+Add) to open the form and create a new vitals entry for the patient.",
                "workflow": "Patient Workbook → Vitals → Action → (+Add) → Fill in Vitals Details → Save"
              }
            ],
            "title": "Vitals",
            "description": "Record, track, and manage essential health metrics."
          }
        ],
        "description": "The Vitals Dashboard allows clinic staff to record, track, and manage essential health metrics for a patient during a consultation or visit."
      },
      {
        "id": "soap-notes",
        "title": "SOAP Notes",
        "groups": [
          {
            "id": "soap-notes-section",
            "items": [
              {
                "id": "soap-notes-overview",
                "title": "SOAP Notes Overview & Record Table",
                "details": [
                  {
                    "type": "heading",
                    "title": "SOAP Notes:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/soap_notes_overview.png",
                    "alt": "SOAP Notes"
                  },
                  {
                    "type": "heading",
                    "title": "Add SOAP Notes:"
                  },
                  {
                    "type": "text",
                    "content": "The SOAP Notes Dashboard allows doctors and clinic staff to record detailed clinical observations, diagnoses, and structured treatment plans for a patient during a consultation."
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → SOAP Notes → Action → (Manual SOAP Icon) → Fill in Details → Save"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the sequential title of the note (e.g., \"Note 2\", \"Note 1\"). The latest entries always appear at the top of the list.",
                        "name": "Name (Display Field)"
                      },
                      {
                        "desc": "Displays the name of the user who created the entry.",
                        "name": "Recorded By (Display Field)"
                      },
                      {
                        "desc": "Shows the exact date and timestamp of when the note was logged into the system.",
                        "name": "Recorded Date/Time (Display Field)"
                      }
                    ],
                    "title": "SOAP Notes Record Table Fields"
                  }
                ],
                "summary": "The SOAP Notes Dashboard allows doctors and clinic staff to record detailed clinical observations, diagnoses, and structured treatment plans for a patient during a consultation.",
                "workflow": "Patient Workbook → SOAP Notes → Action → (Manual SOAP Icon) → Fill in Details → Save"
              },
              {
                "id": "add-manual-soap-note",
                "title": "Add Manual SOAP Note",
                "details": [
                  {
                    "type": "text",
                    "content": "Click the (+) Manual SOAP Icon to open the form and create a new clinical note for the patient. The form allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the clinical diagnosis for the patient.",
                        "name": "Diagnosis (Input Field)"
                      },
                      {
                        "desc": "Record subjective observations, such as history and symptoms reported by the customer.",
                        "name": "Subjective (Input Field)"
                      },
                      {
                        "desc": "Record objective observations, such as physical exam findings and measurable data.",
                        "name": "Objective (Input Field)"
                      },
                      {
                        "desc": "Enter the medical assessment or analysis of the subjective and objective findings.",
                        "name": "Assessment (Input Field)"
                      },
                      {
                        "desc": "Detail the treatment plan, medications, or clinical next steps.",
                        "name": "Plan (Input Field)"
                      },
                      {
                        "desc": "Enter the final conclusion or summary of the consultation.",
                        "name": "Conclusion (Input Field)"
                      },
                      {
                        "desc": "Document any abnormal or irregular clinical findings.",
                        "name": "Abnormal Findings (Input Field)"
                      },
                      {
                        "desc": "Add specific care instructions to be communicated to the pet owner.",
                        "name": "Customer Instructions (Input Field)"
                      },
                      {
                        "desc": "Note any follow-ups, rechecks, or future reminders needed for the patient.",
                        "name": "Reminders (Input Field)"
                      }
                    ],
                    "title": "Form Fields & Details"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Present at the bottom of the form; click to save the newly entered SOAP note.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  }
                ],
                "summary": "Click the (+) Manual SOAP Icon to open the form and create a new clinical note for the patient.",
                "workflow": "Patient Workbook → SOAP Notes → Action → (Manual SOAP Icon) → Fill in Details → Save"
              },
              {
                "id": "edit-existing-soap-note",
                "title": "Edit Existing SOAP Note",
                "details": [
                  {
                    "type": "text",
                    "content": "To edit a previously added note, click on the Note Name directly from the record table, edit the necessary details, and use the action buttons at the bottom of the form:"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to apply and save the updated changes to the existing note.",
                        "name": "Modify (Button)"
                      },
                      {
                        "desc": "Click to cancel your edits. \n\n\nNote: Clicking this will trigger a confirmation pop-up asking, \"Are you sure you want to discard the note?\" with Yes and No buttons.",
                        "name": "Discard (Button)"
                      },
                      {
                        "desc": "Click to save the entries.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Action Buttons & Pop-Up Confirmations"
                  },
                  {
                    "type": "notification",
                    "title": "System Notifications",
                    "content": "Success Confirmation: Upon successfully clicking Save or Modify, a pop-up notification will appear at the bottom right of the screen stating: \"Changes updated successfully!\"."
                  }
                ],
                "summary": "To edit a previously added note, click on the Note Name directly from the record table."
              }
            ],
            "title": "SOAP Notes",
            "description": "Record clinical observations, diagnoses, and treatment plans."
          }
        ],
        "description": "The SOAP Notes Dashboard allows doctors and clinic staff to record detailed clinical observations, diagnoses, and structured treatment plans."
      },
      {
        "id": "procedures",
        "title": "Procedures",
        "groups": [
          {
            "id": "procedures-section",
            "items": [
              {
                "id": "procedure-workflow",
                "title": "Procedure Workflow",
                "details": [
                  {
                    "type": "heading",
                    "title": "Procedure Workflow:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/procedure_workflow.png",
                    "alt": "Procedure Workflow"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/procedure-workflow-video.mp4",
                    "poster": "/assets/images/knowledgebase/procedure_video.jpg",
                    "alt": "Procedure Workflow Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "Records all medical and non-medical procedures performed on the patient during their visit."
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → (+ Add procedure) → Fill in Procedure Details → Add Procedure"
                  }
                ],
                "summary": "Records all medical and non-medical procedures performed on the patient during their visit.",
                "workflow": "Patient Workbook → Procedures → (+ Add procedure) → Fill in Procedure Details → Add Procedure"
              },
              {
                "id": "add-a-procedure",
                "title": "Add a Procedure",
                "details": [
                  {
                    "type": "heading",
                    "title": "Step 1:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/procedure_step1.png",
                    "alt": "Add a Procedure - Step 1"
                  },
                  {
                    "type": "heading",
                    "title": "Step 2:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/procedure_step2.png",
                    "alt": "Add a Procedure - Step 2"
                  },
                  {
                    "type": "text",
                    "content": "Click (+ Add procedure) to open the form and add a new procedure for the patient. The form allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search and select the medical or non-medical procedure/service from the list.\n\n\nHelper Text: Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below.",
                        "name": "Procedure / Service (Input Field / Drop-Down)",
                        "helper": "Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below."
                      },
                      {
                        "desc": "Select a specific variant related to the chosen procedure.\n\n\nHelper Text: Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below.",
                        "name": "Variant (Drop-Down)",
                        "helper": "Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below."
                      },
                      {
                        "desc": "Enter the exact billable quantity for the procedure.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Select a specific dose related to the chosen procedure.",
                        "name": "DOSE IN SERIES (Drop-Down) {For Vaccines}"
                      }
                    ],
                    "title": "Form Fields & Details"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to cancel your entries and exit the form without saving.",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Click to save and add the procedure to the patient's record.",
                        "name": "Add Procedure (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notifications",
                    "content": "Success Confirmation: Upon successfully clicking + Add Procedure, a system notification will appear stating: \"Procedure added successfully\"."
                  }
                ],
                "summary": "Click (+ Add procedure) to open the form and add a new procedure for the patient.",
                "workflow": "Patient Workbook → Procedures → (+ Add procedure) → Fill in Procedure Details → Add Procedure"
              },
              {
                "id": "manage-added-procedures",
                "title": "Manage Added Procedures",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a procedure is added, it displays in the Procedures list with options to view cost breakdowns, edit details, add consumables or administered items, or delete the record."
                  }
                ],
                "summary": "View and manage procedures added to the patient's record."
              },
              {
                "id": "procedure-summary-card",
                "title": "Procedure Summary Card",
                "details": [
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/manage_added_procedures.png",
                    "alt": "Procedure Summary Card"
                  },
                  {
                    "type": "text",
                    "content": "Once a procedure is successfully added to the patient's record, it displays a summary card detailing the costs, utilized items, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the primary category of the procedure (e.g., Surgery).",
                        "name": "Category (Display Field)"
                      },
                      {
                        "desc": "Displays the specific name of the added procedure (e.g., Abdominal Cryptorchid Neutering - L).",
                        "name": "Procedure (Display Field)"
                      },
                      {
                        "desc": "Shows the quantity billed for the procedure.",
                        "name": "Billable Qty (Display Field)"
                      },
                      {
                        "desc": "Displays the base package price.",
                        "name": "Package Price (Display Field)"
                      },
                      {
                        "desc": "Shows any additional charges for items billed separately from the package.",
                        "name": "Extra (Billed) (Display Field)"
                      },
                      {
                        "desc": "Displays the final total cost (Package Price + Extra Billed).",
                        "name": "Line Total (Display Field)"
                      }
                    ],
                    "title": "Record Details (Display Fields)"
                  },
                  {
                    "type": "options",
                    "items": [
                      {
                        "desc": "Click to view the expanded cost breakdown, which displays:\n\n\nService Charge: The fee for the medical service itself.\n\n\nBOM Charge: The cost of materials bundled into the package.\n\n\nPackage Price: The fixed total price (does not change with actual usage).",
                        "name": "Price Breakdown (Option)"
                      },
                      {
                        "desc": "Shows real-time usage tags such as \"✓4 items used from package — no extra charge\" or \"+0 extra items billed separately\".",
                        "name": "Status Tags (Display)"
                      }
                    ],
                    "title": "Options & Tags"
                  }
                ],
                "summary": "Displays a summary card detailing the costs, utilized items, and available action buttons."
              },
              {
                "id": "edit-a-procedure",
                "title": "Edit a Procedure",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing procedure. The form displays the title \"Edit procedure — [Procedure Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select or change the specific variant related to the procedure.",
                        "name": "Variant (Drop-Down)",
                        "helper": "Selecting a procedure with a default kit makes it available under \"Add from procedure kit\" below."
                      },
                      {
                        "desc": "Update the billable quantity.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Displays the locked base service charge.",
                        "name": "Service Charge (Non-Editable Field)"
                      },
                      {
                        "desc": "Displays the locked base consumables charge.",
                        "name": "Consumables Charge (Non-Editable Field)"
                      },
                      {
                        "desc": "Displays the locked total price (Service + Consumables).",
                        "name": "Package Price (Non-Editable Field)"
                      }
                    ],
                    "title": "Editable & Non-Editable Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to cancel your entries and exit the form without saving.",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Click to save the updated procedure details.",
                        "name": "Save Changes (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Procedure updated successfully\" appears after clicking Save Changes."
                  }
                ],
                "summary": "Click Edit (Button) to modify an existing procedure.",
                "workflow": "Patient Workbook → Procedures → Edit → Update Details → Save Changes"
              },
              {
                "id": "add-items-to-a-procedure",
                "title": "Add Items to a Procedure",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → + Add items → Fill Details → Add To Section → Done"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link consumables or administered medications directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Choose whether the item is a consumable used during the procedure or a medication administered to the patient.",
                        "name": "Add Consumables / Administered (Section Selector)"
                      },
                      {
                        "desc": "Search and select the specific item to add.",
                        "name": "Item (Input Field / Drop-Down)"
                      },
                      {
                        "desc": "Select the batch number (auto-fills based on the selected item).",
                        "name": "Batch / Lot No. (Drop-Down)"
                      },
                      {
                        "desc": "Enter the quantity used or administered.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Select the unit of measurement.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration route (e.g., oral, ear drops). \n\n\nNote: This field only appears if the \"Administered\" section is selected.",
                        "name": "Route (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration dose.",
                        "name": "Dose (Input Field)"
                      },
                      {
                        "desc": "Check this box to charge the item as an extra line item outside of the base package.",
                        "name": "Bill separately (Checkbox)",
                        "helper": "Suggested automatically — override if this case needs something different."
                      }
                    ],
                    "title": "Form Fields & Details"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to attach the current item to the procedure. \n\n\nUsers can repeat this process to attach multiple items.",
                        "name": "Add To Consumables / Administered (Button)"
                      },
                      {
                        "desc": "Click once all items have been added to save changes and close the form.",
                        "name": "Done (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ],
                "summary": "Click + Add items (Button) to link consumables or administered medications directly to the added procedure.",
                "workflow": "Patient Workbook → Procedures → + Add items → Fill Details → Add To Section → Done"
              },
              {
                "id": "delete-a-procedure",
                "title": "Delete a Procedure",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a procedure will also permanently delete any items from the \"Consumables\" and \"Administered\" sections that were linked to this procedure.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ],
                "summary": "Click Delete (Button) to remove the procedure from the patient's record.",
                "workflow": "Patient Workbook → Procedures → Delete (Button) → Confirm"
              }
            ],
            "title": "Procedures",
            "description": "Records all medical and non-medical procedures performed on the patient during their visit."
          },
          {
            "id": "consumables-section",
            "items": [
              {
                "id": "add-a-consumable",
                "title": "Add a Consumable",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Consumables → (+ Add item) → Fill in Details → Add Consumable"
                  },
                  {
                    "type": "heading",
                    "title": "Step 1:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/consumables_step1.png",
                    "alt": "Consumables - Step 1"
                  },
                  {
                    "type": "heading",
                    "title": "Step 2:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/consumables_step2.png",
                    "alt": "Consumables - Step 2"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link consumables directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search and select the specific item to add.",
                        "name": "Item (Input Field / Drop-Down)"
                      },
                      {
                        "desc": "Select the batch number (auto-fills based on the selected item).",
                        "name": "Batch / Lot No. (Drop-Down)"
                      },
                      {
                        "desc": "Enter the quantity used or administered.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Select the unit of measurement.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures.",
                        "name": "LINK TO PROCEDURE (Drop-Down)"
                      },
                      {
                        "desc": "Check this box to charge the item as an extra line item outside of the base package.",
                        "name": "Bill separately (Checkbox)",
                        "helper": "Suggested automatically — override if this case needs something different."
                      }
                    ],
                    "title": "Form Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to attach the current item to the procedure. \n\n\nUsers can repeat this process to attach multiple items.",
                        "name": "Add To Consumables (Button)"
                      },
                      {
                        "desc": "Click once all items have been added to save changes and close the form.",
                        "name": "Done (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ],
                "summary": "Click + Add items (Button) to link consumables directly to the added procedure or record them standalone.",
                "workflow": "Patient Workbook → Procedures → Consumables → (+ Add item) → Fill in Details → Add Consumable"
              },
              {
                "id": "consumables-summary-card",
                "title": "Consumables Summary Card",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a Consumable is successfully added to the patient's record, it displays a summary card detailing the costs, quantity, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the name of the Consumable.",
                        "name": "Name (Display Field)"
                      },
                      {
                        "desc": "Shows the quantity billed for the procedure.",
                        "name": "Billable Qty (Display Field)"
                      },
                      {
                        "desc": "Displays the price.",
                        "name": "Price (Display Field)"
                      },
                      {
                        "desc": "'Used', 'Requested'.",
                        "name": "Status (Display Field)"
                      },
                      {
                        "desc": "Batch details.",
                        "name": "Batch (Display Field)"
                      },
                      {
                        "desc": "Indicates the inventory location of the added item.",
                        "name": "Location (Display Field)"
                      },
                      {
                        "desc": "Displays \"Not linked\" if its not linked to a procedure, Displays procedure name if its linked to a procedure.",
                        "name": "Linked to procedure tag (Display Field)"
                      },
                      {
                        "desc": "Displays the specific name of the added procedure (e.g., Abdominal Cryptorchid Neutering - L) if the item is linked to it.",
                        "name": "Procedure (Display Field)"
                      }
                    ],
                    "title": "Record Details (Display Fields)"
                  },
                  {
                    "type": "options",
                    "items": [
                      {
                        "desc": "Shows real-time usage tags such as \"Requested from Pharmacy\".",
                        "name": "Status Tags (Display)"
                      }
                    ],
                    "title": "Options & Tags"
                  }
                ],
                "summary": "Displays a summary card detailing the costs, quantity, and available action buttons."
              },
              {
                "id": "edit-a-consumable",
                "title": "Edit a Consumable",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Consumables → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing consumable. The form displays the title \"Edit item— [Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the batch number (auto-fills based on the selected item).",
                        "name": "Batch / Lot No. (Drop-Down)"
                      },
                      {
                        "desc": "Enter the quantity used or administered.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Select the unit of measurement.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures.",
                        "name": "LINK TO PROCEDURE (Drop-Down)"
                      },
                      {
                        "desc": "Displays the status.",
                        "name": "Billing status (Display field)"
                      }
                    ],
                    "title": "Form Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to cancel and exit the form without saving.",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Click to save the updated details.",
                        "name": "Save Changes (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Procedure updated successfully\" appears after clicking Save Changes."
                  }
                ],
                "summary": "Click Edit (Button) to modify an existing consumable.",
                "workflow": "Patient Workbook → Procedures → Consumables → Edit → Update Details → Save Changes"
              },
              {
                "id": "delete-a-consumable",
                "title": "Delete a Consumable",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Consumables → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a item will also permanently delete it from the \"Consumable \" section.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ],
                "summary": "Click Delete (Button) to remove the consumable from the patient's record.",
                "workflow": "Patient Workbook → Procedures → Consumables → Delete (Button) → Confirm"
              }
            ],
            "title": "Consumables",
            "description": "Manage consumables used during procedures or visits."
          },
          {
            "id": "administer-section",
            "items": [
              {
                "id": "add-an-administered-item",
                "title": "Add an Administered Item",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Administer → (+ Add item) → Fill in Details → Add to Administered"
                  },
                  {
                    "type": "heading",
                    "title": "Step 1:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/administer_step1.png",
                    "alt": "Administer - Step 1"
                  },
                  {
                    "type": "heading",
                    "title": "Step 2:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/administer_step2.png",
                    "alt": "Administer - Step 2"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link Administered item directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search and select the specific item to add.",
                        "name": "Item (Input Field / Drop-Down)"
                      },
                      {
                        "desc": "Select the batch number (auto-fills based on the selected item).",
                        "name": "Batch / Lot No. (Drop-Down)"
                      },
                      {
                        "desc": "Enter the quantity used or administered.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Select the unit of measurement.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration route (e.g., oral, ear drops).",
                        "name": "Route (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration dose.",
                        "name": "Dose (Input Field)"
                      },
                      {
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures.",
                        "name": "LINK TO PROCEDURE (Drop-Down)"
                      },
                      {
                        "desc": "Check this box to charge the item as an extra line item outside of the base package.",
                        "name": "Bill separately (Checkbox)",
                        "helper": "Suggested automatically — override if this case needs something different."
                      }
                    ],
                    "title": "Form Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to attach the current item to the procedure. Users can repeat this process to attach multiple items.",
                        "name": "Add To Administered (Button)"
                      },
                      {
                        "desc": "Click once all items have been added to save changes and close the form.",
                        "name": "Done (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ],
                "summary": "Click + Add items (Button) to link Administered item directly to the added procedure.",
                "workflow": "Patient Workbook → Procedures → Administer → (+ Add item) → Fill in Details → Add to Administered"
              },
              {
                "id": "controlled-substance-workflow",
                "title": "Controlled Substance Workflow",
                "details": [
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/controlled-substance-workflow.mp4",
                    "alt": "Controlled Substance Workflow Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "Controlled Substance Workflow: View details and video guide for logging controlled substance administration."
                  }
                ],
                "summary": "Controlled Substance administration workflow."
              },
              {
                "id": "administered-summary-card",
                "title": "Administered Summary Card",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a Administered is successfully added to the patient's record, it displays a summary card detailing the costs, quantity, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the name of the Consumable.",
                        "name": "Name (Display Field)"
                      },
                      {
                        "desc": "Shows the quantity billed for the procedure.",
                        "name": "Billable Qty (Display Field)"
                      },
                      {
                        "desc": "Displays the price.",
                        "name": "Price (Display Field)"
                      },
                      {
                        "desc": "'Used', 'Requested'.",
                        "name": "Status (Display Field)"
                      },
                      {
                        "desc": "Batch details.",
                        "name": "Batch (Display Field)"
                      },
                      {
                        "desc": "Indicates the inventory location of the added item.",
                        "name": "Location (Display Field)"
                      },
                      {
                        "desc": "The administration route (e.g., oral, ear drops).",
                        "name": "Route (Display Field)"
                      },
                      {
                        "desc": "Select the administration dose.",
                        "name": "Dose (Display Field)"
                      },
                      {
                        "desc": "Displays \"Not linked\" if its not linked to a procedure, Displays procedure name if its linked to a procedure.",
                        "name": "Linked to procedure tag (Display Field)"
                      },
                      {
                        "desc": "Displays the specific name of the added procedure (e.g., Abdominal Cryptorchid Neutering - L) if the item is linked to it.",
                        "name": "Procedure (Display Field)"
                      }
                    ],
                    "title": "Record Details (Display Fields)"
                  },
                  {
                    "type": "options",
                    "items": [
                      {
                        "desc": "Shows real-time usage tags such as \"Requested from Pharmacy\".",
                        "name": "Status Tags (Display)"
                      }
                    ],
                    "title": "Options & Tags"
                  }
                ],
                "summary": "Displays a summary card detailing the costs, quantity, dose, route, and action buttons."
              },
              {
                "id": "edit-an-administered-item",
                "title": "Edit an Administered Item",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Administered → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing consumable. The form displays the title \"Edit item— [Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the batch number (auto-fills based on the selected item).",
                        "name": "Batch / Lot No. (Drop-Down)"
                      },
                      {
                        "desc": "Enter the quantity used or administered.",
                        "name": "Billable Qty (Input Field)"
                      },
                      {
                        "desc": "Select the unit of measurement.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "The administration route (e.g., oral, ear drops).",
                        "name": "Route (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration dose.",
                        "name": "Dose (Input Field)"
                      },
                      {
                        "desc": "Select \"Not linked to a procedure\" to keep it separate or choose anyone of the added procedures.",
                        "name": "LINK TO PROCEDURE (Drop-Down)"
                      },
                      {
                        "desc": "Displays the status.",
                        "name": "Billing status (Display field)"
                      }
                    ],
                    "title": "Form Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to cancel and exit the form without saving.",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Click to save the updated details.",
                        "name": "Save Changes (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Procedure updated successfully\" appears after clicking Save Changes."
                  }
                ],
                "summary": "Click Edit (Button) to modify an existing administered item.",
                "workflow": "Patient Workbook → Procedures → Administered → Edit → Update Details → Save Changes"
              },
              {
                "id": "delete-an-administered-item",
                "title": "Delete an Administered Item",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Administered → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a item will also permanently delete it from the \"Administered\" section.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ],
                "summary": "Click Delete (Button) to remove the item from the patient's record.",
                "workflow": "Patient Workbook → Procedures → Administered → Delete (Button) → Confirm"
              },
              {
                "id": "add-memo",
                "title": "Add Memo",
                "details": [
                  {
                    "type": "text",
                    "content": "Click to write Memo for the administered drug or for the entire treatment."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to save the memo.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  }
                ],
                "summary": "Click to write Memo for the administered drug or for the entire treatment."
              }
            ],
            "title": "Administer",
            "description": "Record and manage administered medications, doses, and memos."
          },
          {
            "id": "prescription-section",
            "items": [
              {
                "id": "add-prescription-medication",
                "title": "Add Prescription / Medication",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Prescription → (+ Add medication) → Fill in Details → Add to Prescription"
                  },
                  {
                    "type": "heading",
                    "title": "Step 1:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/prescription_step1.png",
                    "alt": "Prescription - Step 1"
                  },
                  {
                    "type": "heading",
                    "title": "Step 2:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/prescription_step2.png",
                    "alt": "Prescription - Step 2"
                  },
                  {
                    "type": "text",
                    "content": "Click the + Add items (Button) to link an item directly to the added procedure."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search and select the specific item to add.",
                        "name": "DRUG / MEDICATION (Input Field / Drop-Down)"
                      },
                      {
                        "desc": "Enter the strength.",
                        "name": "Strength / Dose (Input Field)"
                      },
                      {
                        "desc": "When to give that item (morning, afternoon).",
                        "name": "Frequency (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration route (e.g., oral, ear drops).",
                        "name": "Route (Drop-Down)"
                      },
                      {
                        "desc": "Select the duration.",
                        "name": "Duration (Drop-Down)"
                      },
                      {
                        "desc": "Select the instruction (e.g., With food, empty stomach).",
                        "name": "Instructions (Drop-Down)"
                      },
                      {
                        "desc": "To write remarks.",
                        "name": "Remark (Input Field)"
                      }
                    ],
                    "title": "Form Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to attach the current item to the Prescription . \n\n\nUsers can repeat this process to attach multiple items.",
                        "name": "Add To Prescription (Button)"
                      },
                      {
                        "desc": "Click once all items have been added to save changes and close the form.",
                        "name": "Done (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Item Added Successfully\" appears every time this button is clicked."
                  }
                ],
                "summary": "Click + Add items (Button) to link an item directly to the added procedure / prescription.",
                "workflow": "Patient Workbook → Procedures → Prescription → (+ Add medication) → Fill in Details → Add to Prescription"
              },
              {
                "id": "prescription-summary-card",
                "title": "Prescription Summary Card",
                "details": [
                  {
                    "type": "text",
                    "content": "Once a item is successfully added to the patient's record, it displays a summary card detailing the dose, route, and available action buttons."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the name of the Consumable.",
                        "name": "Name (Display Field)"
                      },
                      {
                        "desc": "The administration route (e.g., oral, ear drops).",
                        "name": "Route (Display Field)"
                      },
                      {
                        "desc": "Select the administration dose.",
                        "name": "Dose (Display Field)"
                      },
                      {
                        "desc": "Enter the strength.",
                        "name": "Strength / Dose (Display Field)"
                      },
                      {
                        "desc": "Show the selected frequency (morning, afternoon).",
                        "name": "Frequency (Display Field)"
                      },
                      {
                        "desc": "Displays the duration.",
                        "name": "Duration (Display Field)"
                      },
                      {
                        "desc": "Displays the instruction (e.g., With food, empty stomach).",
                        "name": "Instructions (Display Field)"
                      },
                      {
                        "desc": "Displays remarks.",
                        "name": "Remark (Display Field)"
                      }
                    ],
                    "title": "Record Details (Display Fields)"
                  }
                ],
                "summary": "Displays a summary card detailing the dose, route, and available action buttons."
              },
              {
                "id": "edit-a-prescription-item",
                "title": "Edit a Prescription Item",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Prescription → Edit → Update Details → Save Changes"
                  },
                  {
                    "type": "text",
                    "content": "Click the Edit (Button) to modify an existing consumable. The form displays the title \"Edit item— [Name]\" and allows you to update the following:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the strength.",
                        "name": "Strength / Dose (Input Field)"
                      },
                      {
                        "desc": "When to give that item (morning, afternoon).",
                        "name": "Frequency (Drop-Down)"
                      },
                      {
                        "desc": "Select the administration route (e.g., oral, ear drops).",
                        "name": "Route (Drop-Down)"
                      },
                      {
                        "desc": "Select the duration.",
                        "name": "Duration (Drop-Down)"
                      },
                      {
                        "desc": "Select the instruction (e.g., With food, empty stomach).",
                        "name": "Instructions (Drop-Down)"
                      },
                      {
                        "desc": "To write remarks.",
                        "name": "Remark (Input Field)"
                      }
                    ],
                    "title": "Form Fields"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to cancel and exit the form without saving.",
                        "name": "Cancel (Button)"
                      },
                      {
                        "desc": "Click to save the updated details.",
                        "name": "Save Changes (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "\"Prescription updated successfully\" appears after clicking Save Changes."
                  }
                ],
                "summary": "Click Edit (Button) to modify an existing prescription item.",
                "workflow": "Patient Workbook → Procedures → Prescription → Edit → Update Details → Save Changes"
              },
              {
                "id": "delete-a-prescription-item",
                "title": "Delete a Prescription Item",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Procedures → Prescription item → Delete (Button) → Confirm"
                  },
                  {
                    "type": "text",
                    "content": "Click the Delete (Button) to remove the procedure from the patient's record."
                  },
                  {
                    "type": "systemAction",
                    "title": "System Action & Pop-Up",
                    "content": "System Action: Deleting a item will also permanently delete it from the \"Prescription \" section.\n\nConfirmation Pop-Up: A window will appear asking, \"Are you sure you want to delete this record?\" Click Yes to confirm deletion or No to cancel."
                  }
                ],
                "summary": "Click Delete (Button) to remove the procedure / item from the patient's record.",
                "workflow": "Patient Workbook → Procedures → Prescription item → Delete (Button) → Confirm"
              },
              {
                "id": "add-notes",
                "title": "Add Notes",
                "details": [
                  {
                    "type": "text",
                    "content": "Click to write Notes for the drug."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to save the Notes.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  }
                ],
                "summary": "Click to write Notes for the drug."
              },
              {
                "id": "preview-prescription",
                "title": "Preview Prescription",
                "details": [
                  {
                    "type": "text",
                    "content": "To preview the prescription."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Preview the prescription.",
                        "name": "Preview (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Action Buttons"
                  }
                ],
                "summary": "Preview the prescription."
              },
              {
                "id": "print-prescription",
                "title": "Print Prescription",
                "details": [
                  {
                    "type": "text",
                    "content": "To print the prescription."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "To print the prescription.",
                        "name": "Print (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  }
                ],
                "summary": "Print the prescription."
              }
            ],
            "title": "Prescription",
            "description": "Add, edit, preview, print, and delete take-home prescriptions."
          }
        ],
        "description": "Records all medical and non-medical procedures performed on the patient during their visit, followed by the sections: consumable, administered, prescription."
      },
      {
        "id": "topic-Billing ",
        "title": "Billing",
        "groups": [
          {
            "id": "group-Billing ",
            "items": [
              {
                "id": "item-Billing",
                "title": "Billing Overview",
                "summary": "",
                "workflow": "Patient Module → Billing → Invoices\nPatient Module → Billing → Estimates",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module → Billing → Invoices"
                  },
                  {
                    "type": "workflow",
                    "content": "Patient Module → Billing → Estimates"
                  },
                  {
                    "type": "text",
                    "content": "The Billing section within the Patient Workbook provides a comprehensive view of a patient's financial history. It is divided into two primary modules: Invoices and Estimates, allowing staff to review past transactions, generate cost approximations, and convert estimates into active bills."
                  }
                ]
              },
              {
                "id": "item-Invoices",
                "title": "Invoices",
                "summary": "",
                "workflow": "Patient Module → Billing → Invoices",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module → Billing → Invoices"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module displays the complete invoice history for the patient, sorted chronologically by date. Each invoice is presented as a summary card for quick review."
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Search & Filter Options",
                        "desc": "Use the following fields to locate specific billing records:",
                        "helper": ""
                      },
                      {
                        "name": "Invoice Number (Input Field)",
                        "desc": "Enter a specific invoice ID to search.",
                        "helper": ""
                      },
                      {
                        "name": "Payment Status (Drop-Down with Checkboxes)",
                        "desc": "Filter by current standing (Options: All, Paid, Unpaid, Partially Paid, Cancelled).",
                        "helper": ""
                      },
                      {
                        "name": "Consultation Type (Drop-Down with Checkboxes)",
                        "desc": "Filter by visit type (Options: All, Home Visit, Outpatient, Virtual).",
                        "helper": ""
                      },
                      {
                        "name": "Vet Name (Input Field)",
                        "desc": "Search for invoices generated by a specific veterinarian.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Advanced Filter Options",
                    "items": [
                      {
                        "name": "Advanced Filter (Button)",
                        "desc": "Clicking the Filter button opens a secondary window to narrow results further:",
                        "helper": ""
                      },
                      {
                        "name": "Filter by Date (Checkbox)",
                        "desc": "Select this option and click Add to reveal Start Date and End Date calendar input fields.",
                        "helper": ""
                      },
                      {
                        "name": "Apply Filters (Button)",
                        "desc": "Click to execute the advanced search.",
                        "helper": ""
                      },
                      {
                        "name": "Reset All (Button)",
                        "desc": "Click to clear the selections within the filter window.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Reset",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Located on the main dashboard; click to clear all applied search and filter parameters.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "The dashboard displays a total count (e.g., Number of Records: 10) followed by a list of invoice summary cards."
                  },
                  {
                    "type": "fields",
                    "title": "Invoice Cards List",
                    "items": [
                      {
                        "name": "Card Display Fields",
                        "desc": "Date, Invoice ID, Vet Name, Payable Amount, and Payment details.",
                        "helper": ""
                      },
                      {
                        "name": "Status Tags (Display)",
                        "desc": "Quick-glance tags indicating Consultation Type, Payment Status, and whether the bill is Open or Closed.",
                        "helper": ""
                      },
                      {
                        "name": "Card Action (Click)",
                        "desc": "Clicking directly on any invoice card opens the full, detailed bill for review.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "item-Estimates",
                "title": "Estimates",
                "summary": "",
                "workflow": "Patient Module → Billing → Estimates",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module → Billing → Estimates"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module allows staff to generate cost approximations for future or proposed treatments. These estimates can be saved, reviewed, and later converted directly into an active invoice."
                  },
                  {
                    "type": "buttons",
                    "title": "Creating an Estimate",
                    "items": [
                      {
                        "name": "Create Estimate (Button)",
                        "desc": "Click to open the billing interface in \"Estimate\" mode.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Estimate Window Details",
                    "items": [
                      {
                        "name": "Header Information",
                        "desc": "Displays the current Date/Time (e.g., 18 Sep 2026, 17:02) and sets the Type to \"Estimate\".",
                        "helper": ""
                      },
                      {
                        "name": "Items Table",
                        "desc": "Displays the services or products added to the estimate. Columns include: Service/Item Name, Qty, Unit Price, Unit Discount, Tax, and Item Total.",
                        "helper": ""
                      },
                      {
                        "name": "Add (Button)",
                        "desc": "Click to open a window to search and add specific services or items to the estimate.",
                        "helper": ""
                      },
                      {
                        "name": "Apply Discount (Button)",
                        "desc": "Click to apply a percentage or flat-rate discount to the estimate.",
                        "helper": ""
                      },
                      {
                        "name": "Discard (Icon)",
                        "desc": "Click to delete the current draft of the estimate.",
                        "helper": ""
                      },
                      {
                        "name": "Save As Draft (Button)",
                        "desc": "Click to save the estimate to the patient's record.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Once saved, estimates appear on the dashboard under a total count (e.g., Number of Records: 1)."
                  },
                  {
                    "type": "fields",
                    "title": "Estimates Cards List",
                    "items": [
                      {
                        "name": "Card Display Fields",
                        "desc": "Date, Estimate ID, an \"Estimate\" identification tag, and the Payable Amount.",
                        "helper": ""
                      },
                      {
                        "name": "Card Action (Click)",
                        "desc": "Clicking directly on the card opens the full estimate for detailed review.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Converting an Estimate",
                    "items": [
                      {
                        "name": "Convert (Button)",
                        "desc": "Present on the estimate card. Clicking this triggers a confirmation pop-up stating: \"Convert this estimate to an invoice? All services from this estimate will be added to the current appointment’s bill.\"",
                        "helper": ""
                      },
                      {
                        "name": "Yes (Button)",
                        "desc": "Click Yes to instantly transfer all line items to the patient's active bill.",
                        "helper": ""
                      },
                      {
                        "name": "No (Button)",
                        "desc": "Click No to cancel the conversion.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Billing",
            "description": "The Billing section within the Patient Workbook provides a comprehensive view of a patient's financial history."
          }
        ],
        "description": "The Billing section within the Patient Workbook provides a comprehensive view of a patient's financial history."
      },
      {
        "id": "topic-History",
        "title": "History",
        "groups": [
          {
            "id": "group-History",
            "items": [
              {
                "id": "item-History",
                "title": "History",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Module  → History"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/patient_history.png",
                    "alt": "History"
                  },
                  {
                    "type": "text",
                    "content": "The History tab acts as a comprehensive, chronological archive storing all past and current medical records, consultations, and procedures for the pet. It provides advanced search and filtering tools to quickly locate specific past visits."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Filter records by the specific type of consultation or visit.",
                        "name": "Type (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter records by the attending veterinarian who handled the case.",
                        "name": "Vet Name (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Search for a specific visit using its unique Case ID.",
                        "name": "Case ID (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter by the targeted bodily system or department (Options: general, ear, emergency, eye).",
                        "name": "System (Drop-Down with Checkboxes)",
                        "helper": ""
                      }
                    ],
                    "title": "Search Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Condition Status, Condition, Date.",
                        "name": "Filter by (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the selected filter parameters to the history list.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filter Option"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to instantly clear all entered search fields and applied filters, resetting the history view.",
                        "name": "Reset (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Reset"
                  },
                  {
                    "type": "text",
                    "content": "The history module displays past records date-wise in an organized card layout.\nCard Display Fields: Each individual historical card provides a summary of the visit."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the overarching month and year of the record (e.g., Aug 2026).",
                        "name": "Month & Year (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the unique identifier for that specific visit (e.g., Case ID - 1452).",
                        "name": "Case ID (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the consultation mode or medium (e.g., tag: Physical).",
                        "name": "Consultation Tag (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the visit type and the targeted medical system (e.g., Consultation | General).",
                        "name": "Consultation & System (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific day of the consultation (e.g., 31 Aug 2026).",
                        "name": "Exact Date (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the time of the consultation (e.g., 11:56).",
                        "name": "Exact Time (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the attending veterinarian (e.g., Dr. Luke Dunphy).",
                        "name": "Vet Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": " Within the record layout, a structured table displays the specific procedures performed during that visit",
                        "name": "Procedures Table",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific name of the procedure.",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the classification or category of the procedure.",
                        "name": "Category (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to generate and print a formalized physical copy of that specific historical medical record.",
                        "name": "Print (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "History Record"
                  }
                ],
                "summary": "The History tab acts as a comprehensive, chronological archive storing all past and current medical records, consultations, and procedures for the pet. It provides advanced search and filtering tools to quickly locate specific past visits.",
                "workflow": "Patient Module  → History"
              }
            ],
            "title": "History",
            "description": "The History tab acts as a comprehensive, chronological archive storing all past and current medical records, consultations, and procedures for the pet. It provides advanced search and filtering tools to quickly locate specific past visits."
          }
        ],
        "description": "The History tab acts as a comprehensive, chronological archive storing all past and current medical records, consultations, and procedures for the pet. It provides advanced search and filtering tools to quickly locate specific past visits."
      },
      {
        "id": "topic-Diagnostics",
        "title": "Diagnostics",
        "groups": [
          {
            "id": "group-Diagnostics",
            "items": [
              {
                "id": "item-Diagnostics",
                "title": "Diagnostics",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Diagnostics"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/patient-diagnostics-video.mp4",
                    "alt": "Diagnostics - Adding a New Test Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "The Diagnostics module within the Patient Workbook allows veterinarians to seamlessly order, track, and record results for laboratory and radiology tests directly on a patient's medical record."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "To order a new diagnostic test or record an external one, click the + Add button. This opens the \"Add\" configuration window.",
                        "name": "+ Add (button)",
                        "helper": ""
                      }
                    ],
                    "title": "+ Add"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Choose the category of the diagnostic (Options: Lab, Radiology).",
                        "name": "Select Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific diagnostic test.",
                        "name": "Test Name (Drop-Down)*",
                        "helper": ""
                      },
                      {
                        "desc": "Select the specific variant of the test.\n",
                        "name": "Variant (Drop-Down)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the quantity to be billed to the patient.",
                        "name": "Billable Qty (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the date the test results are required.\nDone At (Drop-Down) : Indicate where the test is being processed (Options: Inhouse, External).\nProcessing Time (Display Field) : Shows the estimated time required to process the test.",
                        "name": "Due Date (Calendar Input)*",
                        "helper": ""
                      },
                      {
                        "desc": "Indicate where the test is being processed (Options: Inhouse, External).",
                        "name": "Done At (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the estimated time required to process the test.",
                        "name": "Processing Time (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Test Details"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Check this box if the physical sample has already been collected or the scan has already been performed.\n",
                        "name": "Study Performed (Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Checking this box reveals two additional fields: Collection Time (Time Field) and Performed by (Drop-Down).",
                        "name": "Dynamic Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Select the urgency of the test (Options: Routine, Urgent, Stat).",
                        "name": "Priority (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter any additional notes or clinical context for the lab technician.",
                        "name": "Remarks (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the configuration and add the diagnostic test to the patient's workbook.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Execution & Routing"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the name of the test.",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the category (e.g., Lab or Radiology).",
                        "name": "Type (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the billed quantity.",
                        "name": "Billable Qty (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the required sample type or imaging modality.",
                        "name": "Sample/Modality (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the current stage of the diagnostic lifecycle.",
                        "name": "Status (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Available by default to remove the test. Note: Other action buttons appear dynamically based on the test's current status (detailed below).",
                        "name": "Delete (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Diagnostics Table"
                  },
                  {
                    "type": "text",
                    "content": "The diagnostics module tracks tests through various stages of completion. As the Status of a test changes, the available action buttons in the table automatically update to match the workflow."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "The diagnostics module tracks tests through various stages of completion. As the Status of a test changes, the available action buttons in the table automatically update to match the workflow.",
                        "name": "Status: Scheduled",
                        "helper": ""
                      },
                      {
                        "desc": "Click to reopen the setup form and check the \"Study Performed\" box once the sample collection or scan is complete. Doing this moves the test to \"In Progress\".",
                        "name": "Study Performed (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Status: Scheduled"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "This status indicates the sample has been collected and the test is actively being processed.",
                        "name": "Status: In Progress",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open the results entry window.",
                        "name": "Enter Results (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the Test Name, Type, Variant, and Performed By.",
                        "name": "Header (Display Fields)",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the medical results directly into the standardized test template, or use the provided File Upload option to attach external lab reports.",
                        "name": "Result Input (Interactive Area)",
                        "helper": ""
                      },
                      {
                        "desc": "Indicate the finality of the data (Options: Preliminary, Final).",
                        "name": "Result Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the next routing stage for the test (Options: Send for clinical review, Pending patient review, Completed).",
                        "name": "Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to log the results and update the main status of the diagnostic",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Status: In Progress"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "If the test was routed for review during the \"Enter Results\" step, the table status will update to match.",
                        "name": "Status: Send for Clinical Review / Pending Patient Review",
                        "helper": ""
                      },
                      {
                        "desc": "Appears during the review stages, allowing authorized staff to reopen the entry window and edit the recorded results before finalizing them.",
                        "name": "Modify Results (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Status: Send for Clinical Review / Pending Patient Review"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Once the results are finalized and the status is changed to \"Completed\", the diagnostic workflow for that test is finished.",
                        "name": "Status: Completed",
                        "helper": ""
                      },
                      {
                        "desc": "Appears in the table, allowing you to generate and print a physical copy of the finalized diagnostic results.",
                        "name": "Print Report (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Status: Completed"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Diagnostics",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Right-Side-panel",
        "title": "Right Side panel",
        "groups": [
          {
            "id": "group--Right-Side-panel",
            "items": [
              {
                "id": "item-Right-Side-panel",
                "title": "Right Side panel",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Patient Workbook → Right Side Panel"
                  },
                  {
                    "type": "text",
                    "content": "The Right Side Panel within the Patient Workbook provides a persistent, quick-access menu to essential clinical tools, legal documents, and patient records during a consultation. It is organized into three primary menu options: Clinic Flow, Forms, and Records."
                  },
                  {
                    "type": "text",
                    "content": "The \"Clinic Flow\" menu is the primary working space and opens by default when accessing the right side panel. It houses the core medical tracking modules required during a standard patient visit."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Access and record the patient's current health metrics.\n",
                        "name": "Vitals",
                        "helper": ""
                      },
                      {
                        "desc": "Enter Subjective, Objective, Assessment, and Plan notes for the consultation.",
                        "name": "SOAP Notes",
                        "helper": ""
                      },
                      {
                        "desc": "Order tests and review lab or radiology results.",
                        "name": "Diagnostics",
                        "helper": ""
                      },
                      {
                        "desc": "Log and manage medical procedures or services performed on the patient.",
                        "name": "Procedures",
                        "helper": ""
                      }
                    ],
                    "title": "Clinic Flow (Default View)"
                  },
                  {
                    "type": "text",
                    "content": "The \"Forms\" menu allows staff to manage, attach, and execute standardized clinical and legal documents for the patient. It contains two sub-modules: Consent Form and Protocol Form.\nBoth sub-modules share an identical layout and workflow."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to open a search window, allowing you to locate and attach a specific consent or protocol form to the patient's current workbook.",
                        "name": "+ Add (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Once a form is added, it populates in a structured table with the following details:\n\n\nName (Display Field) : Shows the specific title of the attached form.\n\n\nStatus (Display Field) : Displays the current completion status of the document (e.g., Pending, Signed).",
                        "name": "Forms Table Layout",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open the dedicated signature window, allowing the pet parent or veterinarian to legally sign the document directly on the screen.",
                        "name": "Sign Now (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open and read the full text of the form.",
                        "name": "View (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to remove the un-signed form from the patient's workbook.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Forms - Adding a Form"
                  },
                  {
                    "type": "text",
                    "content": "The \"Records\" menu serves as a localized storage area for visual and uploaded medical files specific to the current consultation. It contains two sub-modules: Body Map and Files.\nBoth sub-modules share an identical layout and workflow"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to upload a new file, image, or generate a new body map diagram for the patient's record.",
                        "name": "+ Add (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Once a record or file is added, it populates in a structured table with the following details:\n\n\nName (Display Field) : Shows the file name or the title of the body map.\n\n\nDate (Display Field) : Shows the exact date and time the record was uploaded or created.",
                        "name": "Records Table Layout",
                        "helper": ""
                      },
                      {
                        "desc": "Click to permanently remove the file or body map from the patient's records.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Records - Adding a Record"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Right Side panel",
            "description": ""
          }
        ],
        "description": ""
      }
    ],
    "description": "A comprehensive record of the patient’s care, including vitals, SOAP notes, diagnostics, procedures, clinical flow, forms, and records."
  },
  {
    "id": "module-Appointments",
    "title": "Appointments",
    "topics": [
      {
        "id": "topic-Appointments",
        "title": "Appointments Overview",
        "groups": [
          {
            "id": "group-Appointments-Overview",
            "items": [
              {
                "id": "item-Appointments-Overview",
                "title": "Appointments Module Overview",
                "details": [
                  {
                    "type": "text",
                    "content": "The Appointments module manages scheduling and clinic availability, containing three distinct sub-modules: Calendar, Requests, and Facility management."
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Appointments Overview",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Calendar",
        "title": "Calendar",
        "groups": [
          {
            "id": "group-Calendar",
            "items": [
              {
                "id": "item-Calendar",
                "title": "Calendar",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Appointments → Calendar"
                  },
                  {
                    "type": "text",
                    "content": "The Calendar sub-module opens by default when the Appointments tab is clicked. It provides a comprehensive scheduling interface, displaying appointments as a functional matrix comparing time slots against either specific veterinarians or clinic facilities."
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-calendar-main.png",
                    "alt": "Appointments Calendar Main View"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Switch the calendar layout between a single-day view and a full-week view.",
                        "name": "Day / Week (Toggle)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to instantly return the calendar matrix to the current day.",
                        "name": "Today (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Top-Left Controls"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "The center displays the currently viewed day and date. Use the left and right arrows to navigate to previous or future days and weeks.",
                        "name": "Date Navigation (Display & Arrows)",
                        "helper": ""
                      }
                    ],
                    "title": "Center Controls"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Switch the horizontal axis of the matrix to organize the schedule by individual veterinarians or by specific clinic facilities.",
                        "name": "Vet view / Facility view (Toggle)",
                        "helper": ""
                      },
                      {
                        "desc": "Switch the overall visual layout between the standard visual matrix (Grid) or a structured list format (Table).",
                        "name": "Grid view / Table view (Toggle)",
                        "helper": ""
                      }
                    ],
                    "title": "Top-Right Controls"
                  },
                  {
                    "type": "heading",
                    "title": "Grid View"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-calendar-grid-view.png",
                    "alt": "Calendar Grid View"
                  },
                  {
                    "type": "heading",
                    "title": "Table View"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-calendar-table-view.png",
                    "alt": "Calendar Table View"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Clicking the Filter button opens a dedicated window allowing you to heavily customize which appointments and slots appear on the calendar.",
                        "name": "Filter (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Search & Filter Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Check to display summary statistic cards on the calendar interface.",
                        "name": "Show stats cards (Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Check to highlight available time slots for booking.",
                        "name": "Show availability (Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the time interval increments for the grid (Options: 60 min, 30 min, 15 min).",
                        "name": "Slot Size (Options)",
                        "helper": ""
                      }
                    ],
                    "title": "Display Settings"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the consultation modes to display (Options: Physical, Virtual, Home Visit).",
                        "name": "Appointment Type (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter by specific medical or grooming services (Options: All, or select specific listed services).",
                        "name": "Service Type (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter the schedule by staff roles (Options: Vet, Groomers, Lab/Radiology tech).",
                        "name": "Roles (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter to show only specific staff members (Options: All, or select specific listed names).",
                        "name": "Vets/Staffs (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter by the patient's current journey stage (Options: All, Upcoming, Cancelled, Billing, etc.).",
                        "name": "Appointment Status (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to clear all selected filters and return the calendar to its default state.",
                        "name": "Reset All (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to update the calendar view based on your selected configurations.",
                        "name": "Apply filters (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filtering Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Hovering over any empty time slot in the matrix displays a \"Click to Book\" prompt. Clicking the slot automatically opens the \"Book an Appointment\" window, pre-filled for that specific time and vet/facility.",
                        "name": "Blank Slot (Interactive Grid Area)",
                        "helper": ""
                      },
                      {
                        "desc": "Scheduled appointments are displayed as summary cards within their respective time and vet/facility slots on the grid.",
                        "name": "Viewing Booked Appointments",
                        "helper": ""
                      },
                      {
                        "desc": "Each card summarizes the visit, showing the Patient Name, Owner Name, Facility Name, Consultation Mode, Appointment Status, and Check-in Time.",
                        "name": "Card Display Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Located on every appointment card. Clicking this reveals a quick-action context menu with options to manage the appointment, such as Follow up, Cancel, or Reassign.",
                        "name": "3-Dot Menu (Action Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Booking & Managing Appointments (Grid View)"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Calendar",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Facilities",
        "title": "Facilities",
        "groups": [
          {
            "id": "group-Facilities",
            "items": [
              {
                "id": "item-Facilities",
                "title": "Facilities",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Appointments → Facilities"
                  },
                  {
                    "type": "text",
                    "content": "The Facilities module enables administrators to create, configure, and manage specific operational spaces (such as consultation rooms, kennels, or surgical suites) within the clinic."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for a specific facility by its exact name.",
                        "name": "Search Field (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter the table to display only specific types of facilities. Select a facility type from the list to instantly apply the filter.",
                        "name": "Facility Type Filter (Drop-Down)",
                        "helper": ""
                      }
                    ],
                    "title": "Search & Filter Options"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to set up a brand-new facility. This opens the \"Create\" window with the configuration fields (* indicates a mandatory field).",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/appointments-create-facility.png",
                    "alt": "Create Facility Window"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the overarching classification of the space (Options include: Consultation, Kennel, etc.).",
                        "name": "Facility Type (Drop-Down)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the specific name, number, or identifier for the facility (e.g., Exam Room 1).",
                        "name": "Name (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Define the inventory and stock permissions associated with this specific space (Options: All permitted storage, By Category, Selected Locations).",
                        "name": "Inventory Access (Radio Buttons)*",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the configuration and officially create the new facility in the system.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Facility Window"
                  },
                  {
                    "type": "text",
                    "content": "The main dashboard displays all created facilities in a structured, organized table. (Note: All column headers in this table are fully sortable, allowing you to organize the data in ascending or descending order.)"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the sequential serial number of the entry.",
                        "name": "Sr. No. (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the designated name of the facility.",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the classification category of the facility.",
                        "name": "Type (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the staff member who created the entry.",
                        "name": "Created By (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the exact date the facility was added to the system.",
                        "name": "Created Date (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "An interactive toggle switch present on each row, allowing you to quickly change the operational status of that specific facility (Options: Active, Under maintenance).",
                        "name": "Status (Toggle)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to open the configuration window and modify the facility's existing details.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to permanently remove the facility from the system.",
                        "name": "Delete (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Table Layout"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Facilities",
            "description": ""
          }
        ],
        "description": ""
      }
    ],
    "description": ""
  },
  {
    "id": "billing",
    "title": "Billing Module",
    "topics": [
      {
        "id": "billing-dashboard-topic",
        "title": "Billing Dashboard",
        "groups": [
          {
            "id": "billing-dashboard-group",
            "items": [
              {
                "id": "billing-dashboard-overview",
                "title": "Billing Dashboard",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_overview.png",
                    "alt": "Billing"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_dashboard.png",
                    "alt": "Billing Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Billing module provides comprehensive access to all financial functionalities, including the dashboard, payments, returns, master settings, and downloadable reports. The Billing Dashboard itself serves as a centralized financial overview, featuring summary cards, quick-action buttons, a recent transactions table, and a visual collection summary graph."
                  }
                ],
                "summary": "The Billing module provides comprehensive access to all financial functionalities, including the dashboard, payments, returns, master settings, and downloadable reports.",
                "workflow": ""
              },
              {
                "id": "billing-summary-cards",
                "title": "Summary Cards",
                "details": [
                  {
                    "type": "text",
                    "content": "The top of the dashboard displays three cards providing a quick snapshot of the clinic's current financial status:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows today's total collection amount, a comparison percentage versus yesterday (e.g., -100% vs yesterday), and \"Today's Refund\".",
                        "name": "Today's Collection (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click the \"View today’s transactions\" shortcut to jump directly to the payments module. \n\n\nThe \"Payment Date\" filter for the current day is automatically applied.",
                        "name": "View today’s transactions (Action Shortcut)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the total outstanding balance and the number of invoices it spans (e.g., across 296 invoices).",
                        "name": "Outstanding Balance (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click the \"View all unpaid\" shortcut to jump to the closed section of the payments module. \n\n\nThe \"Unpaid\" and \"Partially Paid\" status filters are automatically applied.",
                        "name": "View all unpaid (Action Shortcut)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the total count of draft/incomplete transactions, as well as the count of completed transactions (e.g., Completed transactions: 612).",
                        "name": "Draft Bills (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Summary Cards"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to open the \"Create Bill\" window and initiate a new invoice.",
                        "name": "Create (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to navigate directly to the \"Open\" section of the Billing module to review and finalize pending bills.",
                        "name": "Finalize Bill (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Dashboard Action Buttons"
                  }
                ],
                "summary": "Today's Collection, Outstanding Balance, Draft Bills cards, and Create Bill modal."
              },
              {
                "id": "creating-bill",
                "title": "Creating a Bill",
                "details": [
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/billing-create-bill-video.mp4",
                    "alt": "Creating a Bill Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "Click the \"Create\" button on the dashboard that opens a window to generate a new transaction."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Clicking the \"Create\" button on the dashboard opens a window to generate a new transaction",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the department generating the bill (Options: Clinic, Pharmacy, Petshop).",
                        "name": "Bill as (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the entity type to search for (Options: Customer, Pet).",
                        "name": "Search Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Search for an existing customer by their name, email, or phone number, or search for a pet by its name.",
                        "name": "Search Field (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click this shortcut to open a secondary window to register a brand-new customer (see details below).",
                        "name": "Add new customer (Clickable Text)",
                        "helper": ""
                      },
                      {
                        "desc": "Check this box if you are creating a bill for a past date. \n\n\nNote: If selected, a Date calendar field dynamically appears allowing you to select the retroactive date.",
                        "name": "Back dated (Checkbox)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Bill (fields)"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to generate the bill. \n\n\nA \"Bill created successfully!\" pop-up will appear, and the system will automatically redirect you to the newly created bill inside the \"Point of Sales\" module.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Submit"
                  },
                  {
                    "type": "text",
                    "content": "If the \"Add new customer\" shortcut is clicked during bill creation, a smaller window appears with the following fields (* indicates a mandatory field):"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the pet owner's full name.",
                        "name": "Parent Name (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the owner's primary phone number.",
                        "name": "Contact Number (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter the owner's email address.",
                        "name": "Email (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to register the new customer and return to the bill creation process.",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Add New Customer (Sub-Window)"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "finalizing-bill",
                "title": "Finalizing a Bill",
                "details": [
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/billing-finalize-bill-video.mp4",
                    "alt": "Finalize Bill Workflow Video Guide"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to navigate directly to the \"Open\" section of the Billing module to review and finalize pending bills.",
                        "name": "Finalize Bill (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Finalize Bill"
                  }
                ],
                "summary": "Navigate directly to review and finalize pending bills.",
                "workflow": "Billing → Open Bills → Finalize Bill"
              },
              {
                "id": "billing-collection-summary",
                "title": "Recent Transactions & Collection Summary Graph",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays top 5 most recent transactions: customer name, pet name, invoice number, total amount, status tag (Paid, Unpaid, Partially Paid).",
                        "name": "Transaction Details (Display Fields)"
                      },
                      {
                        "desc": "Click to navigate directly to closed section of payments module to view full history.",
                        "name": "View All (Clickable Shortcut)"
                      }
                    ],
                    "title": "Recent Transactions Table"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Filter the graph by revenue source \n\n\n(Options: All, Clinic, Pet shop, Pharmacy).",
                        "name": "Source Filter (Drop-Down with Checkboxes)"
                      },
                      {
                        "desc": "Filter the graph by a specific timeframe \n\n\n(Options: Today, This week, This month, This year).\n",
                        "name": "Time Filter (Drop-Down)"
                      },
                      {
                        "desc": "Vertical Axis: Monetary amount range. \n\n\nHorizontal Axis: Payment methods (Cash, UPI, Card). \n\n\nHover State: Tooltip window showing specific payment method and exact total amount collected.",
                        "name": "Graph Display Details"
                      }
                    ],
                    "title": "Collection Summary (Graph)"
                  }
                ],
                "summary": "Top 5 recent transactions table and visual revenue graph with dynamic filters."
              }
            ],
            "title": "Financial Summary & Bill Creation",
            "description": "Dashboard widgets, bill creation, and revenue analytics."
          }
        ],
        "description": "Central financial overview featuring summary cards, action buttons, recent transactions, and revenue graph."
      },
      {
        "id": "pos-topic",
        "title": "Point of Sale (POS)",
        "groups": [
          {
            "id": "pos-group",
            "items": [
              {
                "id": "pos-overview",
                "title": "Point of Sale (POS) Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Payments → Open → View(Button)"
                  },
                  {
                    "type": "text",
                    "content": "Accessed by clicking \"View\" on an open bill, this module displays the comprehensive details of the selected transaction and allows you to modify, process, or finalize it."
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_payments_open_view.png",
                    "alt": "Payments – Open / View"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "pos-checkout-flow",
                "title": "POS Header, Items Table & Apply Discount",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the exact date and time the bill was created.",
                        "name": "Date/Time (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the originating location (e.g., Pharmacy). Includes a \"Switch\" button to toggle viewing bills from different locations.",
                        "name": "Type POS (Display Field & Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows any outstanding credit amounts, the location, and the payable amount.",
                        "name": "Unpaid Credits (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the customer/owner's name.",
                        "name": "Parent Name (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Header Information"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Service/Item Name, Qty (Quantity), Unit Price, Unit Discount, Tax, Item Total.",
                        "name": "Table Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Present on each row to modify or remove specific line items.",
                        "name": "Edit / Delete (Buttons)",
                        "helper": ""
                      }
                    ],
                    "title": "Items Table"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Opens a window to add new items or services to the current bill.",
                        "name": "Add (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens the \"Apply Discount\" window. You can select a Discount Type via radio buttons (Options: Code, Percentage, Price) and click Add to apply it to the bill.",
                        "name": "Apply Discount (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Assign or change the Vet Name and Billed By staff member.",
                        "name": "Drop-Downs",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the financial breakdown including Discount, Subtotal, Taxes (CGST, SGST), Round Off, and the final Total.",
                        "name": "Summary Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Provides an audit trail at the bottom showing Date and Time, Action, Changes, and Performed by.",
                        "name": "Change Log (Display Table)",
                        "helper": ""
                      }
                    ],
                    "title": "Invoice Details & Summary"
                  }
                ],
                "summary": "Header information, line items table, line discounts/taxes, apply discount modal, and change log."
              },
              {
                "id": "pos-pay-actions",
                "title": "Primary POS Action Buttons & Pay Window",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to delete draft bill. Confirmation prompt (\"Are you sure you want to discard this record?\") appears with Confirm/Cancel options.",
                        "name": "Discard (Button)"
                      },
                      {
                        "desc": "Click to save current progress without finalizing.",
                        "name": "Save as draft (Button)"
                      },
                      {
                        "desc": "Click to lock the invoice for payment.",
                        "name": "Finalize bill (Button)"
                      },
                      {
                        "desc": "Opens preview window with checkbox option to print as \"dot matrix monospace,\" followed by final Print button.",
                        "name": "Print (Button)"
                      },
                      {
                        "desc": "Opens payment processing window.",
                        "name": "Pay (Button)"
                      }
                    ],
                    "title": "Primary Action Buttons"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays Pet Name, Parent Name, today's date.",
                        "name": "Summary Card"
                      },
                      {
                        "desc": "Select specific invoice to pay.",
                        "name": "Invoice Number (Drop-Down with Checkbox)"
                      },
                      {
                        "desc": "Shows invoice location, ID, total amount. Amount field defaults to total but is editable for partial payments.",
                        "name": "Location & Amount (Display/Input Fields)"
                      },
                      {
                        "desc": "Select payment method (e.g., Cash, Card, UPI).",
                        "name": "Payment Mode (Drop-Down)"
                      },
                      {
                        "desc": "Defaults to today's date.",
                        "name": "Collection Date (Calendar Field)"
                      },
                      {
                        "desc": "Shows existing credits customer can apply.",
                        "name": "Available Credit (Display Field)"
                      },
                      {
                        "desc": "Click to process transaction.",
                        "name": "Pay (Button)"
                      }
                    ],
                    "title": "\"Pay Window\" Details"
                  }
                ],
                "summary": "Discard, Save draft, Finalize bill, Print, and Pay processing window."
              }
            ],
            "title": "POS Checkout & Pay Window",
            "description": "Header details, items table, discounts, audit trail, and payment modal."
          }
        ],
        "description": "Modify, process, apply discounts, finalize, and process payment for open bills."
      },
      {
        "id": "payments-topic",
        "title": "Payments Module",
        "groups": [
          {
            "id": "payments-group",
            "items": [
              {
                "id": "payments-overview",
                "title": "Payments Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Payments"
                  },
                  {
                    "type": "text",
                    "content": "The Payments module is the central hub for managing and tracking financial transactions. It is organized into three distinct sub-modules: Open, Closed, and Collections, allowing for precise tracking of pending, finalized, and processed payments."
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_payments.png",
                    "alt": "Payments"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "open-bills",
                "title": "Open Bills",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Payments → Open"
                  },
                  {
                    "type": "text",
                    "content": "The \"Open\" sub-module lists all current, pending, or draft bills that require finalization or payment."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search by the pet's name.",
                        "name": "Pet Name (Input Field)"
                      },
                      {
                        "desc": "Search by the pet owner's name.",
                        "name": "Customer Name (Input Field)"
                      },
                      {
                        "desc": "Search by the attending veterinarian.",
                        "name": "Vet Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter by the department originating the bill (Options: All, Clinic, Petshop, Pharmacy).",
                        "name": "Source (Drop-Down with Checkboxes)",
                        "helper": ""
                      }
                    ],
                    "title": "Search Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Transaction Date, Invoice Number, Invoice Type, Payment Mode, Total Due, Balance Due.",
                        "name": "Filter by (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the selected filter parameters.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filter Option"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to instantly clear all entered search fields and applied filters.",
                        "name": "Reset (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Reset"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Pet Name, Vet Name, Total Due, Balance Due, Source, Transaction Date.",
                        "name": "Table Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Present against each record. Clicking this opens the Point of Sale module for that specific billing record.",
                        "name": "View (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Open Bills Table"
                  }
                ],
                "summary": "Filter, search, view, process payment, cancel, return, or reopen invoices."
              },
              {
                "id": "closed-bills",
                "title": "Closed Bills",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Payments → Closed"
                  },
                  {
                    "type": "text",
                    "content": "The \"Closed\" sub-module acts as an archive for all finalized and processed invoices."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search by the pet's name.",
                        "name": "Pet Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Search by the pet owner's name.",
                        "name": "Customer Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter by the department originating the bill (Options: All, Clinic, Petshop, Pharmacy).",
                        "name": "Source (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Options: All, Clinic, Petshop, Pharmacy",
                        "name": "Status (Drop-Down with Checkbox)",
                        "helper": ""
                      }
                    ],
                    "title": "Search Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Transaction Date, Invoice Number, Invoice Type, Payment Mode, Total Due, Balance Due, Person In Charge. ",
                        "name": "Filter by (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to apply the selected filter parameters.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filter Option"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to instantly clear all entered search fields and applied filters.",
                        "name": "Reset (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Reset"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Table Layout Preceded by a total count (e.g., Number of Records: 629).",
                        "name": "Total count",
                        "helper": ""
                      },
                      {
                        "desc": "Pet Name, Vet Name, Total Due, Balance Due, Source, Status, Transaction Date.",
                        "name": "Table Fields",
                        "helper": ""
                      }
                    ],
                    "title": "Closed Bills Table"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_closed_bills.png",
                    "alt": "Closed Bills"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Opens the Point of Sale module for that specific record in a read-only or payment-processing state.",
                        "name": "View (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens the standard Pay window (detailed in the POS section).",
                        "name": "Pay (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens a cancellation window requiring Person In charge (Drop-Down), Cancel Reason (Drop-Down), and Remarks (Input Field) before confirming or canceling.",
                        "name": "Cancel (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens the return window displaying Return Type (Sales), Customer Name, and a Select Invoice (Drop-Down), followed by a Next button.",
                        "name": "Return (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens a confirmation window (\"Are you sure you want to Reopen the bill?\") with Yes/Cancel options, moving the bill back to the Open status.",
                        "name": "Reopen (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Record Action Buttons"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_cancel_bill.png",
                    "alt": "Cancel Bill"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_reopen_bill.png",
                    "alt": "Reopen Bill"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "collections-module",
                "title": "Collections",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Payments → Collections"
                  },
                  {
                    "type": "text",
                    "content": "The \"Collections\" sub-module provides a ledger of all actual payments received, organized by individual receipts."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the client's name to locate their specific billing or service records.",
                        "name": "Customer Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Type the patient's name to quickly pull up transactions tied to that specific animal.",
                        "name": "Pet Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the origin point of the transaction (e.g., front desk, online portal) to narrow down results.",
                        "name": "Source (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": " Filter the list to show only records in a specific state, such as completed, pending, or overdue.",
                        "name": "Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter transactions based on how they were paid, like cash, credit card, or insurance.",
                        "name": "Payment Mode (Drop-Down)",
                        "helper": ""
                      }
                    ],
                    "title": "Search Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Filter the view by the specific transaction category (Options include: All, Payment collection, Credit collection, Credit usage, or Refund).",
                        "name": "Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Invoice Number, Invoice Date, Payment Date, Amount Paid, Total Due, Balance Due, Discount, Taxes.",
                        "name": "Filter Window (Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Click this to formally apply all your selected checkbox criteria to the active data view.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Filter Options"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Instantly clears all active search parameters, dropdown selections, and checkbox filters to return the table to its default, unfiltered state.",
                        "name": "Reset (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens a window to generate financial reports. Select the Report Type and Date Range via radio buttons, then click Download.",
                        "name": "Download (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Reset"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Table Layout Preceded by a total count (e.g., Number of Records: 661).",
                        "name": " Total count",
                        "helper": ""
                      },
                      {
                        "desc": "Invoice No., Receipt No., Customer Name, Payment Date, Source, Amount Paid, Payment Mode.",
                        "name": "Table Fields",
                        "helper": ""
                      },
                      {
                        "desc": "Edit (to modify the receipt) and Print (to generate a physical copy).",
                        "name": "Record Action Buttons",
                        "helper": ""
                      }
                    ],
                    "title": "Collections Table"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_collections.png",
                    "alt": "Collections"
                  }
                ],
                "summary": "Ledger of actual payments received, organized by receipts."
              }
            ],
            "title": "Payment Tracking Sub-Modules",
            "description": "Open, Closed, and Collections tracking."
          }
        ],
        "description": "Manage Open Bills, Closed Bills, and Collections ledgers."
      },
      {
        "id": "Returns",
        "title": "Returns",
        "groups": [
          {
            "id": "Returns-group",
            "items": [
              {
                "id": "returns-overview",
                "title": "Returns",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Returns"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_returns.png",
                    "alt": "Returns"
                  },
                  {
                    "type": "text",
                    "content": "The Returns module provides a centralized ledger of all processed return transactions, allowing staff to track, search, and initiate new returns for sales or purchases."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": " Search for specific return records across the database.",
                        "name": "Search Field (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Filter the displayed list of returns by their specific transaction type (Options: Sales, Purchase).",
                        "name": "Sort by Type (Drop-Down)",
                        "helper": ""
                      }
                    ],
                    "title": "Search & Filter Options"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to initiate a new return process. This opens a dedicated setup window with the following fields:",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select the classification of the return (e.g., Return of sales).",
                        "name": "Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Search for the specific customer associated with the return.",
                        "name": "Search Field (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to confirm the details and generate the return record.",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Return Window"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_create_return.png",
                    "alt": "Create Return"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the date the return was processed.",
                        "name": "Date (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the invoice number. Action: Clicking on this specific field directly opens the associated invoice credit note for detailed review.",
                        "name": "Invoice (Clickable Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the unique reference tracking number for the return.",
                        "name": "Reference No. (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the customer who initiated the return.",
                        "name": "Customer (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the total monetary amount being returned or credited.",
                        "name": "Return Total (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the current processing status of the return.",
                        "name": "Status (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Collections Table"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Returns-Overview",
            "description": "The Returns module provides a centralized ledger of all processed return transactions, allowing staff to track, search, and initiate new returns for sales or purchases."
          }
        ],
        "description": "The Returns module provides a centralized ledger of all processed return transactions, allowing staff to track, search, and initiate new returns for sales or purchases."
      },
      {
        "id": "topic-Master",
        "title": "Master",
        "groups": [
          {
            "id": "Master-Overview-group",
            "items": [
              {
                "id": "Master-overview",
                "title": "Master Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Master (Drop-Down) → Payment Method / Discount"
                  },
                  {
                    "type": "text",
                    "content": "The Master module acts as a core configuration hub, accessible via a drop-down menu, allowing administrators to define and manage system-wide billing settings. It contains two primary sub-modules: Payment Method and Discount."
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Master Overview",
            "description": "It contains two primary sub-modules: Payment Method and Discount."
          },
          {
            "id": "Payment-method-group",
            "items": [
              {
                "id": "Payment-method",
                "title": "Payment Method",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Master (Drop-Down) → Payment Method"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module allows you to view, manage, and add the accepted modes of payment across different departments of the software."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the sequential serial number of the entry.",
                        "name": "S.No (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the payment type (e.g., Cash, UPI, Credit Card).",
                        "name": "Payment Method (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows whether the payment method is currently active or inactive.",
                        "name": "Status (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the name of the user who created or last modified the entry.",
                        "name": "User Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the date the payment method was added to the system.",
                        "name": "Created Date (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to modify the existing payment method's details or status.",
                        "name": "Edit (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to remove the payment method from the system.",
                        "name": "Delete (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Payment Method Table"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to configure and add a brand-new payment mode. This opens the \"Create Payment Method\" window.",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  }
                ],
                "summary": "This sub-module allows you to view, manage, and add the accepted modes of payment across different departments of the software.",
                "workflow": ""
              },
              {
                "id": "creating-payment-method",
                "title": "Creating a Payment Method",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the specific name of the new payment mode.",
                        "name": "Payment Method (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select which departments are authorized to use this payment method (Options: Clinic, Petshop, Pharmacy).",
                        "name": "Type (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Set the operational status of the payment method (Options: Active, Inactive).",
                        "name": "Status (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save and officially add the new payment method to the system.",
                        "name": "Add (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Payment Method Window Fields"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Payment method",
            "description": "This sub-module allows you to view, manage, and add the accepted modes of payment across different departments of the software."
          },
          {
            "id": "group-discount",
            "items": [
              {
                "id": "item-Discount",
                "title": "Discount",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Master (Drop-Down) → PDiscount"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module provides a centralized view of all promotional codes and discounts created within the system, allowing for the creation of new discount rules."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Shows the sequential serial number of the entry.",
                        "name": "S.No (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the classification of the discount (e.g., Percentage, Flat Rate).",
                        "name": "Discount Type (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the specific alphanumeric code used to apply the discount during checkout.",
                        "name": "Discount Code (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the active date range or expiration date of the discount.",
                        "name": "Validity (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the staff member who generated the discount.",
                        "name": "Created by (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the exact date the discount was created.",
                        "name": "Created Date (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Contains options to manage the specific discount entry.",
                        "name": "Action (Display Field/Buttons)",
                        "helper": ""
                      }
                    ],
                    "title": "Discounts Table"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to establish a new discount rule. This opens a dedicated setup window categorized by headers and input fields.",
                        "name": "Add discount (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Add discount "
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "create-discount",
                "title": "Creating a Discount",
                "details": [
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Fields to define how the discount is applied (e.g., code-based, automatic price adjustment).",
                        "name": "Discount Method (Configuration Section)",
                        "helper": ""
                      },
                      {
                        "desc": "Fields to set financial thresholds that must be met before the discount becomes valid.",
                        "name": "Minimum purchase requirements (Configuration Section)",
                        "helper": ""
                      },
                      {
                        "desc": "Fields to define which specific customers, patient types, or departments can utilize the discount.",
                        "name": "Eligibility (Configuration Section)",
                        "helper": ""
                      },
                      {
                        "desc": "Fields to set limits, such as maximum uses per customer or total global uses.",
                        "name": "Usage Restrictions (Configuration Section)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize the parameters and activate the newly created discount in the system.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Add Discount Window"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Discount",
            "description": ""
          }
        ],
        "description": "It contains two primary sub-modules: Payment Method and Discount."
      },
      {
        "id": "topic-Download-Reports",
        "title": "Download Reports",
        "groups": [
          {
            "id": "group-Download-Reports",
            "items": [
              {
                "id": "item-Download-Reports-overview",
                "title": "Download Reports Overview",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Billing Module → Download Reports"
                  },
                  {
                    "type": "text",
                    "content": "The Download Reports module allows administrators and authorized staff to generate, export, and download comprehensive financial, operational, and medical compliance data. All reports are extracted and saved locally as formatted Excel files for external review, accounting, and regulatory record-keeping."
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/billing_download_reports.png",
                    "alt": "Download Reports"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-downloading-reports",
                "title": "Downloading a Report",
                "details": [
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Generates a detailed list of all outstanding payments, unpaid invoices, and total dues owed by customers.",
                        "name": "Accounts Receivables Report (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Extracts a comprehensive log of all invoices and bills generated across the system within a specified timeframe.",
                        "name": "Bills Report (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Provides a high-level overview of total collections, revenues, discounts, and overall financial health across all clinic departments.",
                        "name": "Consolidated Financial Summary (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Generates a daily ledger tracking all day-to-day financial transactions, including receipts, payments, and refunds.",
                        "name": "Day Book (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Extracts a detailed summary of Goods and Services Tax (GST) collected on services and products, structured for tax filing and financial compliance.",
                        "name": "GST Report (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Generates a report categorizing sales and associated taxes based on the Harmonized System of Nomenclature (HSN) codes for inventory and billing compliance.",
                        "name": "HSN Summary (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Provides a mandatory regulatory tracking log detailing the dispensing, usage, and stock of Narcotic Drugs and Psychotropic Substances (NDPS).",
                        "name": "NDPS Drug Report (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Generates a compliance log tracking the prescription, sale, and patient details for regulated Schedule H1 prescription medications.",
                        "name": "Schedule H1 Report (Option)",
                        "helper": ""
                      },
                      {
                        "desc": "Extracts a detailed, customer-specific chronological ledger showing all historical financial transactions, payments made, and outstanding balances.",
                        "name": "Statement of Account (SOA) (Option)",
                        "helper": ""
                      }
                    ],
                    "title": "Available Report Types"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to generate the selected report type and automatically download it to your local device as a Microsoft Excel file.",
                        "name": "Download (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Download"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Download Reports group",
            "description": ""
          }
        ],
        "description": ""
      }
    ],
    "description": "Centralized financial hub providing access to dashboard, POS, payments, returns, master settings, and downloadable reports."
  },
  {
    "id": "inventory",
    "title": "Inventory",
    "topics": [
      {
        "id": "inventory-dashboard",
        "title": "Inventory Dashboard",
        "groups": [
          {
            "id": "inventory-overview-section",
            "items": [
              {
                "id": "inventory-dashboard-overview",
                "title": "Inventory Dashboard Overview",
                "details": [
                  {
                    "type": "heading",
                    "title": "Inventory Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inventory_dashboard.png",
                    "alt": "Inventory Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Inventory Dashboard functions as the clinic’s inventory management system, providing tools to upload inventory, monitor inventory levels, manage items and medications, and maintain an organized list of services."
                  }
                ],
                "summary": "The Inventory Dashboard functions as the clinic’s inventory management system, providing tools to upload inventory, monitor inventory levels, manage items and medications, and maintain an organized list of services."
              }
            ],
            "title": "Inventory Dashboard",
            "description": "The Inventory Dashboard functions as the clinic’s inventory management system."
          }
        ],
        "description": "Overview of inventory levels, item management, and service lists."
      },
      {
        "id": "stock",
        "title": "Stock",
        "groups": [
          {
            "id": "create-stock-section",
            "items": [
              {
                "id": "create-stock-workflow",
                "title": "Create Stock Workflow",
                "details": [
                  {
                    "type": "heading",
                    "title": "Create Stock"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/inventory-create-stock-video.mp4",
                    "poster": "/assets/images/knowledgebase/inventory_create_stock_video.jpg",
                    "alt": "Create Stock Video Guide"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Action → Create Stock → Fill in Item Master Details → Configure Operational Settings → Configure UOM Mapping → Add Batch Details → Submit"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Tick box if it's a NDPS Drug.",
                        "name": "NDPS Drug (Check-Box)"
                      },
                      {
                        "desc": "Enter general product information and master attributes.",
                        "name": "Fill up the details"
                      }
                    ],
                    "title": "Item Master Details"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Check this box if the item/medication is active, still being sold or prescribed by the clinic. If unchecked, the item becomes inactive. It remains in the system but will not appear as an available item during consultations.",
                        "name": "Active (Check-Box)"
                      },
                      {
                        "desc": "Check this box if the item/medication is billed by the clinic. Billable means the item should be billed or customer is charged for it every time it is used like vaccines or syringes; if it is unchecked the item is non-billable which means the item is used but not charged to the client (e.g. cotton rolls during surgery, disinfectants etc).",
                        "name": "Billable (Check-Box)"
                      },
                      {
                        "desc": "Check this box when the item/medication is consumed during services and you want to charge for the item but you are unable to measure how much of the item is utilized.\n• When Auto-Consume is ON, any stock used as part of a service is automatically deducted based on the service itself, not on the exact quantity used. In this case, the cost is included in the service charge.\n• However, when an item that has auto-consume on but the item is sold or prescribed directly, the system deducts and charges for the exact quantity at its MRP.\n• Auto-consume comes in when an item is billable and non-billable based on the service.",
                        "name": "Auto-Consumed (Check-Box)"
                      }
                    ],
                    "title": "Operational Settings (Vetinstant)"
                  },
                  {
                    "type": "text",
                    "content": "Click (+Add), fill in the following details:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Conversion Factor from Purchase UOM to Sales UOM.",
                        "name": "Conversion Factor (Input Field)"
                      },
                      {
                        "desc": "Selling unit. Add multiple layers as necessary.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "Mark \"Yes\" if the given UOM is available in current branch. Default is set to \"Yes\".",
                        "name": "Allow Sale (Check-Box)"
                      },
                      {
                        "desc": "To delete a UOM mapping entry.",
                        "name": "Delete (Icon)"
                      },
                      {
                        "desc": "Alerts you when stock falls below this quantity.",
                        "name": "Threshold Qty (Input Field)"
                      },
                      {
                        "desc": "Selling unit. Add multiple layers as necessary.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "Recommended quantity to reorder when stock is low.",
                        "name": "Reorder Qty (Input Field)"
                      }
                    ],
                    "title": "UOM Mapping (Sales UOM)"
                  },
                  {
                    "type": "text",
                    "content": "Click (+Add), fill in the following details:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Unique ID or code stamped on a specific product.",
                        "name": "Batch Number (Input Field)"
                      },
                      {
                        "desc": "The day the item was made or packaged.",
                        "name": "Mfg Date (Date Field)"
                      },
                      {
                        "desc": "The deadline after which the product shouldn't be sold or used.",
                        "name": "Expiry Date (Date Field)"
                      },
                      {
                        "desc": "The specific shelf, aisle, or room where the item is kept.",
                        "name": "Location (Drop-Down)"
                      },
                      {
                        "desc": "The exact physical count or amount of the item in stock.",
                        "name": "Unit Qty (Input Field)"
                      },
                      {
                        "desc": "How the stock is counted (eg. Bottle(s), Box(es), Pack(s)). Value will show based on what's added in UOM Mapping.",
                        "name": "UOM (Drop-Down)"
                      },
                      {
                        "desc": "The price a customer pays for a single unit.",
                        "name": "Selling Price (Input Field)"
                      },
                      {
                        "desc": "The unit used to set the price (eg., charging per Box vs. per Piece).",
                        "name": "Pricing UOM (Input Field)"
                      },
                      {
                        "desc": "Total cost to buy and deliver the item, including shipping and taxes. Auto-computed from purchases.",
                        "name": "Landed Cost (Buying) (Input Field)"
                      }
                    ],
                    "title": "Batch Details"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "To save changes.",
                        "name": "Save (Button)"
                      },
                      {
                        "desc": "To discard changes.",
                        "name": "Discard (Button)"
                      },
                      {
                        "desc": "To edit existing batch details.",
                        "name": "Edit (Button)"
                      },
                      {
                        "desc": "Click to save all the created stock details.",
                        "name": "Submit (Button)"
                      }
                    ],
                    "title": "Action Buttons & Submissions"
                  }
                ],
                "summary": "Workflow to create and upload new stock items with operational settings and batch details.",
                "workflow": "Inventory → Stock → Action → Create Stock → Fill in Item Master Details → Configure Operational Settings → Configure UOM Mapping → Add Batch Details → Submit"
              }
            ],
            "title": "Create Stock",
            "description": "Workflow and configuration options for creating new stock items."
          },
          {
            "id": "all-stock-section",
            "items": [
              {
                "id": "all-stock-dashboard",
                "title": "All Stock Dashboard",
                "details": [
                  {
                    "type": "heading",
                    "title": "All Stock"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inventory_all_stock.png",
                    "alt": "All Stock"
                  },
                  {
                    "type": "text",
                    "content": "The All Stock Dashboard displays the total number of inventory stocks, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory."
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → All Stock"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name to search for a specific item.",
                        "name": "Generic/Trade Name (Input Field)"
                      },
                      {
                        "desc": "Enter or scan the barcode to search for an item.",
                        "name": "Barcode (Input Field)"
                      },
                      {
                        "desc": "Select one or multiple categories to filter the stock list.",
                        "name": "Category (Drop-Down with Checkbox)"
                      },
                      {
                        "desc": "Select one or multiple clinic locations to filter the stock list.",
                        "name": "Location (Drop-Down with Checkbox)"
                      },
                      {
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the stock table. \n\n\nNote: By default, items starting with a numeric value will always appear first in both A-Z and Z-A sorting results.",
                        "name": "Sorting (Toggle)"
                      }
                    ],
                    "title": "Search & Sort Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select checkboxes to narrow down stock list by: Status, Billable, Auto-consumed, Subcategory, HSN Code, Brand, Nature, Batch Number, Mfg Date, Expiry Date, Base UOM, Unit Quantity, Unit Selling Price, Taxes, Landed Cost, Drug Schedule, Reorder Level.",
                        "name": "Filter Checkboxes"
                      },
                      {
                        "desc": "After selecting the desired filter options, click save to apply the filter to the table.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Advanced Filter Options"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all selected search constraints and filters.",
                        "name": "Reset (Button)"
                      },
                      {
                        "desc": "Click to reveal the following inventory management options:",
                        "name": "Action (Drop-Down Menu)"
                      },
                      {
                        "desc": "Select from Action drop-down menu to create a new stock item.",
                        "name": "Create Stock (Option)"
                      },
                      {
                        "desc": "Select to update pricing for multiple items at once. This opens a window with the following fields:\n\n\nType (Drop-Down) : Select either \"Increase\" or \"Decrease\".\n\n\nValue Type (Drop-Down) : Select either \"Percentage\" or \"Price\".\n\n\nValue (Input Field) : Enter the numerical value for the adjustment.\n\n\nUpdate (Button) : Click to apply the bulk price updates.",
                        "name": "Bulk Price Update (Option)"
                      },
                      {
                        "desc": "Click to download the current stock table data as an Excel file.",
                        "name": "Download (Button)"
                      }
                    ],
                    "title": "Action Buttons & Bulk Updates"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to expand the row and view specific batch details (e.g., Batch No, Mfg Date, Expiry Date, Unit Qty, UOM, Unit Selling Price, Location).",
                        "name": "+ (Icon)"
                      },
                      {
                        "desc": "Shows the commercial or brand name of the product.",
                        "name": "Trade Name (Display Field)"
                      },
                      {
                        "desc": "Shows the scientific or generic name of the item.",
                        "name": "Generic Name (Display Field)"
                      },
                      {
                        "desc": "Displays the assigned category of the item.",
                        "name": "Category (Display Field)"
                      },
                      {
                        "desc": "Shows the current unit quantity in stock.",
                        "name": "Unit Qty (Display Field)"
                      },
                      {
                        "desc": "Hovering over this icon reveals a tooltip displaying the \"Total Stock Value\" (e.g., Rs. 39665.00) and the exact \"Available Qty\" broken down by measurement (e.g., 52 pcs | 523 L | 5230 mcg).",
                        "name": "ⓘ (Info Icon)"
                      },
                      {
                        "desc": "Displays current operational status of item (\"Active\" or \"Inactive\").",
                        "name": "Status (Display Field)"
                      }
                    ],
                    "title": "Table Content & Display Fields"
                  }
                ],
                "summary": "The All Stock Dashboard displays the total number of inventory stocks, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "workflow": "Inventory → Stock → All Stock"
              }
            ],
            "title": "All Stock",
            "description": "Displays total inventory stocks, search options, filters, bulk updates, and table views."
          },
          {
            "id": "low-stock-section",
            "items": [
              {
                "id": "low-stock-dashboard",
                "title": "Low Stock Dashboard",
                "details": [
                  {
                    "type": "heading",
                    "title": "Low Stock"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inventory_low_stock.png",
                    "alt": "Low Stock"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Low Stock"
                  },
                  {
                    "type": "text",
                    "content": "The Low Stock Dashboard displays the total number of inventory low stocks, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name to search for a specific item.",
                        "name": "Generic/Trade Name (Input Field)"
                      },
                      {
                        "desc": "Enter or scan the barcode to search for an item.",
                        "name": "Barcode (Input Field)"
                      },
                      {
                        "desc": "Select one or multiple categories to filter the stock list.",
                        "name": "Category (Drop-Down with Checkbox)"
                      },
                      {
                        "desc": "Select one or multiple clinic locations to filter the stock list.",
                        "name": "Location (Drop-Down with Checkbox)"
                      },
                      {
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the stock table.\n\n\nNote: By default, items starting with a numeric value will always appear first in both A-Z and Z-A sorting results.",
                        "name": "Sorting (Toggle)"
                      }
                    ],
                    "title": "Search & Sort Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Select checkboxes to narrow down stock list by: Status, Billable, Auto-consumed, Subcategory, HSN Code, Brand, Nature, Batch Number, Mfg Date, Expiry Date, Base UOM, Unit Quantity, Unit Selling Price, Taxes, Landed Cost, Drug Schedule, Reorder Level.",
                        "name": "Filter Checkboxes"
                      },
                      {
                        "desc": "After selecting the desired filter options, click save to apply the filter to the table.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Advanced Filter"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all selected search constraints and filters.",
                        "name": "Reset (Button)"
                      },
                      {
                        "desc": "Click to download the current stock table data as an Excel file.",
                        "name": "Download (Button)"
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to expand the row and view specific batch details (e.g., Batch No, Mfg Date, Expiry Date, Unit Qty, UOM, Unit Selling Price, Location).",
                        "name": "+ (Icon)"
                      },
                      {
                        "desc": "Shows the commercial or brand name of the product.",
                        "name": "Trade Name (Display Field)"
                      },
                      {
                        "desc": "Shows the scientific or generic name of the item.",
                        "name": "Generic Name (Display Field)"
                      },
                      {
                        "desc": "Displays the assigned category of the item.",
                        "name": "Category (Display Field)"
                      },
                      {
                        "desc": "Shows the current unit quantity in stock.",
                        "name": "Unit Qty (Display Field)"
                      },
                      {
                        "desc": "Hovering over this icon reveals a tooltip displaying the \"Total Stock Value\" (e.g., Rs. 39665.00) and the exact \"Available Qty\" broken down by measurement (e.g., 52 pcs | 523 L | 5230 mcg).",
                        "name": "ⓘ (Info Icon)"
                      },
                      {
                        "desc": "Displays the current operational status of the item (\"Active\" or \"Inactive\").",
                        "name": "Status (Display Field)"
                      }
                    ],
                    "title": "Table content"
                  }
                ],
                "summary": "The Low Stock Dashboard displays the total number of inventory low stocks, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "workflow": "Inventory → Stock → Low Stock"
              }
            ],
            "title": "Low Stock",
            "description": "Monitor items where current unit quantity is at or below threshold reorder levels."
          },
          {
            "id": "near-expiry-section",
            "items": [
              {
                "id": "near-expiry-dashboard",
                "title": "Near Expiry Dashboard",
                "details": [
                  {
                    "type": "heading",
                    "title": "Near Expiry"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inventory_near_expiry.png",
                    "alt": "Near Expiry"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Near Expiry"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name to search for a specific item.",
                        "name": "Generic/Trade Name (Input Field)"
                      },
                      {
                        "desc": "Enter or scan the barcode to search for an item.",
                        "name": "Barcode (Input Field) "
                      },
                      {
                        "desc": "Select the number of days to filter the stock by the expiry date.",
                        "name": "Expiry Date (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Select one or multiple clinic locations to filter the stock list.",
                        "name": "Location (Drop-Down with Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the stock table.\n\n\nNote: By default, items starting with a numeric value will always appear first in both A-Z and Z-A sorting results.",
                        "name": "Sorting (Toggle) ",
                        "helper": ""
                      }
                    ],
                    "title": "Search & Sort Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Status, Billable, Auto-consumed, Subcategory, HSN Code, Brand, Nature, Batch Number, Mfg Date, Expiry Date, Base UOM, Unit Quantity, Unit Selling Price, Taxes, Landed Cost, Drug Schedule, Reorder Level.",
                        "name": "Select the (checkboxes) from the filter menu",
                        "helper": ""
                      },
                      {
                        "desc": "After selecting the desired filter options, click save to apply the filter to the table.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Advanced Filter"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all selected search constraints and filters.",
                        "name": "Reset (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to download the current stock table data as an Excel file.",
                        "name": "Download (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to expand the row and view specific batch details (e.g., Batch No, Mfg Date, Expiry Date, Unit Qty, UOM, Unit Selling Price, Location).",
                        "name": "+ (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the commercial or brand name of the product.",
                        "name": "Trade Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the scientific or generic name of the item.",
                        "name": "Generic Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the assigned category of the item.",
                        "name": "Category (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the current unit quantity in stock.",
                        "name": "Unit Qty (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Hovering over this icon reveals a tooltip displaying the \"Total Stock Value\" (e.g., Rs. 39665.00) and the exact \"Available Qty\" broken down by measurement (e.g., 52 pcs | 523 L | 5230 mcg).",
                        "name": "ⓘ (Info Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the current operational status of the item (\"Active\" or \"Inactive\").",
                        "name": "Status (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Table content"
                  }
                ],
                "summary": "The Near Expiry Stock Dashboard displays the total number of inventory stocks that is near expiry, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "workflow": "Inventory → Stock → Near Expiry"
              }
            ],
            "title": "Near Expiry",
            "description": "Monitor stock items approaching expiration dates."
          },
          {
            "id": "out-of-stock-section",
            "items": [
              {
                "id": "out-of-stock-dashboard",
                "title": "Out of Stock Dashboard",
                "details": [
                  {
                    "type": "heading",
                    "title": "Out of Stock"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/inventory_out_of_stock.png",
                    "alt": "Out of Stock"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Stock → Out of Stock"
                  },
                  {
                    "type": "text",
                    "content": "The Out of Stock Dashboard displays the total number of inventory items that are out of stock (Unit Qty. = 0), providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name to search for a specific item.",
                        "name": "Generic/Trade Name (Input Field)"
                      },
                      {
                        "desc": "Enter or scan the barcode to search for an item.",
                        "name": "Barcode (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select one or multiple categories to filter the stock list.",
                        "name": "Category (Drop-Down with Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Select one or multiple clinic locations to filter the stock list.",
                        "name": "Location (Drop-Down with Checkbox)",
                        "helper": ""
                      },
                      {
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the stock table.\n\n\nNote: By default, items starting with a numeric value will always appear first in both A-Z and Z-A sorting results.",
                        "name": "Sorting (Toggle)",
                        "helper": ""
                      }
                    ],
                    "title": "Search & Sort Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Status, Billable, Auto-consumed, Subcategory, HSN Code, Brand, Nature, Batch Number, Mfg Date, Expiry Date, Base UOM, Unit Quantity, Unit Selling Price, Taxes, Landed Cost, Drug Schedule, Reorder Level.",
                        "name": "Select the (checkboxes) from the filter menu "
                      },
                      {
                        "desc": "After selecting the desired filter options, click save to apply the filter to the table.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Advanced Filter"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all selected search constraints and filters.",
                        "name": "Reset (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to download the current stock table data as an Excel file.",
                        "name": "Download (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Action Buttons"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to expand the row and view specific batch details (e.g., Batch No, Mfg Date, Expiry Date, Unit Qty, UOM, Unit Selling Price, Location).",
                        "name": "+ (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the commercial or brand name of the product.",
                        "name": "Trade Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the scientific or generic name of the item.",
                        "name": "Generic Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the assigned category of the item.",
                        "name": "Category (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Shows the current unit quantity in stock.",
                        "name": "Unit Qty (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": " Hovering over this icon reveals a tooltip displaying the \"Total Stock Value\" (e.g., Rs. 39665.00) and the exact \"Available Qty\" broken down by measurement (e.g., 52 pcs | 523 L | 5230 mcg).",
                        "name": "ⓘ (Info Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the current operational status of the item (\"Active\" or \"Inactive\").",
                        "name": "Status (Display Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Table content"
                  }
                ],
                "summary": "The Out of Stock Dashboard displays the total number of inventory items that are out of stock (Unit Qty. = 0), providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the inventory.",
                "workflow": "Inventory → Stock → Out of Stock"
              }
            ],
            "title": "Out of Stock",
            "description": "Monitor inventory items where Unit Quantity = 0."
          }
        ],
        "description": "Manage stock creation, view all inventory items, low stock alerts, near expiry items, and out of stock records."
      },
      {
        "id": "service",
        "title": "Service",
        "groups": [
          {
            "id": "services-dashboard-section",
            "items": [
              {
                "id": "services-dashboard-overview",
                "title": "Services Dashboard Overview",
                "details": [
                  {
                    "type": "heading",
                    "title": "Services Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "https://media.canva.com/v2/image-resize/format:PNG/height:758/quality:100/uri:ifs%3A%2F%2FM%2Ffef2493b-eb63-4b98-a805-8c8d4a2d8e1f/watermark:F/width:1600?csig=AAAAAAAAAAAAAAAAAAAAAMNVCV9d0ACWuR9M8VOxQnYSqSmcJ31pAXS5BHXWovsY&exp=1790250401&osig=AAAAAAAAAAAAAAAAAAAAABeITeSSDMNDecrak2CHbZaKadLjkB2rMm7kvRDdgDga&signer=media-rpc&x-canva-quality=screen_2x",
                    "alt": "Services Dashboard"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Services"
                  },
                  {
                    "type": "text",
                    "content": "The Services Dashboard displays the total number of services, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the clinic's service offerings."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name to search for a specific service.",
                        "name": "Service Name (Input Field)"
                      },
                      {
                        "desc": "Select a category to filter the list.",
                        "name": "Category (Drop-Down)"
                      },
                      {
                        "desc": "Select a sub-category to filter the list.",
                        "name": "Sub-Category (Drop-Down)"
                      },
                      {
                        "desc": "Select the type of service (\"Medical\" or \"Non-Medical\").",
                        "name": "Type (Drop-Down)"
                      },
                      {
                        "desc": "Enables A-Z or Z-A alphabetical sorting of the services table.\n\n\nNote: By default, items starting with a numeric value will always appear first in both A-Z and Z-A sorting results.",
                        "name": "Sorting (Toggle)"
                      }
                    ],
                    "title": "Search & Sort Options"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Variant ID, Variant Name, SAC Code, Price Range, Tax Applicability, Taxes.",
                        "name": "Select the checkboxes from the filter menu to narrow down the service list by the following options:"
                      },
                      {
                        "desc": "After selecting desired filter options, click add to apply filter to table.",
                        "name": "Add (Button)"
                      }
                    ],
                    "title": "Advanced Filter"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Click to clear and reset all selected search constraints and filters.",
                        "name": "Reset (Button)"
                      },
                      {
                        "name": "Action (Drop-Down Menu)",
                        "desc": "Click to reveal the following service management options:",
                        "helper": ""
                      },
                      {
                        "desc": "Select from Action menu to create a new service.",
                        "name": "Create Service (Option)"
                      },
                      {
                        "desc": "Select to enable a previously disabled service.",
                        "name": "Enable (Option)"
                      },
                      {
                        "desc": "Select to update pricing for multiple services at once :\n\n\nType (Drop-Down) : Select either \"Increase\" or \"Decrease\".\n\n\nValue Type (Drop-Down) : Select either \"Percentage\" or \"Price\".\n\n\nValue (Input Field) : Enter the numerical value for the adjustment.\n\n\nUpdate (Button) : Click to apply the bulk price updates.",
                        "name": "Bulk Price Update (Option)"
                      }
                    ],
                    "title": "Action Buttons & Bulk Updates"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Click to select specific services (used for bulk actions).",
                        "name": "Checkbox (Check-Box)"
                      },
                      {
                        "desc": "Shows designated name of service.",
                        "name": "Service Name (Display Field)"
                      },
                      {
                        "desc": "Displays assigned category.",
                        "name": "Category (Display Field)"
                      },
                      {
                        "desc": "Displays current operational status (\"Active\" or \"Inactive\").",
                        "name": "Status (Display Field)"
                      },
                      {
                        "desc": "Displays assigned sub-category.",
                        "name": "Sub-Category (Display Field)"
                      },
                      {
                        "desc": "Shows Services Accounting Code associated with service for tax purposes.",
                        "name": "SAC Code (Display Field)"
                      },
                      {
                        "desc": "Displays current price a customer pays for the service.",
                        "name": "Selling Price (Display Field)"
                      }
                    ],
                    "title": "Table Content Fields"
                  }
                ],
                "summary": "The Services Dashboard displays the total number of services, providing comprehensive search fields, advanced filters, and action buttons to efficiently manage and update the clinic's service offerings.",
                "workflow": "Inventory → Services"
              }
            ],
            "title": "Services Dashboard",
            "description": "Manage clinic service offerings, filtering, bulk price updates, and status toggles."
          },
          {
            "id": "create-service-section",
            "items": [
              {
                "id": "create-service-workflow",
                "title": "Create Service Workflow",
                "details": [
                  {
                    "type": "heading",
                    "title": "Create Service"
                  },
                  {
                    "type": "image",
                    "src": "https://media.canva.com/v2/image-resize/format:PNG/height:758/quality:100/uri:ifs%3A%2F%2FM%2F7c70fd40-9e60-4038-afd7-b059673a3ff9/watermark:F/width:1600?csig=AAAAAAAAAAAAAAAAAAAAAADBcVvpjm2rQhj6xThx1DlnQs8q1cgXIofVXLdb_5kB&exp=1790252952&osig=AAAAAAAAAAAAAAAAAAAAAFW_nVAYcUn30ZmZSLBJdIkct21qNGEA5i9Pqt2r5jU-&signer=media-rpc&x-canva-quality=screen_2x",
                    "alt": "Create Service"
                  },
                  {
                    "type": "heading",
                    "title": "Steps to Create a Services — Video"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/create-service-video.mp4",
                    "alt": "Steps to Create a Services Video Guide"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Service → Action → Create Service → Fill in Service Details → Add Reminder Template (if applicable) → Select Linked Subcategories → Add Service Variant → Configure Variant → Save → Submit"
                  },
                  {
                    "type": "text",
                    "content": "Services are used to create a list of services that the clinic offers. P.A.W.S not only has an option to create services but you can add consumables, medications administered during the service and prescribed to the patient. Once these details are added it will get reflected in the patient workbook when the said service is chosen, further removing manual entry by the doctors and automating workflows.\n\nClick on Create → A pop-up opens asking you to fill up Service Details & its Variants.\n\nService Details : They define the core information of a service offered by the clinic."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Uncheck to disable this service so it is no longer offered.",
                        "name": "Disabled (Check-box)"
                      },
                      {
                        "desc": "Select Medical or Non-medical.",
                        "name": "Medical Type"
                      },
                      {
                        "desc": "Consultation, Surgery, Grooming etc.",
                        "name": "Category"
                      },
                      {
                        "desc": "Based on Category (e.g., General Consultation or Follow-Up).",
                        "name": "Sub-Category"
                      },
                      {
                        "desc": "Enter the designated procedure name.",
                        "name": "Procedure Name"
                      },
                      {
                        "desc": "Service Accounting Code used for GST purposes.",
                        "name": "SAC Code"
                      },
                      {
                        "desc": "Yes/No.",
                        "name": "Tax Applicability"
                      }
                    ],
                    "title": "Service Details Form Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter name of reminder template.",
                        "name": "TEMPLATE NAME (Input Field)"
                      },
                      {
                        "desc": "Select and link pre-existing service(s).",
                        "name": "LINKED SERVICE (Drop-down)"
                      },
                      {
                        "desc": "Dose.",
                        "name": "WHAT DO YOU CALL ONE OCCURRENCE?"
                      },
                      {
                        "desc": "Chained steps, each due date calculated once the previous step is completed.",
                        "name": "STEPS — CHAINED, EACH DUE DATE CALCULATED ONCE THE PREVIOUS STEP IS COMPLETED"
                      },
                      {
                        "desc": "Click Save to store template.",
                        "name": "Save (Button)"
                      }
                    ],
                    "title": "Reminder Template Configuration"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Stock subcategories whose items become searchable for every variant of this service.",
                        "name": "Linked Subcategories"
                      }
                    ],
                    "title": "Linked Subcategories"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Unique identifier for this variant.",
                        "name": "Variant Id (Input Field)"
                      },
                      {
                        "desc": "Name that distinguishes this service variant.",
                        "name": "Variant Name (Input Field)"
                      },
                      {
                        "desc": "Auto-calculated cost from consumables and administered items linked to the variant.",
                        "name": "BOM Charge"
                      },
                      {
                        "desc": "Estimated time to complete the service, in minutes.",
                        "name": "Estimated Duration (in Minutes) (Input Field)"
                      },
                      {
                        "desc": "Professional or service fee charged for this variant.",
                        "name": "Service Charge (Input Field)"
                      },
                      {
                        "desc": "Total selling price for the bundled service (including included items).",
                        "name": "Bundled Selling Price (Input Field)"
                      }
                    ],
                    "title": "Service Variants Configuration",
                    "desc": "Variants : Variants represent the different configurations or options of a service. Each variant corresponds to a specific version of the service that may differ by factors such as species, dosage, duration, pricing, or execution method, and is identified using a unique Variant ID.\n\nClick (+Add) and fill the following details:"
                  },
                  {
                    "type": "text",
                    "content": "After filling the above details → Next to the Variant ID and the details there is a “View” button → Click on the “View” Button → Another Pop-up will open where you will have to fill the Consumables, Administered & Prescription → Click on “Add” under Consumables & Administered and Prescriptions → Fill the Details as shown below → Click Save → This will take you back to the Initial pop-up with the Service Details → Check the details and click submit."
                  },
                  {
                    "type": "image",
                    "src": "https://media.canva.com/v2/image-resize/format:PNG/height:235/quality:100/uri:ifs%3A%2F%2FM%2Fac111244-0e45-4c21-90f7-936408fe50a1/watermark:F/width:1600?csig=AAAAAAAAAAAAAAAAAAAAAMOam3rHWP0iW9C3jOH769EPmJ2K4Zy0CoKM_KwB8VEJ&exp=1790249474&osig=AAAAAAAAAAAAAAAAAAAAACjSe_kAApaWhgJzg1QZWX44FgF-2J0zucPi3FvHa8nh&signer=media-rpc&x-canva-quality=screen_2x",
                    "alt": "Variant / Configuration Screenshot 1 - Consumables"
                  },
                  {
                    "type": "image",
                    "src": "https://media.canva.com/v2/image-resize/format:PNG/height:226/quality:100/uri:ifs%3A%2F%2FM%2Fccb76613-34bc-4ab0-b2d1-006a2c72116d/watermark:F/width:1600?csig=AAAAAAAAAAAAAAAAAAAAALK-Ygf2Y_nAGYt-SdaIK9nRvp8mDquacNnr_uN15I3z&exp=1790250059&osig=AAAAAAAAAAAAAAAAAAAAAF5u4khq8P7o5IVkc_F4o9V9nBuNN08J3lQdBdTSFu70&signer=media-rpc&x-canva-quality=screen_2x",
                    "alt": "Variant / Configuration Screenshot 2 - Administered"
                  },
                  {
                    "type": "image",
                    "src": "https://media.canva.com/v2/image-resize/format:PNG/height:207/quality:100/uri:ifs%3A%2F%2FM%2F9fb8b155-fd6a-4e23-a8f0-92180acd9687/watermark:F/width:1585?csig=AAAAAAAAAAAAAAAAAAAAAJ-wTmGKNccv3IfFWHGrRO2WGwbKSVkWmnsRA2u28JPR&exp=1790250214&osig=AAAAAAAAAAAAAAAAAAAAAD8aGEVx8F2mt7K5GpAuMCuDqq5dofuSI_m2qrmqI1Um&signer=media-rpc&x-canva-quality=screen_3x",
                    "alt": "Variant / Configuration Screenshot 3 - Prescription"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "There will be a drop-down of all the medications & items picked up from the stock inventory.",
                        "name": "Stock Inventory Drop-Down"
                      },
                      {
                        "desc": "You can add any number of variants for each service/procedure and you can add all the consumables and medications, the system will automatically update the cost.",
                        "name": "Multiple Service Variants & Auto Cost Updates"
                      }
                    ],
                    "title": "Stock Inventory Selection & Multiple Variants"
                  },
                  {
                    "type": "systemAction",
                    "title": "Prerequisite Workflow Rule",
                    "content": "Note: For the best workflow, complete the stock upload before you begin setting up services."
                  },
                  {
                    "type": "notification",
                    "title": "Consultation & Real-Time Inventory Updates",
                    "content": "Automated Workflow Integration:\nSetting up services allows you to streamline your workflow during consultations. Once a service is configured, simply typing the procedure name during a consultation will automatically display the associated consumables, administered medications, and prescribed medications in the Patient Workbook.\n\nThis saves time, reduces manual entry, and helps you maintain accurate inventory and billing records.\n\nReal-Time Stock Deduction:\nEvery time a service is used, the inventory of the associated items is automatically updated in real time, ensuring that your stock levels remain accurate."
                  }
                ],
                "summary": "Services define clinic offerings and link consumables, administered medications, and prescriptions directly to patient workbook consultations.",
                "workflow": "Inventory → Service → Action → Create Service → Fill in Service Details → Add Reminder Template (if applicable) → Select Linked Subcategories → Add Service Variant → Configure Variant → Save → Submit"
              }
            ],
            "title": "Create Service",
            "description": "Complete workflow for creating services, reminder templates, linked subcategories, and variant configurations."
          }
        ],
        "description": "Services Dashboard, Create Service workflow, Reminder Templates, Linked Subcategories, and Variant Configurations."
      },
      {
        "id": "topic-Purchase",
        "title": "Purchase",
        "groups": [
          {
            "id": "group-Purchase",
            "items": [
              {
                "id": "item-Purchase-Overview",
                "title": "Purchase Overview",
                "summary": "",
                "workflow": "Inventory → Purchase → Purchase Orders\nInventory → Purchase → Purchase Invoice",
                "details": [
                  {
                    "type": "workflow",
                    "content": "Inventory → Purchase → Purchase Orders"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Purchase → Purchase Invoice"
                  },
                  {
                    "type": "text",
                    "content": "The Purchase module consists of the Purchase Orders and Purchase Invoice sections. This module helps clinic staff track and manage supplier purchase orders and handle incoming invoice tracking."
                  }
                ]
              },
              {
                "id": "item-Purchase-Orders",
                "title": "Purchase Orders",
                "summary": "",
                "workflow": "Inventory → Purchase → Purchase Orders",
                "details": [
                  {
                    "type": "heading",
                    "title": "Purchase Orders"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/purchase_orders.png",
                    "alt": "Purchase Orders"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Purchase → Purchase Orders"
                  },
                  {
                    "type": "text",
                    "content": "The Purchase Orders sub-module allows you to create, track, and manage orders placed with suppliers."
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select the beginning date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select the ending date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier (Input Field)",
                        "desc": "Enter the supplier name to filter the orders.",
                        "helper": ""
                      },
                      {
                        "name": "PO Number (Input Field)",
                        "desc": "Enter a specific Purchase Order number.",
                        "helper": ""
                      },
                      {
                        "name": "GNR Number (Input Field)",
                        "desc": "Enter a specific Goods Receipt Note number.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Reset",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all search input fields.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Create",
                    "items": [
                      {
                        "name": "Create (Button)",
                        "desc": "Click to initiate a new purchase order.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Clicking the \"Create\" button opens a new window to generate a purchase order with the following fields (* indicates a mandatory field):"
                  },
                  {
                    "type": "fields",
                    "title": "Creating a Purchase Order",
                    "items": [
                      {
                        "name": "Supplier Name * (Input Field with Drop-Down)",
                        "desc": "Search and select the supplier. Once selected, the system automatically fills the GSTIN, Email, Phone, Purchase Type, and Expected Delivery (defaults to today's date).",
                        "helper": ""
                      },
                      {
                        "name": "GSTIN (Input Field)",
                        "desc": "The supplier's tax identification number.",
                        "helper": ""
                      },
                      {
                        "name": "Email (Input Field)",
                        "desc": "The supplier's email address.",
                        "helper": ""
                      },
                      {
                        "name": "Phone (Input Field)",
                        "desc": "The supplier's contact number.",
                        "helper": ""
                      },
                      {
                        "name": "Address (Input Field)",
                        "desc": "The physical address of the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Date * (Calendar Field)",
                        "desc": "The date the order is created.",
                        "helper": ""
                      },
                      {
                        "name": "Purchase Type (Drop-Down)",
                        "desc": "Select the tax classification (Options: Intrastate, Interstate, Exempt).",
                        "helper": ""
                      },
                      {
                        "name": "Expected Delivery * (Calendar Field)",
                        "desc": "The anticipated date of arrival.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Items Table",
                    "items": [
                      {
                        "name": "Add (Button)",
                        "desc": "Click the Add (Button) to include items in the order. Once an item name is selected, the system auto-fills the related details, defaults the quantity to 1, and leaves all fields editable.",
                        "helper": ""
                      },
                      {
                        "name": "Table Fields",
                        "desc": "S No, Name *, HSN Code, Ordered Qty *, UOM *, Taxable Value *, CGST % *, SGST % *, Item Total *.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Order Summary & Actions",
                    "items": [
                      {
                        "name": "Order Summary",
                        "desc": "The display fields auto-calculate as items are added or removed (default values are 0.00).",
                        "helper": ""
                      },
                      {
                        "name": "Total Taxable Value (Display Field)",
                        "desc": "The base cost of the ordered items.",
                        "helper": ""
                      },
                      {
                        "name": "Taxes (CGST) / Taxes (SGST) (Display Fields)",
                        "desc": "Auto-calculated tax amounts.",
                        "helper": ""
                      },
                      {
                        "name": "Additional Charges (Input Field)",
                        "desc": "Enter any extra fees (e.g., shipping). This is not auto-calculated and can be left blank.",
                        "helper": ""
                      },
                      {
                        "name": "Grand Total (Display Field)",
                        "desc": "The final calculated amount.",
                        "helper": ""
                      },
                      {
                        "name": "Save as Draft (Button)",
                        "desc": "Click to save the progress without finalizing.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to create and submit the purchase order.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "Validation Note",
                    "content": "At least 1 item must be added to create an order. Otherwise, a \"Please add at least 1 record\" notification appears above the action buttons."
                  },
                  {
                    "type": "text",
                    "content": "The main dashboard displays all purchase orders in a structured table, preceded by a total count display (e.g., Number of records: 10)."
                  },
                  {
                    "type": "fields",
                    "title": "Purchase Order Record Table",
                    "items": [
                      {
                        "name": "Table Fields",
                        "desc": "PO, Supplier, Date, Amount, Status (Awaiting Receipt, Draft, Received).",
                        "helper": ""
                      },
                      {
                        "name": "View (Icon)",
                        "desc": "Available for all statuses. Click to open and review the full Purchase Order window.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Available only for \"Awaiting Receipt\" or \"Draft\" statuses. Clicking this triggers a pop-up: \"Are you sure you want to delete the invoice?\" with Yes/No options.",
                        "helper": ""
                      },
                      {
                        "name": "Mark as Received (Icon)",
                        "desc": "Clicking this triggers a confirmation pop-up: \"Are you sure you want to continue?\" with Yes/No options.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "item-Purchase-Invoice",
                "title": "Purchase Invoice",
                "summary": "",
                "workflow": "Inventory → Purchase → Purchase Invoice",
                "details": [
                  {
                    "type": "heading",
                    "title": "Purchase Invoice"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/purchase_invoice.png",
                    "alt": "Purchase Invoice"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Purchase → Purchase Invoice"
                  },
                  {
                    "type": "text",
                    "content": "The Purchase Invoice sub-module allows you to log and manage the finalized bills received from suppliers."
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select the beginning date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select the ending date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier (Input Field)",
                        "desc": "Enter the supplier name to filter the invoices.",
                        "helper": ""
                      },
                      {
                        "name": "Invoice Number (Input Field)",
                        "desc": "Enter a specific invoice number.",
                        "helper": ""
                      },
                      {
                        "name": "GNR Number (Input Field)",
                        "desc": "Enter a specific Goods Receipt Note number.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Reset",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all search input fields.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Create",
                    "items": [
                      {
                        "name": "Create (Button)",
                        "desc": "Click to initiate a new purchase invoice.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Clicking the \"Create\" button gives you two initial options:"
                  },
                  {
                    "type": "fields",
                    "title": "Initial Options",
                    "items": [
                      {
                        "name": "Upload",
                        "desc": "Upload a clear picture of your supplier invoice.",
                        "helper": ""
                      },
                      {
                        "name": "Manual Entry",
                        "desc": "Manually input the invoice details into the system.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Selecting Manual Entry opens the \"Purchase Invoice\" window with the following fields (* indicates a mandatory field):"
                  },
                  {
                    "type": "fields",
                    "title": "Creating a Purchase Invoice",
                    "items": [
                      {
                        "name": "Bill To * (Drop-Down)",
                        "desc": "Select the billing entity.",
                        "helper": ""
                      },
                      {
                        "name": "Location * (Input Field with Drop-Down)",
                        "desc": "Select the receiving clinic location.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier Name * (Input Field with Drop-Down)",
                        "desc": "Select the supplier. This auto-fills the GSTIN, Email, and Phone fields.",
                        "helper": ""
                      },
                      {
                        "name": "GSTIN / Email / Phone / Address (Input Fields)",
                        "desc": "Supplier contact and tax details.",
                        "helper": ""
                      },
                      {
                        "name": "Invoice Number * (Input Field)",
                        "desc": "Enter the invoice number provided by the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Date * (Calendar Input)",
                        "desc": "Enter the date of the invoice.",
                        "helper": ""
                      },
                      {
                        "name": "Purchase Type (Drop-Down)",
                        "desc": "Select the tax classification (Options: Intrastate, Interstate, Exempt).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Items Table",
                    "items": [
                      {
                        "name": "Add (Button)",
                        "desc": "Click the Add (Button) to include items in the invoice. When an item name is selected, all related product details automatically populate.",
                        "helper": ""
                      },
                      {
                        "name": "Table Fields",
                        "desc": "S No, Name *, HSN Code, Batch Number, Location, Expiry Date, Received Qty *, UOM *, MRP, Rate *, Disc %, CGST % *, SGST % *, Item Total *.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Invoice Summary & Actions",
                    "items": [
                      {
                        "name": "Total Taxable Value (Display Field)",
                        "desc": "The base cost of the invoice items.",
                        "helper": ""
                      },
                      {
                        "name": "Discount Value (Display Field)",
                        "desc": "Total discounts applied.",
                        "helper": ""
                      },
                      {
                        "name": "Taxes (CGST) / Taxes (SGST) (Display Fields)",
                        "desc": "Auto-calculated tax amounts.",
                        "helper": ""
                      },
                      {
                        "name": "Additional Charges (Input Field)",
                        "desc": "Enter any extra fees (not auto-calculated).",
                        "helper": ""
                      },
                      {
                        "name": "Grand Total (Display Field)",
                        "desc": "The final calculated invoice amount.",
                        "helper": ""
                      },
                      {
                        "name": "Save as Draft (Button)",
                        "desc": "Click to save the progress without finalizing.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to create and submit the purchase invoice.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "Validation Note",
                    "content": "At least 1 item must be added to create an invoice. Otherwise, a \"Please add at least 1 record\" notification appears."
                  },
                  {
                    "type": "text",
                    "content": "The dashboard displays all purchase invoices in a structured table, preceded by a total count display (e.g., Number of records: 130)."
                  },
                  {
                    "type": "fields",
                    "title": "Purchase Invoice Record Table",
                    "items": [
                      {
                        "name": "Table Fields",
                        "desc": "Invoice, GRN Number, Supplier, Date, Amount, Status (Draft, Received, Cancelled).",
                        "helper": ""
                      },
                      {
                        "name": "View (Icon)",
                        "desc": "Available for all statuses. Click to open and review the full Purchase Invoice window.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Available only for \"Draft\" status. Clicking this triggers a pop-up: \"Are you sure you want to delete the invoice?\" with Yes/No options.",
                        "helper": ""
                      },
                      {
                        "name": "Cancel (Icon)",
                        "desc": "Available only for \"Draft\" status. Clicking this triggers a pop-up: \"Are you sure you want to cancel this purchase invoice? The purchase will be cancelled, but the associated batch will remain in inventory. Please use Stock Adjustments to adjust the batch quantity if required.\" with Yes/No options.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Purchase",
            "description": "The Purchase module consists of the Purchase Orders and Purchase Invoice sections."
          }
        ],
        "description": "The Purchase module consists of the Purchase Orders and Purchase Invoice sections."
      },
      {
        "id": "adjustments-transfer",
        "title": "Adjustments & Transfer",
        "groups": [
          {
            "id": "Adjustments-Transfer-Dashboard ",
            "items": [
              {
                "id": "Adjustments-Transfer-Dashboard-Section",
                "title": "Adjustments & Transfer Dashboard",
                "summary": "The Adjustments & Transfer Dashboard manages and tracks inventory movements.",
                "workflow": "Inventory → Adjustments & Transfer",
                "details": [
                  {
                    "type": "heading",
                    "title": "Adjustments & Transfer Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/adjustments_transfer_dashboard.png",
                    "alt": "Adjustments & Transfer Dashboard"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer"
                  },
                  {
                    "type": "text",
                    "content": "The Adjustments & Transfer Dashboard manages and tracks inventory movements. It encompasses the Transfers module, Adjustment History module, and Pending Review module, providing a comprehensive overview of all stock adjustments and transfer requests."
                  }
                ]
              }
            ],
            "title": "Adjustments & Transfer Dashboard",
            "description": "The Adjustments & Transfer Dashboard manages and tracks inventory movements. It encompasses the Transfers module, Adjustment History module, and Pending Review module, providing a comprehensive overview of all stock adjustments and transfer requests."
          },
          {
            "id": "transfers-group",
            "items": [
              {
                "id": "create-request",
                "title": "Create Request",
                "summary": "Click Create Request (Button) to open the form and initiate a new inventory transfer.",
                "workflow": "Inventory → Adjustments & Transfer → Transfers",
                "details": [
                  {
                    "type": "heading",
                    "title": "Create Request"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_request.png",
                    "alt": "Create Request"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/create-request-video.mp4",
                    "poster": "/assets/images/knowledgebase/create_request_video.jpg",
                    "alt": "Create Request Video Guide"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Transfers"
                  },
                  {
                    "type": "text",
                    "content": "The Transfers Dashboard allows clinic staff to create, manage, track, and process inventory movement requests, including internal transfers, external transfers, and stock consumption.\n\nClick the Create Request (Button) to open the form and initiate a new inventory transfer. The form allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "title": "Request Details",
                    "items": [
                      {
                        "name": "Request Type * (Drop-Down)",
                        "desc": "Select the nature of the request (\"Consumption\", \"Internal Transfer\", or \"External Transfer\").",
                        "helper": ""
                      },
                      {
                        "name": "Requested By * (Drop-Down)",
                        "desc": "Select the name of the clinic staff member or vet making the request.",
                        "helper": ""
                      },
                      {
                        "name": "To Location * (Drop-Down)",
                        "desc": "Select the destination location within the clinic for the transferred items.",
                        "helper": ""
                      },
                      {
                        "name": "Request Date * (Calendar Input)",
                        "desc": "Select the relevant date for the transfer request.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Request Items Table",
                    "items": [
                      {
                        "name": "Product Name * (Input Field / Drop-Down)",
                        "desc": "Search and select the item to be requested.",
                        "helper": ""
                      },
                      {
                        "name": "Batch No. * (Input Field / Drop-Down)",
                        "desc": "Select from the available batch details (displays the batch number, location, and available quantity for the issuer to review).",
                        "helper": ""
                      },
                      {
                        "name": "Available Qty @ Location (Display Field)",
                        "desc": "Displays the total available quantity based on the selected product and batch.",
                        "helper": ""
                      },
                      {
                        "name": "From Location (Display Field)",
                        "desc": "Displays the origin location based on the selected product and batch.",
                        "helper": ""
                      },
                      {
                        "name": "Issuing Qty * (Input Field)",
                        "desc": "Enter the specific quantity requested for transfer.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Delete (Button)",
                        "desc": "Click to remove a specific item row from the request table.",
                        "helper": ""
                      },
                      {
                        "name": "+ Add (Button)",
                        "desc": "Click to add additional items to the transfer request.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize, create, and send the request.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "transfers-record-table",
                "title": "Transfers Record Table",
                "summary": "The main Transfers module displays a table tracking all requests.",
                "workflow": "Inventory → Adjustments & Transfer → Transfers",
                "details": [
                  {
                    "type": "heading",
                    "title": "Transfers Record Table"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Transfers"
                  },
                  {
                    "type": "text",
                    "content": "The main Transfers module displays a table tracking all requests with the following fields:"
                  },
                  {
                    "type": "fields",
                    "title": "Record Table Fields",
                    "items": [
                      {
                        "name": "Sr.No. (Display Field)",
                        "desc": "The serial or sequence number of the record.",
                        "helper": ""
                      },
                      {
                        "name": "Request Type (Display Field)",
                        "desc": "Shows the type of transfer.",
                        "helper": ""
                      },
                      {
                        "name": "Location (Display Field)",
                        "desc": "Shows the destination location.",
                        "helper": ""
                      },
                      {
                        "name": "Requested By (Display Field)",
                        "desc": "Displays the name of the staff member who initiated the request.",
                        "helper": ""
                      },
                      {
                        "name": "Requested Date (Display Field)",
                        "desc": "Displays the date the request was submitted.",
                        "helper": ""
                      },
                      {
                        "name": "Issue Date (Display Field)",
                        "desc": "Displays the date the request was processed and issued.",
                        "helper": ""
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "Shows the current state of the request (Pending, Completed, or Cancelled).",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "action-buttons-status",
                "title": "Action Buttons (Based on Status)",
                "summary": "Action buttons available based on transfer status.",
                "workflow": "Inventory → Adjustments & Transfer → Transfers",
                "details": [
                  {
                    "type": "heading",
                    "title": "Action Buttons (Based on Status)"
                  },
                  {
                    "type": "text",
                    "content": "Depending on the status of the transfer in the record table, different action buttons are available:"
                  },
                  {
                    "type": "heading",
                    "title": "For Status: Pending"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/action_buttons_processing_1.png",
                    "alt": "Transfer Action Buttons - Pending"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/action_buttons_processing_2.png",
                    "alt": "Stock Requests Processing Pop-up"
                  },
                  {
                    "type": "buttons",
                    "title": "Pending Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to immediately cancel the pending issuing/transfer request.",
                        "helper": ""
                      },
                      {
                        "name": "Issue (Button)",
                        "desc": "Click to open the \"Stock Request\" window and process the transfer.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Stock Request Summary Details",
                    "items": [
                      {
                        "name": "Summary Details",
                        "desc": "Displays read-only fields for Requested By, Request Date, Request Type, and Location.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Processing Table",
                    "items": [
                      {
                        "name": "Product Name (Display Field)",
                        "desc": "Shows the requested product.",
                        "helper": ""
                      },
                      {
                        "name": "Requested Qty (Display Field)",
                        "desc": "Shows the amount requested.",
                        "helper": ""
                      },
                      {
                        "name": "Batch No. * (Input Field / Drop-Down)",
                        "desc": "The issuer selects the specific batch to draw stock from (displays batch number, location, and quantity).",
                        "helper": ""
                      },
                      {
                        "name": "From Location (Display Field)",
                        "desc": "Shows the origin location of the selected batch.",
                        "helper": ""
                      },
                      {
                        "name": "Available Qty @ Location (Display Field)",
                        "desc": "Shows current stock for the selected batch.",
                        "helper": ""
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "Initially empty. Upon taking action, it displays a green \"Issued quantity\" tag or a red \"Cancelled quantity\" tag.",
                        "helper": ""
                      },
                      {
                        "name": "Issuing Qty * (Input Field)",
                        "desc": "The issuer inputs the final quantity to be issued (defaults to the requested quantity).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Icons (Per Item)",
                    "items": [
                      {
                        "name": "Tick Mark (Icon)",
                        "desc": "Click to issue the entered quantity.",
                        "helper": ""
                      },
                      {
                        "name": "Cross (Icon)",
                        "desc": "Click to reject or cancel the specific item's request.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "heading",
                    "title": "For Status: Completed or Cancelled"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/action_buttons_processing_3.png",
                    "alt": "Transfer Action Buttons - Completed or Cancelled"
                  },
                  {
                    "type": "buttons",
                    "title": "Finalized Status Action Button",
                    "items": [
                      {
                        "name": "View (Button)",
                        "desc": "Click to open a read-only summary window of the finalized transfer. It displays the overarching request details (Requested By, Date, Type, Location) alongside the finalized table details (Product Name, Requested Qty, selected Batch No., From Location, Available Qty @ Location, final Status tag, and actual Issued Qty).",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Transfers",
            "description": "Allows clinic staff to create, manage, track, and process inventory movement requests."
          },
          {
            "id": "adjustment-history-group",
            "items": [
              {
                "id": "adjustment-history-overview",
                "title": "Adjustment History Search & Filter Options",
                "summary": "Search and filter past stock adjustments.",
                "workflow": "Inventory → Adjustments & Transfer → Adjustment History",
                "details": [
                  {
                    "type": "heading",
                    "title": "Adjustment History Search & Filter Options"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/adjustment_history.png",
                    "alt": "Adjustment History Search & Filter Options"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Adjustment History"
                  },
                  {
                    "type": "text",
                    "content": "The Adjustment History Dashboard displays a comprehensive log of stock adjustments. It provides various search fields, a total record count, and a detailed table to track and review past inventory changes."
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select the beginning date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select the ending date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier (Input Field)",
                        "desc": "Enter the supplier name to filter the adjustments.",
                        "helper": ""
                      },
                      {
                        "name": "Customer (Input Field)",
                        "desc": "Enter the customer name to filter the adjustments.",
                        "helper": ""
                      },
                      {
                        "name": "Invoice Number (Input Field)",
                        "desc": "Enter a specific invoice number to search.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select the adjustment type to filter the list (Options: \"Other Adjustments\", \"Return on Sales\").",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Reset",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all selected or entered search fields.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "create-stock-adjustment",
                "title": "Create Stock Adjustment",
                "summary": "Log a new inventory adjustment.",
                "workflow": "Inventory → Adjustments & Transfer → Adjustment History",
                "details": [
                  {
                    "type": "heading",
                    "title": "Create Stock Adjustment"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_stock_adjustment.png",
                    "alt": "Create Stock Adjustment"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Adjustment History"
                  },
                  {
                    "type": "text",
                    "content": "Click the Create (Button) to open the form and log a new inventory adjustment. The form displays the title \"Stock Adjustment\" and allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "title": "Stock Adjustment Form Fields",
                    "items": [
                      {
                        "name": "Type * (Drop-Down)",
                        "desc": "Select the adjustment type (e.g., \"Other Adjustments\").",
                        "helper": ""
                      },
                      {
                        "name": "Product * (Input Field / Drop-Down)",
                        "desc": "Search and select the specific product to be adjusted.",
                        "helper": ""
                      },
                      {
                        "name": "Batch Number * (Input Field / Drop-Down)",
                        "desc": "Select the specific batch to draw stock from (displays batch number, location, and quantity).",
                        "helper": ""
                      },
                      {
                        "name": "Quantity * (Input Field)",
                        "desc": "Enter the numerical quantity of items to be adjusted.",
                        "helper": ""
                      },
                      {
                        "name": "UOM (Display Field)",
                        "desc": "Automatically fills with the unit of measurement based on the selected product and batch details.",
                        "helper": ""
                      },
                      {
                        "name": "Remarks * (Input Field)",
                        "desc": "Enter any necessary notes, reasons, or context for the adjustment.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Submit",
                    "items": [
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the stock adjustment.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "adjustment-history-table",
                "title": "Adjustment History Record Table",
                "summary": "Displays historical adjustment logs.",
                "workflow": "Inventory → Adjustments & Transfer → Adjustment History",
                "details": [
                  {
                    "type": "heading",
                    "title": "Adjustment History Record Table"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Adjustment History"
                  },
                  {
                    "type": "fields",
                    "title": "Record Count",
                    "items": [
                      {
                        "name": "Number of count (Display Field)",
                        "desc": "Displays the total number of adjustment records currently listed (e.g., Number of count: 25).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "The main table displays the following fields for each historical adjustment record:"
                  },
                  {
                    "type": "fields",
                    "title": "Record Table Fields",
                    "items": [
                      {
                        "name": "# (Display Field)",
                        "desc": "The serial number or sequence of the record in the table.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Display Field)",
                        "desc": "Shows the type of adjustment made.",
                        "helper": ""
                      },
                      {
                        "name": "Reference / Reason (Display Field)",
                        "desc": "Displays the reference number or reason provided for the adjustment.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier / Customer Name (Display Field)",
                        "desc": "Shows the associated supplier or customer name, if applicable.",
                        "helper": ""
                      },
                      {
                        "name": "Date (Display Field)",
                        "desc": "Displays the date the adjustment was executed.",
                        "helper": ""
                      },
                      {
                        "name": "Details (Display Field)",
                        "desc": "Provides a quick summary of the adjustment (Format: [qty number] items adjusted).",
                        "helper": ""
                      },
                      {
                        "name": "View (Icon)",
                        "desc": "Click to open a detailed summary window of the specific adjustment.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "view-adjustment-summary",
                "title": "View Adjustment Summary",
                "summary": "Read-only summary window of selected adjustment record.",
                "workflow": "Inventory → Adjustments & Transfer → Adjustment History",
                "details": [
                  {
                    "type": "heading",
                    "title": "View Adjustment Summary"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/view_adjustment_summary.png",
                    "alt": "View Adjustment Summary"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Adjustment History"
                  },
                  {
                    "type": "text",
                    "content": "Clicking the View (Icon) opens a window titled \"Stock Adjustment\" which provides a read-only overview of the selected record:"
                  },
                  {
                    "type": "fields",
                    "title": "Summary Details & Items Table",
                    "items": [
                      {
                        "name": "Summary Details",
                        "desc": "Displays the Type, Create Date, and Remarks at the top of the window.",
                        "helper": ""
                      },
                      {
                        "name": "Items Table",
                        "desc": "Displays a table (with sorting options for all fields) detailing the specific items involved in the adjustment. The columns include: Product, Adjusted Quantity, UOM, Batch Number, Location.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Adjustment History",
            "description": "Displays a comprehensive log of stock adjustments."
          },
          {
            "id": "pending-reviews-group",
            "items": [
              {
                "id": "pending-reviews-overview",
                "title": "Pending Reviews",
                "summary": "Search and filter pending stock adjustment reviews.",
                "workflow": "Inventory → Adjustments & Transfer → Pending Reviews",
                "details": [
                  {
                    "type": "heading",
                    "title": "Pending Reviews"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/pending_reviews.png",
                    "alt": "Pending Reviews"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Pending Reviews"
                  },
                  {
                    "type": "text",
                    "content": "The Pending Reviews Dashboard displays a comprehensive list of stock adjustments waiting to be reviewed. It provides various search fields, a total record count, and a detailed table to track and review inventory changes."
                  },
                  {
                    "type": "fields",
                    "title": "Search & Filter Options",
                    "items": [
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select the beginning date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select the ending date for the search range.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier (Input Field)",
                        "desc": "Enter the supplier name to filter the pending reviews.",
                        "helper": ""
                      },
                      {
                        "name": "Customer (Input Field)",
                        "desc": "Enter the customer name to filter the pending reviews.",
                        "helper": ""
                      },
                      {
                        "name": "Invoice Number (Input Field)",
                        "desc": "Enter a specific invoice number to search.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select the adjustment type to filter the list (Options: \"Other Adjustments\", \"Return on Sales\").",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Reset",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset all selected or entered search fields.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Click the Create (Button) to open the form and log a new inventory adjustment. The form displays the title \"Stock Adjustment\" and allows you to add the following details:"
                  },
                  {
                    "type": "fields",
                    "title": "Create Stock Adjustment",
                    "items": [
                      {
                        "name": "Type * (Drop-Down)",
                        "desc": "Select the adjustment type (e.g., \"Other Adjustments\").",
                        "helper": ""
                      },
                      {
                        "name": "Product * (Input Field / Drop-Down)",
                        "desc": "Search and select the specific product to be adjusted.",
                        "helper": ""
                      },
                      {
                        "name": "Batch Number * (Input Field / Drop-Down)",
                        "desc": "Select the specific batch to draw stock from (displays batch number, location, and quantity).",
                        "helper": ""
                      },
                      {
                        "name": "Quantity * (Input Field)",
                        "desc": "Enter the numerical quantity of items to be adjusted.",
                        "helper": ""
                      },
                      {
                        "name": "UOM (Display Field)",
                        "desc": "Automatically fills with the unit of measurement based on the selected product and batch details.",
                        "helper": ""
                      },
                      {
                        "name": "Remarks * (Input Field)",
                        "desc": "Enter any necessary notes, reasons, or context for the adjustment.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Submit",
                    "items": [
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the stock adjustment.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "pending-review-table",
                "title": "Pending Review Record Table",
                "summary": "Displays pending adjustment review records.",
                "workflow": "Inventory → Adjustments & Transfer → Pending Reviews",
                "details": [
                  {
                    "type": "heading",
                    "title": "Pending Review Record Table"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/pending_review_record_table.png",
                    "alt": "Pending Review Record Table"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Adjustments & Transfer → Pending Reviews"
                  },
                  {
                    "type": "fields",
                    "title": "Record Count",
                    "items": [
                      {
                        "name": "Number of count (Display Field)",
                        "desc": "Displays the total number of adjustment records currently listed (e.g., Number of count: 25).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "The main table displays the following fields for each pending review record:"
                  },
                  {
                    "type": "fields",
                    "title": "Record Table Fields",
                    "items": [
                      {
                        "name": "# (Display Field)",
                        "desc": "The serial number or sequence of the record in the table.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Display Field)",
                        "desc": "Shows the type of adjustment made.",
                        "helper": ""
                      },
                      {
                        "name": "Reference / Reason (Display Field)",
                        "desc": "Displays the reference number or reason provided for the adjustment.",
                        "helper": ""
                      },
                      {
                        "name": "Supplier / Customer Name (Display Field)",
                        "desc": "Shows the associated supplier or customer name, if applicable.",
                        "helper": ""
                      },
                      {
                        "name": "Date (Display Field)",
                        "desc": "Displays the date the adjustment was executed.",
                        "helper": ""
                      },
                      {
                        "name": "Details (Display Field)",
                        "desc": "Provides a quick summary of the adjustment (Format: [qty number] items adjusted).",
                        "helper": ""
                      },
                      {
                        "name": "View (Icon)",
                        "desc": "Click to open a detailed summary window of the specific adjustment.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Pending Reviews",
            "description": "Displays list of stock adjustments waiting to be reviewed."
          }
        ],
        "description": "The Adjustments & Transfer Dashboard manages and tracks inventory movements. It encompasses the Transfers module, Adjustment History module, and Pending Review module."
      },
      {
        "id": "master",
        "title": "Master",
        "groups": [
          {
            "id": "Master-Dashboard",
            "items": [
              {
                "id": "Master-Dashboard-Overview",
                "title": "Master Overview",
                "summary": "Master overview settings.",
                "workflow": "Inventory → Master",
                "details": [
                  {
                    "type": "heading",
                    "title": "Master Overview"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Master"
                  },
                  {
                    "type": "text",
                    "content": "The Master module is used to configure foundational settings for the Vet facility, including different locations, stock parameters, and services offered. The module is accessible via a drop-down menu containing three options: Location, Supplier, and Inventory Settings."
                  }
                ]
              }
            ],
            "title": "Master Dashboard",
            "description": "The Master module is used to configure foundational settings for the Vet facility, including different locations, stock parameters, and services offered."
          },
          {
            "id": "location-group",
            "items": [
              {
                "id": "location-management",
                "title": "Location",
                "summary": "Create, search, update, and delete clinic locations.",
                "workflow": "Master → Location → Location",
                "details": [
                  {
                    "type": "heading",
                    "title": "Location"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/location_dashboard.png",
                    "alt": "Location Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_location.png",
                    "alt": "Create Location"
                  },
                  {
                    "type": "workflow",
                    "content": "Master → Location → Location"
                  },
                  {
                    "type": "text",
                    "content": "This module is used to create and manage clinic locations and their associated categories. It contains two sub-modules: Location and Category."
                  },
                  {
                    "type": "fields",
                    "title": "Search Options",
                    "items": [
                      {
                        "name": "Search Bar (Input Field)",
                        "desc": "Enter a name to search for a specific location.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Location",
                    "items": [
                      {
                        "name": "Location Name * (Input Field)",
                        "desc": "Enter the name of the new location.",
                        "helper": ""
                      },
                      {
                        "name": "Location Type * (Drop-Down)",
                        "desc": "Select the classification of the location (Options: \"warehouse\", \"storage\", \"consumption\").",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the new location.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“location added successfully” appears after clicking Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Location Record Table",
                    "items": [
                      {
                        "name": "Location Name (Display Field)",
                        "desc": "Shows the designated name of the location.",
                        "helper": ""
                      },
                      {
                        "name": "Location Type (Display Field)",
                        "desc": "Displays the assigned type (e.g., warehouse, storage).",
                        "helper": ""
                      },
                      {
                        "name": "Category (Display Field)",
                        "desc": "Shows the assigned category for the location.",
                        "helper": ""
                      },
                      {
                        "name": "Edit (Icon)",
                        "desc": "Click to open a window to update the location name. Click the Update (Button) to save changes.\n\n\nSystem Notification: “location updated successfully” appears after updating.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to remove the location.\n\n\nConfirmation Pop-Up: A window will appear asking, “Are you sure you want to delete the supplier?” Click Yes to confirm deletion or No to cancel.\n\n\nSystem Notification: “location removed successfully” appears after confirming deletion.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "category-management",
                "title": "Category",
                "summary": "Create and manage custom location categories.",
                "workflow": "Master → Location → Category",
                "details": [
                  {
                    "type": "heading",
                    "title": "Category"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/category_dashboard.png",
                    "alt": "Category Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_category.png",
                    "alt": "Create Category"
                  },
                  {
                    "type": "workflow",
                    "content": "Master → Location → Category"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module allows the clinic to create and manage custom location categories."
                  },
                  {
                    "type": "fields",
                    "title": "Search Options",
                    "items": [
                      {
                        "name": "Search Bar (Input Field)",
                        "desc": "Enter a name to search for a specific category.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Category",
                    "items": [
                      {
                        "name": "Category Name * (Input Field)",
                        "desc": "Enter the name of the new category.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the new category.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“category added successfully” appears after clicking Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Category Record Table",
                    "items": [
                      {
                        "name": "Name (Display Field)",
                        "desc": "Shows the designated name of the category.",
                        "helper": ""
                      },
                      {
                        "name": "Edit (Icon)",
                        "desc": "Click to open a window to update the category name. Click the Update (Button) to save changes.\n\n\nSystem Notification: “category updated successfully” appears after updating.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to remove the category.\n\n\nConfirmation Pop-Up: A window will appear asking, “Are you sure you want to delete the category?” Click Yes to confirm deletion or No to cancel.\n\n\nSystem Notification: “category removed successfully” appears after confirming deletion.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Location",
            "description": "Create and manage clinic locations and categories."
          },
          {
            "id": "supplier-group",
            "items": [
              {
                "id": "supplier-management",
                "title": "Supplier",
                "summary": "Create, search, update, and delete supplier records.",
                "workflow": "Master → Supplier",
                "details": [
                  {
                    "type": "heading",
                    "title": "Supplier"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/supplier_dashboard.png",
                    "alt": "Supplier Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/create_supplier.png",
                    "alt": "Create Supplier"
                  },
                  {
                    "type": "workflow",
                    "content": "Master → Supplier"
                  },
                  {
                    "type": "text",
                    "content": "The Supplier Dashboard is used to manage and maintain the contact, tax, and business details of all vendors and suppliers associated with the clinic."
                  },
                  {
                    "type": "fields",
                    "title": "Search Options",
                    "items": [
                      {
                        "name": "Search Bar (Input Field)",
                        "desc": "Enter a name to search for a specific supplier.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Supplier",
                    "items": [
                      {
                        "name": "Supplier Name * (Input Field)",
                        "desc": "Enter the registered name of the supplier or vendor.",
                        "helper": ""
                      },
                      {
                        "name": "Contact Person (Input Field)",
                        "desc": "Enter the name of the primary contact person for this supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Email (Input Field)",
                        "desc": "Enter the official email address for the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Phone number * (Input Field)",
                        "desc": "Enter the primary contact phone number for the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "GST number (Input Field)",
                        "desc": "Enter the Goods and Services Tax (GST) identification number for billing and tax purposes.",
                        "helper": ""
                      },
                      {
                        "name": "Address (Input Field)",
                        "desc": "Enter the physical or billing address of the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the new supplier record.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“Supplier added successfully” appears after clicking Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Supplier Record Table",
                    "items": [
                      {
                        "name": "Supplier Name (Display Field)",
                        "desc": "Shows the registered name of the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Contact Person (Display Field)",
                        "desc": "Displays the designated contact person.",
                        "helper": ""
                      },
                      {
                        "name": "Email ID (Display Field)",
                        "desc": "Shows the email address of the supplier.",
                        "helper": ""
                      },
                      {
                        "name": "Phone Number (Display Field)",
                        "desc": "Displays the contact phone number.",
                        "helper": ""
                      },
                      {
                        "name": "Address (Display Field)",
                        "desc": "Shows the physical or billing address.",
                        "helper": ""
                      },
                      {
                        "name": "Edit (Icon)",
                        "desc": "Click to open a window to update the supplier's details. Click the Update (Button) to save changes.\n\n\nSystem Notification: “supplier updated successfully” appears after updating.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to permanently remove the supplier from the system.\n\n\nConfirmation Pop-Up: A window will appear asking, “Are you sure you want to delete the supplier?” Click Yes to confirm deletion or No to cancel.\n\n\nSystem Notification: “supplier removed successfully” appears after confirming deletion.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Supplier",
            "description": "Manage and maintain contact, tax, and business details of vendors and suppliers."
          },
          {
            "id": "inventory-settings-group",
            "items": [
              {
                "id": "stock-categories",
                "title": "Stock (Categories)",
                "summary": "Create and manage broad stock categories.",
                "workflow": "Master → Inventory Settings → Stock",
                "details": [
                  {
                    "type": "heading",
                    "title": "Stock (Categories)"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/stock_categories.png",
                    "alt": "Stock Categories"
                  },
                  {
                    "type": "workflow",
                    "content": "Master → Inventory Settings → Stock"
                  },
                  {
                    "type": "text",
                    "content": "The Inventory Settings Dashboard is divided into two distinct modules: Stock and Services. This dashboard is used to configure and manage the foundational categories for the clinic's inventory and service offerings.\n\nThis module allows clinic staff to create and manage broad stock categories to keep inventory items organized. It includes a search bar and a comprehensive management table."
                  },
                  {
                    "type": "fields",
                    "title": "Search Options",
                    "items": [
                      {
                        "name": "Search Bar (Input Field)",
                        "desc": "Enter a name to search for a specific stock category.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Stock Category",
                    "items": [
                      {
                        "name": "Name (Input Field)",
                        "desc": "Enter the name of the new stock category.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the new stock category.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“stock created” appears after clicking Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Stock Category Record Table",
                    "items": [
                      {
                        "name": "Category (Display Field)",
                        "desc": "Shows the designated name of the stock category.",
                        "helper": ""
                      },
                      {
                        "name": "Edit (Icon)",
                        "desc": "Click to open a window to update the category name. Click the Update (Button) to save changes.\n\n\nSystem Notification: “category updated successfully” appears after updating.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to permanently remove the stock category from the system.\n\n\nConfirmation Pop-Up: A window will appear asking, “Are you sure you want to delete the category?” Click Yes to confirm deletion or No to cancel.\n\n\nSystem Notification: “category removed successfully” appears after confirming deletion.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "services-categories",
                "title": "Services (Categories)",
                "summary": "Create and manage broad service categories (Medical/Non-Medical).",
                "workflow": "Master → Inventory Settings → Services",
                "details": [
                  {
                    "type": "heading",
                    "title": "Services (Categories)"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/services_categories.png",
                    "alt": "Services Categories"
                  },
                  {
                    "type": "workflow",
                    "content": "Master → Inventory Settings → Services"
                  },
                  {
                    "type": "text",
                    "content": "This module allows clinic staff to create and manage broad service categories, sorting them by medical or non-medical classifications. It includes a search bar and a comprehensive management table."
                  },
                  {
                    "type": "fields",
                    "title": "Search Options",
                    "items": [
                      {
                        "name": "Search Bar (Input Field)",
                        "desc": "Enter a name to search for a specific service category.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Create Service Category",
                    "items": [
                      {
                        "name": "Name (Input Field)",
                        "desc": "Enter the name of the new service category.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select the classification of the service category (Options: \"Medical\" or \"Non-Medical\").",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to finalize and create the new service category.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "notification",
                    "title": "System Notification",
                    "content": "“service created” appears after clicking Submit."
                  },
                  {
                    "type": "fields",
                    "title": "Service Category Record Table",
                    "items": [
                      {
                        "name": "Category (Display Field)",
                        "desc": "Shows the designated name of the service category.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Display Field)",
                        "desc": "Displays the classification of the category (\"Medical\" or \"Non-Medical\").",
                        "helper": ""
                      },
                      {
                        "name": "Edit (Icon)",
                        "desc": "Click to open a window to update the category name or type. Click the Update (Button) to save changes.\n\n\nSystem Notification: “category updated successfully” appears after updating.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to permanently remove the service category from the system.\n\n\nConfirmation Pop-Up: A window will appear asking, “Are you sure you want to delete the category?” Click Yes to confirm deletion or No to cancel.\n\n\nSystem Notification: “category removed successfully” appears after confirming deletion.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Inventory Settings",
            "description": "Configure foundational categories for stock and services."
          }
        ],
        "description": "Configure foundational settings for Vet facility: Location, Supplier, and Inventory Settings."
      },
      {
        "id": "download-reports",
        "title": "Download Reports",
        "groups": [
          {
            "id": "Download-Reports-Dashboard",
            "items": [
              {
                "id": "Download-Reports-Dashboard-Overview",
                "title": "Download Reports Dashboard Overview",
                "summary": "The Download Reports module contains the \"Stock Transaction History\" option. This allows clinic staff to generate, filter, and download detailed logs of all inventory movements for specific products over a defined period.",
                "workflow": "Inventory → Download Reports → Stock Transaction History",
                "details": [
                  {
                    "type": "heading",
                    "title": "Download Reports Dashboard Overview"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/download_reports_dashboard.png",
                    "alt": "Download Reports Dashboard"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/download-reports-video.mp4",
                    "poster": "/assets/images/knowledgebase/download_reports_video.jpg",
                    "alt": "Download Reports Video Guide"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Download Reports → Stock Transaction History"
                  },
                  {
                    "type": "text",
                    "content": "The Download Reports module contains the \"Stock Transaction History\" option. This allows clinic staff to generate, filter, and download detailed logs of all inventory movements for specific products over a defined period."
                  }
                ]
              }
            ],
            "title": "Download Reports Dashboard",
            "description": "The Download Reports module contains the \"Stock Transaction History\" option. "
          },
          {
            "id": "stock-transaction-history-group",
            "items": [
              {
                "id": "generate-report-form",
                "title": "Generate Report Form",
                "summary": "Parameter window to generate specific stock transaction report.",
                "workflow": "Inventory → Download Reports → Stock Transaction History",
                "details": [
                  {
                    "type": "heading",
                    "title": "Generate Report Form"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Download Reports → Stock Transaction History"
                  },
                  {
                    "type": "text",
                    "content": "Click the Stock Transaction History (Option) from the drop-down menu to open the initial parameter window. The form allows you to add the following details to generate the report:"
                  },
                  {
                    "type": "fields",
                    "title": "Generate Report Form Fields",
                    "items": [
                      {
                        "name": "Stock Name * (Input Field / Drop-Down)",
                        "desc": "Search and select the name of the specific stock product.",
                        "helper": ""
                      },
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Select the beginning date for the transaction search.",
                        "helper": ""
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Select the ending date for the transaction search.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Select the nature of the transactions to include (Options: \"All\", \"Sales\", \"Purchases\", \"Adjustments\", \"Returns\").",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Generate",
                    "items": [
                      {
                        "name": "Generate (Button)",
                        "desc": "After the parameters are selected, click to generate the specific stock report and open the detailed view window.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              },
              {
                "id": "detailed-report-filters",
                "title": "Detailed Report View & Advanced Filters",
                "summary": "Advanced filtering, data summary, and transaction logs.",
                "workflow": "Inventory → Download Reports → Stock Transaction History",
                "details": [
                  {
                    "type": "heading",
                    "title": "Detailed Report View & Advanced Filters"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/detailed_report_view.png",
                    "alt": "Detailed Report View & Advanced Filters"
                  },
                  {
                    "type": "workflow",
                    "content": "Inventory → Download Reports → Stock Transaction History"
                  },
                  {
                    "type": "text",
                    "content": "Upon clicking \"Generate\", a new window titled \"Stock Transaction History\" opens, providing advanced filtering options, a data summary, and a detailed transaction table."
                  },
                  {
                    "type": "fields",
                    "title": "Advanced Search & Filter Fields:",
                    "items": [
                      {
                        "name": "Start Date (Calendar Input)",
                        "desc": "Contains the previously selected date data, but allows manual editing to refine the search.",
                        "helper": ""
                      },
                      {
                        "name": "End Date (Calendar Input)",
                        "desc": "Contains the previously selected date data, but allows manual editing to refine the search.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Drop-Down)",
                        "desc": "Allows adjustment of the transaction type (Options: \"All\", \"Sales\", \"Purchases\", \"Adjustments\", \"Returns\").",
                        "helper": ""
                      },
                      {
                        "name": "Supplier Name (Input Field)",
                        "desc": "Enter a supplier name to narrow the transaction results.",
                        "helper": ""
                      },
                      {
                        "name": "Customer Name (Input Field)",
                        "desc": "Enter a customer name to narrow the transaction results.",
                        "helper": ""
                      },
                      {
                        "name": "Pet Name (Input Field)",
                        "desc": "Enter the specific pet's name to filter related transactions.",
                        "helper": ""
                      },
                      {
                        "name": "Location (Drop-Down)",
                        "desc": "Select a specific clinic location to filter the results.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Reset (Button)",
                        "desc": "Click to clear and reset the advanced filter fields.",
                        "helper": ""
                      },
                      {
                        "name": "Download (Button)",
                        "desc": "Click to download the generated transaction history as an Excel file.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Summary Display",
                    "items": [
                      {
                        "name": "Summary",
                        "desc": "Above the table, the system provides a quick numerical summary of the selected stock item (e.g., Bravecto Chewable Tablet 112.5 mg 1 Chewable Tablet / Fluralaner Chew 112.5 mg):",
                        "helper": ""
                      },
                      {
                        "name": "Current Stock Qty (Display Field)",
                        "desc": "Shows the present available stock (e.g., 13).",
                        "helper": ""
                      },
                      {
                        "name": "Total Purchase Qty (Display Field)",
                        "desc": "Shows the total quantity purchased during the selected timeframe (e.g., 0).",
                        "helper": ""
                      },
                      {
                        "name": "Total Sales Qty (Display Field)",
                        "desc": "Shows the total quantity sold during the selected timeframe (e.g., 1).",
                        "helper": ""
                      },
                      {
                        "name": "Number of Records (Display Field)",
                        "desc": "Shows the total number of transactions found based on the filters (e.g., 6).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Transaction Record Table",
                    "items": [
                      {
                        "name": "Date (Display Field)",
                        "desc": "Shows the exact date of the transaction.",
                        "helper": ""
                      },
                      {
                        "name": "Type (Display Field)",
                        "desc": "Displays the type of transaction (e.g., Sale, Purchase, Adjustment).",
                        "helper": ""
                      },
                      {
                        "name": "Customer/Supplier Name (Display Field)",
                        "desc": "Shows the associated entity for the transaction.",
                        "helper": ""
                      },
                      {
                        "name": "Invoice No (Display Field)",
                        "desc": "Displays the relevant invoice or receipt number.",
                        "helper": ""
                      },
                      {
                        "name": "Batch Number (Display Field)",
                        "desc": "Shows the specific batch number of the product involved.",
                        "helper": ""
                      },
                      {
                        "name": "Qty Sold/Purchased (Display Field)",
                        "desc": "Displays the numerical quantity moved in this specific transaction.",
                        "helper": ""
                      },
                      {
                        "name": "MRP (Display Field)",
                        "desc": "Shows the Maximum Retail Price of the item at the time of the transaction.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Stock Transaction History",
            "description": "Generate and view stock transaction logs over defined periods."
          }
        ],
        "description": "Generate, filter, and download detailed logs of all inventory movements for specific products."
      }
    ],
    "description": "The Inventory Dashboard functions as the clinic’s inventory management system, providing tools to upload inventory, monitor inventory levels, manage items and medications, and maintain an organized list of services."
  },
  {
    "id": "plan",
    "title": "Plan Module",
    "topics": [
      {
        "id": "topic-Plan",
        "title": "Plan Module Overview",
        "groups": [
          {
            "id": "group-Plan",
            "items": [
              {
                "id": "item-1789119673887",
                "title": "Plan Overview",
                "details": [
                  {
                    "type": "text",
                    "content": "The Plan module provides administrators and authorized staff with the tools to create, configure, and manage standard operational forms and templates. This module is divided into six key sub-modules: Consent Forms, Protocol Forms, Parameters, Diagnostic Template, Reminder Template, and Certificates."
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Plan Module Overview",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Consent-Form",
        "title": "Consent Form",
        "groups": [
          {
            "id": "group-Consent-Form",
            "items": [
              {
                "id": "item-Consent-Form",
                "title": "Consent Form",
                "details": [
                  {
                    "type": "heading",
                    "title": "Consent Forms"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-consent-form-01.png",
                    "alt": "Consent Forms Dashboard"
                  },
                  {
                    "type": "workflow",
                    "content": "Plan → Consent Form"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module enables the clinic to create and manage legal and medical consent forms for various procedures."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for an existing form by its name.",
                        "name": "Search Field",
                        "helper": ""
                      }
                    ],
                    "title": "Search Field"
                  },
                  {
                    "type": "text",
                    "content": "The dashboard displays all saved consent forms in a table with the following fields:"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the form name.",
                        "name": "Name (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the service type.",
                        "name": "Service Type (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the creator's name.",
                        "name": "Created By (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays the last updated date.",
                        "name": "Last Updated (Display Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens the form to make changes.",
                        "name": "Edit (Icon)",
                        "helper": ""
                      },
                      {
                        "desc": "Triggers a confirmation window (\"Are you sure you want to delete the form?\"). Clicking Yes removes the form.",
                        "name": "Delete (Icon)",
                        "helper": ""
                      }
                    ],
                    "title": "Table Layout"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Creating-Consent-Form",
                "title": "Creating a Consent Form",
                "details": [
                  {
                    "type": "heading",
                    "title": "Create Consent Form"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-consent-form-02.png",
                    "alt": "Create Consent Form"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Clicking the Create button opens a setup window with the following configuration fields:",
                        "name": "Create (button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the title of the consent form.",
                        "name": "Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the category (Options: Grooming, Lab tests, Surgery, Vaccination, Others).",
                        "name": "Service Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Link specific clinic services directly to this consent form (and vice versa).",
                        "name": "Linked Services (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "A large text area to write or paste the detailed legal content of the consent form.",
                        "name": "Content (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Automatically includes placeholder texts for \"Signature of pet parent\" and \"Signature of licensed veterinarian\".",
                        "name": "Signatures (Display Texts)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Consent Form"
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons (Create Window)",
                    "items": [
                      {
                        "desc": "Opens a window displaying how the final form will look, accompanied by a Print button.",
                        "name": "Preview (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to save the new consent form to the system.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ]
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Consent Form",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Protocol-Forms",
        "title": "Protocol Forms",
        "groups": [
          {
            "id": "group-Protocol-Forms",
            "items": [
              {
                "id": "item-Protocol-Forms",
                "title": "Protocol Forms",
                "details": [
                  {
                    "type": "heading",
                    "title": "Protocol Forms"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-protocol-form-01.png",
                    "alt": "Protocol Forms Dashboard"
                  },
                  {
                    "type": "workflow",
                    "content": "Plan → Protocol Form"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module enables the creation of custom, step-by-step treatment or assessment protocols."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for an existing protocol form by its name.",
                        "name": "Search Field",
                        "helper": ""
                      }
                    ],
                    "title": "Search Field"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Table Layout Displays saved protocols with fields for: Name, Species, Created By, Last Updated, alongside Edit and Delete icons.",
                        "name": "Display Fields",
                        "helper": ""
                      }
                    ],
                    "title": "Table Layout"
                  }
                ],
                "summary": "",
                "workflow": ""
              },
              {
                "id": "item-Creating-Protocol-Form",
                "title": "Creating a Protocol Form",
                "details": [
                  {
                    "type": "text",
                    "content": "Create Protocol Form : Clicking the Create button initiates a two-step creation process:"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-protocol-form-02.png",
                    "alt": "Protocol Form Builder - Step 1"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the protocol title.",
                        "name": "Name (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Select the applicable animal species.",
                        "name": "Species (Drop-Down with Checkboxes)*",
                        "helper": ""
                      },
                      {
                        "desc": "Select the patient context (Options: Outpatient, Inpatient).",
                        "name": "Type (Drop-Down)*",
                        "helper": ""
                      },
                      {
                        "desc": "Enter a brief overview of the protocol's purpose.",
                        "name": "Description (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Question 1 (Input Field) : Enter the prompt or question.\nAnswer Type (Drop-Down) : Define how the staff will answer (Options: Checkboxes, File upload, Multiple choice, Long text, Short text).\nDelete (Icon) : Removes the specific question.",
                        "name": "Custom Treatment Protocol Form (Builder Section)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to add another question to the protocol.",
                        "name": "+ Add (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Proceeds to Step 2.",
                        "name": "Next (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 1: Configuration & Builder"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-protocol-form-03.png",
                    "alt": "Protocol Form Review & Submit - Step 2"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays a summary of the Name, Species, Service Type, and Description selected in Step 1.",
                        "name": "Review Header",
                        "helper": ""
                      },
                      {
                        "desc": "Displays a live preview of the questions and answer fields.",
                        "name": "Preview",
                        "helper": ""
                      },
                      {
                        "desc": "Return to Step 1 to make edits.",
                        "name": "Back (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize. A pop-up confirms \"Treatment plan created successfully!\".",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 2: Review & Submit"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Protocol Forms",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Parameters ",
        "title": "Parameters",
        "groups": [
          {
            "id": "group-Parameters ",
            "items": [
              {
                "id": "item-Parameters",
                "title": "Parameters",
                "details": [
                  {
                    "type": "heading",
                    "title": "Parameters"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-parameters-01.png",
                    "alt": "Parameters Group Management"
                  },
                  {
                    "type": "workflow",
                    "content": "Plan → Parameters"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module manages specific medical measurement parameters, organized logically into Groups.\n"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for a specific parameter group by name.",
                        "name": "Search Field",
                        "helper": ""
                      },
                      {
                        "desc": "Opens a window to enter a Group Name (Input Field) and click Add.",
                        "name": "Create Group (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Displays S.No, Group Name, Created By, Last Updated, alongside Edit/Delete icons.",
                        "name": "Groups Table",
                        "helper": ""
                      },
                      {
                        "desc": "The Group Name is clickable. Clicking it opens the dedicated Parameters page for that specific group.",
                        "name": "Action: Group Name (Click)",
                        "helper": ""
                      }
                    ],
                    "title": "Group Management"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-parameters-02.png",
                    "alt": "Create Group Window"
                  },
                  {
                    "type": "text",
                    "content": "Parameters (Inside a Group) Clicking Add on a group's parameter page opens the \"Add Parameter\" window."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name of the metric (e.g., Heart Rate).",
                        "name": "Parameter Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Select the data format (Options: Numeric, Text, Drop-down).\n\n\nDynamic Field: If Numeric is selected, an input field for Units appears, along with an Add Reference button. Clicking this reveals fields for Species (Drop-Down), Lower Range (Input Field), and Upper Range (Input Field).",
                        "name": "Input Type (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Assign or re-assign the parameter to specific groups.",
                        "name": "Group Name (Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Saves the parameter to the group.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Add Parameter window"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-parameters-03.png",
                    "alt": "Add Parameter Window"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the parameters associated with the group with the following fields: Parameter Name, Unit, Input Type, Reference Range (Species), Group, Created By, Created Date, and Edit/Delete Action Buttons.",
                        "name": "Parameters Table Fields",
                        "helper": ""
                      }
                    ],
                    "title": "Parameters Table"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Parameters ",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Diagnostic-Template",
        "title": " Diagnostic Template",
        "groups": [
          {
            "id": "group-Diagnostic-Template",
            "items": [
              {
                "id": "item-Diagnostic-Template",
                "title": "Diagnostic Template",
                "details": [
                  {
                    "type": "heading",
                    "title": "Diagnostic Template"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-diagnostic-template-01.png",
                    "alt": "Diagnostic Template Dashboard"
                  },
                  {
                    "type": "workflow",
                    "content": "Plan →  Diagnostic Template"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module allows staff to build standardized reporting templates for lab work and diagnostics."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for an existing diagnostic template.",
                        "name": "Search Field",
                        "helper": ""
                      }
                    ],
                    "title": "Search Field"
                  },
                  {
                    "type": "text",
                    "content": "Clicking the Create button initiates a multi-step process."
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Clicking the Create button initiates a multi-step process.",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-diagnostic-template-02.png",
                    "alt": "Diagnostic Template - Step 1 Setup"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the title.",
                        "name": "Template Name (Input Field)*",
                        "helper": ""
                      },
                      {
                        "desc": "Select the template format (Options: Parameter, Report).",
                        "name": "Type (Drop-Down)*",
                        "helper": ""
                      },
                      {
                        "desc": "Connect the template to specific diagnostic services.",
                        "name": "Linked Service (Drop-Down with Checkboxes)",
                        "helper": ""
                      },
                      {
                        "desc": "Proceeds to Step 2.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 1: Setup"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-diagnostic-template-03.png",
                    "alt": "Diagnostic Template - Step 2 Add Fields"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the Template Name, Type, and a sub-tag showing the Linked Service(s) from Step 1.",
                        "name": "Header",
                        "helper": ""
                      },
                      {
                        "desc": "Click to insert standard text/data fields.\n",
                        "name": "Add Field (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to insert specific medical parameter groups.",
                        "name": "Add Parameter (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Proceeds to Review.",
                        "name": "Next (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 2: Add Fields"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-diagnostic-template-04.png",
                    "alt": "Diagnostic Template - Step 3 Review & Submit"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Displays the finalized look of the diagnostic template.",
                        "name": "Preview",
                        "helper": ""
                      },
                      {
                        "desc": "Return to the previous step.",
                        "name": "Back (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to finalize. A pop-up confirms \"Template created successfully!\".",
                        "name": "Submit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Step 3: Review & Submit"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Table Layout Displays saved templates with fields for: Name, Created By, Linked Services, Last Update, and an Edit button.",
                        "name": "Display Field",
                        "helper": ""
                      }
                    ],
                    "title": "Table Layout"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": " Diagnostic Template",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Reminder-Template",
        "title": "Reminder Template",
        "groups": [
          {
            "id": "group-Reminder-Template",
            "items": [
              {
                "id": "item-Reminder-Template",
                "title": "Reminder Template",
                "details": [
                  {
                    "type": "heading",
                    "title": "Reminder Template"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-reminder-template-01.png",
                    "alt": "Reminder Template Dashboard"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/plan-reminder-template-video.mp4",
                    "poster": "/assets/images/knowledgebase/plan-reminder-template-media-03.jpg",
                    "alt": "Reminder Template Video Guide"
                  },
                  {
                    "type": "workflow",
                    "content": "Plan →  Reminder Template"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module configures automated, chained follow-up schedules (e.g., vaccine boosters).\n"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Search for an existing reminder sequence.\n",
                        "name": "Search Field",
                        "helper": ""
                      }
                    ],
                    "title": "Search Field"
                  },
                  {
                    "type": "buttons",
                    "items": [
                      {
                        "desc": "Clicking the Create button opens the configuration window.",
                        "name": "Create (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Create"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Enter the name of the reminder sequence.",
                        "name": "Template Name (Input Field)",
                        "helper": ""
                      },
                      {
                        "desc": "Connect the reminder to a specific clinic service.",
                        "name": "Linked Service (Input Field with Drop-Down)",
                        "helper": ""
                      },
                      {
                        "desc": "Define the terminology (Default text: \"dose\").",
                        "name": "What do you call one occurrence? (Input Field)",
                        "helper": ""
                      }
                    ],
                    "title": "Create Reminder Template"
                  },
                  {
                    "type": "text",
                    "content": "Steps — Chained Configuration Section\nThis section calculates due dates dynamically once the previous step is completed."
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-reminder-template-02.png",
                    "alt": "Create Reminder Template - Chained Configuration"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "\"Completed at time of service — this is the starting point the rest of the chain is calculated from. No reminder needed here; it is part of the visit itself.\"",
                        "name": "1. Dose 1 (Display Text)",
                        "helper": ""
                      },
                      {
                        "desc": "Click to add subsequent chained reminders (e.g., Dose 2). This reveals dynamic configuration fields:\n\n\nDue (Input Field) : \"[ X ] days after the previous step’s completed date.\"\n\n\nRemind (Input Field) : \"[ X ] days before due, then every...\"\n\n\nInterval (Input Field) : \"...[ X ] days, until completed or...\"\n\n\nCut-off (Input Field) : \"...[ X ] days after due — whichever comes first.\"",
                        "name": "Add Dose (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Deletes this specific dose step.",
                        "name": "Remove (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Finalizes the automated reminder chain.",
                        "name": "Save (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Steps — Chained Configuration Section"
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Table Layout Displays active templates with fields for: Name, Linked Services, Occurrence, Last Updated, and Edit/Delete buttons.",
                        "name": "Display Fields",
                        "helper": ""
                      }
                    ],
                    "title": "Table Layout"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Reminder Template",
            "description": ""
          }
        ],
        "description": ""
      },
      {
        "id": "topic-Certificates",
        "title": "Certificates",
        "groups": [
          {
            "id": "group-1789124791916",
            "items": [
              {
                "id": "item-Certificates",
                "title": "Certificates",
                "details": [
                  {
                    "type": "heading",
                    "title": "Certificates"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/plan-certificates-01.png",
                    "alt": "Certificates Dashboard"
                  },
                  {
                    "type": "workflow",
                    "content": "Plan →  Certificates"
                  },
                  {
                    "type": "text",
                    "content": "This sub-module houses the clinic's standardized, official medical certificates (e.g., spay/neuter certificates, travel health certificates)."
                  },
                  {
                    "type": "fields",
                    "items": [
                      {
                        "desc": "Opens the certificate in a preview window, providing secondary buttons to Print or Download as PDF.",
                        "name": "Preview (Button)",
                        "helper": ""
                      },
                      {
                        "desc": "Opens the certificate template in an editable view. Includes a Save button to apply any permanent modifications to the standard certificate.",
                        "name": "Edit (Button)",
                        "helper": ""
                      }
                    ],
                    "title": "Record Action Buttons"
                  }
                ],
                "summary": "",
                "workflow": ""
              }
            ],
            "title": "Certificates",
            "description": ""
          }
        ],
        "description": ""
      }
    ],
    "description": "Tools for administrators to create, configure, and manage standard operational forms and templates."
  },
  {
    "id": "support",
    "title": "Support Module",
    "topics": [
      {
        "id": "support-tickets-topic",
        "title": "Support Tickets",
        "groups": [
          {
            "id": "support-tickets-group",
            "items": [
              {
                "id": "item-Support-Overview",
                "title": "Support Overview",
                "summary": "",
                "workflow": "",
                "details": [
                  {
                    "type": "heading",
                    "title": "Support Dashboard"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/support-ticket-01.png",
                    "alt": "Support Dashboard"
                  },
                  {
                    "type": "text",
                    "content": "The Support module allows all users to report, track, and manage tickets regarding any system-related issues or inquiries they experience while using the software."
                  }
                ]
              },
              {
                "id": "create-support-ticket",
                "title": "Creating & Tracking Support Tickets",
                "summary": "Categorize system issues, attach screenshots, and track query cards across Active and Closed sections.",
                "workflow": "Support → Create ticket → Fill Details → Submit",
                "details": [
                  {
                    "type": "heading",
                    "title": "Creating a Ticket"
                  },
                  {
                    "type": "heading",
                    "title": "Video Guide"
                  },
                  {
                    "type": "video",
                    "src": "/assets/images/knowledgebase/support-creating-ticket-video.mp4",
                    "poster": "/assets/images/knowledgebase/support-video-guide.jpg",
                    "alt": "Support Video Guide"
                  },
                  {
                    "type": "text",
                    "content": "To report an issue, click the Create ticket button. This opens the \"Create Ticket\" setup window with the following fields to help categorize and explain the problem:"
                  },
                  {
                    "type": "buttons",
                    "title": "Create Ticket",
                    "items": [
                      {
                        "name": "Create Ticket (Button)",
                        "desc": "Click to open the \"Create Ticket\" setup window.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/support-ticket-02.png",
                    "alt": "Create Ticket Setup Window"
                  },
                  {
                    "type": "fields",
                    "title": "Ticket Details",
                    "items": [
                      {
                        "name": "Category (Drop-Down)",
                        "desc": "Select the primary system area related to your issue (Options include: Login & Account, Appointments & Consultation, Pet & Client Records, EMR & Medical Records, Billing & Payments, Inventory & Pharmacy, Diagnostics, Reports & Documents, etc.).",
                        "helper": ""
                      },
                      {
                        "name": "Reason (Drop-Down)",
                        "desc": "Select the specific issue. Note: The available options here will populate dynamically based on the Category selected above.",
                        "helper": ""
                      },
                      {
                        "name": "Problem Type (Drop-Down)",
                        "desc": "Select the classification of the issue (Options: I can't do something, Something is not working, Something is incorrect, Something is missing, I don't know how to do something, I need a change/enhancement).",
                        "helper": ""
                      },
                      {
                        "name": "Description (Input Field)",
                        "desc": "Enter a detailed explanation of the problem, including any steps leading up to the error.",
                        "helper": ""
                      },
                      {
                        "name": "Attachment (Images Only) (Select Field)",
                        "desc": "Upload relevant screenshots or images to help the support team understand the issue.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Action Buttons",
                    "items": [
                      {
                        "name": "Cancel (Button)",
                        "desc": "Click to discard the ticket and close the window without saving.",
                        "helper": ""
                      },
                      {
                        "name": "Submit (Button)",
                        "desc": "Click to officially raise the ticket to the support team.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/support-ticket-01.png",
                    "alt": "Support Ticket Tracking & Display Cards"
                  },
                  {
                    "type": "text",
                    "content": "The main dashboard is divided into two distinct sections to help you track the progress of your queries:"
                  },
                  {
                    "type": "fields",
                    "title": "Ticket Tracking Sections",
                    "items": [
                      {
                        "name": "Active (Section)",
                        "desc": "Lists all currently raised queries that are open and in the process of being investigated or responded to by the support team.",
                        "helper": ""
                      },
                      {
                        "name": "Closed (Section)",
                        "desc": "Lists all past queries that have been successfully resolved, responded to and closed.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "content": "Whether in the Active or Closed section, each individual support ticket displays a summary card with the following fields:"
                  },
                  {
                    "type": "fields",
                    "title": "Ticket Display Information",
                    "items": [
                      {
                        "name": "Ticket Type (Display Field)",
                        "desc": "Shows the overarching category of the issue (e.g., Login & Account).",
                        "helper": ""
                      },
                      {
                        "name": "Ticket ID (Display Field)",
                        "desc": "Displays the unique tracking number generated for the query (e.g., Ticket_20260904103925).",
                        "helper": ""
                      },
                      {
                        "name": "Status (Display Field)",
                        "desc": "Shows the current operational state of the ticket (e.g., OPEN or CLOSED).",
                        "helper": ""
                      },
                      {
                        "name": "Tag (Display Field)",
                        "desc": "Displays a \"RESPONSE RECEIVED\" tag. Note: This tag is only visible on tickets currently in the Active section when the support team has replied.",
                        "helper": ""
                      },
                      {
                        "name": "Description (Display Field)",
                        "desc": "Shows a brief preview of the text entered when the ticket was created.",
                        "helper": ""
                      },
                      {
                        "name": "Date & Time (Display Field)",
                        "desc": "Displays exactly when the ticket was originally submitted (e.g., 04 Sep 2026, 10:39 AM).",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ],
            "title": "Ticket Management",
            "description": "Issue reporting and query progress tracking."
          }
        ],
        "description": "Ticket creation and Active/Closed status tracking."
      }
    ],
    "description": "Report, track, and manage tickets regarding system-related issues or feature inquiries."
  },
  {
    "id": "general-settings",
    "title": "Global Settings",
    "description": "Configure clinic-wide data sharing, billing, refund approval rules, consultation, inventory approvals, and role permissions from Global Settings.",
    "topics": [
      {
        "id": "topic-general-data-sharing",
        "title": "General & Data Sharing",
        "description": "Control how clinical and inventory data is shared between HQ and branches.",
        "groups": [
          {
            "id": "group-general-data-sharing",
            "title": "Data Sharing Controls",
            "description": "Independent toggles for pets, vets, plan, stock, and services sharing.",
            "items": [
              {
                "id": "general-data-sharing-overview",
                "title": "General & Data Sharing",
                "summary": "Control how specific clinical and inventory data is distributed between the main clinic and associated branches.",
                "workflow": "Profile → View Account → Global Settings → General & Data Sharing",
                "details": [
                  {
                    "type": "heading",
                    "title": "General & Data Sharing"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-data-sharing.png",
                    "alt": "General & Data Sharing Settings"
                  },
                  {
                    "type": "text",
                    "content": "The General & Data Sharing section allows administrators to control how specific clinical and inventory data is distributed between the main clinic and its associated branches."
                  },
                  {
                    "type": "text",
                    "content": "Important Note: This section is only available to the HQ branch or Super Admin."
                  },
                  {
                    "type": "fields",
                    "title": "General & Data Sharing Settings",
                    "items": [
                      {
                        "name": "Pets Data (Toggle)",
                        "desc": "When enabled, pet records and histories are shared across all associated branches.",
                        "helper": ""
                      },
                      {
                        "name": "Vets Data (Toggle)",
                        "desc": "When enabled, veterinarian profiles and data are shared across all associated branches.",
                        "helper": ""
                      },
                      {
                        "name": "Plan Data (Toggle)",
                        "desc": "When enabled, care plans and memberships are shared across all associated branches.",
                        "helper": ""
                      },
                      {
                        "name": "Inventory Data — Stock (Toggle)",
                        "desc": "Controls whether inventory stock levels and item information are shared between associated branches.",
                        "helper": ""
                      },
                      {
                        "name": "Allow Branches to Create and Edit Stock (Toggle)",
                        "desc": "When enabled, sub-branches can create new stock items or modify existing ones, and these changes will sync across all branches.",
                        "helper": ""
                      },
                      {
                        "name": "Inventory Data — Services (Toggle)",
                        "desc": "Controls whether the master list of services is shared across associated branches.",
                        "helper": ""
                      },
                      {
                        "name": "Allow Branches to Create and Edit Services (Toggle)",
                        "desc": "When enabled, sub-branches can create new services or modify existing ones, and these changes will sync across all branches.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Saving Actions",
                    "items": [
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Click to apply and enforce the configured data-sharing rules across the clinic network.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-billing-finance-settings",
        "title": "Billing & Finance",
        "description": "Configure invoice layout fields, footer content, live preview, and refund/return approval rules.",
        "groups": [
          {
            "id": "group-billing-finance-settings",
            "title": "Invoice & Billing Configuration",
            "description": "Control invoice fields, footer text, and live preview.",
            "items": [
              {
                "id": "billing-finance-settings",
                "title": "Billing & Finance",
                "summary": "Configure invoice layouts, visible data fields, footer content, and rules for refunds and returns.",
                "workflow": "Profile → View Account → Global Settings → Billing & Finance",
                "details": [
                  {
                    "type": "heading",
                    "title": "Billing & Finance"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-billing-finance.png",
                    "alt": "Billing & Finance Settings"
                  },
                  {
                    "type": "text",
                    "content": "The Billing & Finance section allows administrators to configure invoice layouts, control visible data fields, customize footer content, and configure rules for refunds and returns."
                  },
                  {
                    "type": "fields",
                    "title": "1. Invoice & Billing Fields — Header Information",
                    "items": [
                      {
                        "name": "Clinic logo (Checkbox)",
                        "desc": "Toggles the visibility of the clinic's logo.",
                        "helper": ""
                      },
                      {
                        "name": "Clinic name, address & Tax/GSTIN (Checkbox)",
                        "desc": "Toggles core clinic identity details.",
                        "helper": ""
                      },
                      {
                        "name": "Invoice number & date (Checkbox)",
                        "desc": "Toggles the official invoice tracking details.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Patient & Owner",
                    "items": [
                      {
                        "name": "Pet name, species & breed (Checkbox)",
                        "desc": "Toggles the patient's biological details.",
                        "helper": ""
                      },
                      {
                        "name": "Owner name (Checkbox)",
                        "desc": "Toggles the pet parent's name.",
                        "helper": ""
                      },
                      {
                        "name": "Owner address (Checkbox)",
                        "desc": "Toggles the pet parent's address.",
                        "helper": ""
                      },
                      {
                        "name": "Case ID (Checkbox)",
                        "desc": "Toggles the internal medical case reference number.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Clinical Context",
                    "items": [
                      {
                        "name": "Attending vet name (Checkbox)",
                        "desc": "Toggles the name of the consulting veterinarian.",
                        "helper": ""
                      },
                      {
                        "name": "Appointment date & time (Checkbox)",
                        "desc": "Toggles the specific schedule of the visit.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Line Items",
                    "items": [
                      {
                        "name": "HSN / SAC code (Checkbox)",
                        "desc": "Toggles tax classification codes for items/services.",
                        "helper": ""
                      },
                      {
                        "name": "Batch number & expiry (Checkbox)",
                        "desc": "Toggles pharmacy/inventory batch details.",
                        "helper": ""
                      },
                      {
                        "name": "Tax breakup (Checkbox)",
                        "desc": "Toggles the detailed split of applied taxes.",
                        "helper": ""
                      },
                      {
                        "name": "Discount % and code (Checkbox)",
                        "desc": "Toggles the visibility of applied promotional codes and discount rates.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "2. Footer Configuration",
                    "items": [
                      {
                        "name": "Disclaimer Text (Text Field)",
                        "desc": "Enter specific legal or medical disclaimers to print at the bottom of the invoice.",
                        "helper": ""
                      },
                      {
                        "name": "Footer Content (Text Field)",
                        "desc": "Enter additional standardized footer content (e.g., clinic greetings, return policies).",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "3. Live Preview",
                    "items": [
                      {
                        "name": "Live Preview (Display Panel)",
                        "desc": "A real-time visual representation on the right side of the screen. Updates instantly as fields are toggled, allowing administrators to verify the invoice layout before saving.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "heading",
                    "title": "4. Approval Rules"
                  },
                  {
                    "type": "text",
                    "content": "This section allows administrators to set monetary thresholds and define exactly who must sign off on sensitive financial actions. Approvers can be designated by staff role, a specific person, or both."
                  },
                  {
                    "type": "fields",
                    "title": "Refunds & Returns",
                    "items": [
                      {
                        "name": "Auto approve (Radio Button)",
                        "desc": "Automatically processes all refunds and returns without requiring management intervention.",
                        "helper": ""
                      },
                      {
                        "name": "Requires approval (Radio Button)",
                        "desc": "Enforces a structured, multi-level approval workflow based on configured financial limits.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Configuring Approval Tiers",
                    "items": [
                      {
                        "name": "Tier 1, Tier 2, etc. (Sections)",
                        "desc": "Distinct levels of approval required based on the monetary value of the return. Shown when \"Requires approval\" is active so administrators can build escalating authorization workflows.",
                        "helper": ""
                      },
                      {
                        "name": "₹ Min / ₹ Max (Text Fields)",
                        "desc": "Define the monetary range for the specific tier. The system enforces logical continuity (e.g., the minimum of a new tier must logically follow the maximum of the previous tier).",
                        "helper": ""
                      },
                      {
                        "name": "By role (Checkboxes)",
                        "desc": "Select which standard staff roles (e.g., Front Desk, Billing, Practice Manager, Clinic Owner) have the authority to approve returns falling within this tier's financial range.",
                        "helper": ""
                      },
                      {
                        "name": "By specific person (Search/Dropdown)",
                        "desc": "Optionally designate a specific staff member by name to approve returns for this tier. This can be used in combination with role-based approvers or as a standalone approver.",
                        "helper": ""
                      },
                      {
                        "name": "+ Add threshold tier (Button)",
                        "desc": "Click to create an additional approval level for higher-value refunds that require stricter administrative oversight.",
                        "helper": ""
                      },
                      {
                        "name": "Delete (Icon)",
                        "desc": "Click to remove a configured threshold tier.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-consultation-settings",
        "title": "Consultation Settings",
        "description": "Manage online booking, operating hours, EMR sharing, and unit measurements.",
        "groups": [
          {
            "id": "group-consultation-settings",
            "title": "Consultation Configuration",
            "description": "Appointment lifecycle, entity hours, EMR visibility, and units.",
            "items": [
              {
                "id": "consultation-settings-kb",
                "title": "Consultation Settings",
                "summary": "Manage online booking permissions, appointment lifecycles, branch operating hours, and customer-facing EMR visibility.",
                "workflow": "Profile → View Account → Global Settings → Consultation Settings",
                "details": [
                  {
                    "type": "heading",
                    "title": "Consultation Settings"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-consultation-01.png",
                    "alt": "Consultation Settings Overview"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-consultation-02.png",
                    "alt": "Entities & Operating Hours"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-consultation-03.png",
                    "alt": "Holiday Calendar and EMR Sharing"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-consultation-04.png",
                    "alt": "EMR Visibility and Unit Measurement Settings"
                  },
                  {
                    "type": "text",
                    "content": "The Consultation Settings section manages online booking permissions, appointment lifecycles, branch operating hours, and customer-facing EMR visibility."
                  },
                  {
                    "type": "fields",
                    "title": "1. General Appointment Settings",
                    "items": [
                      {
                        "name": "Allow Online Booking (Toggle)",
                        "desc": "Controls whether customers can self-book appointments through the Patient App or booking widget.",
                        "helper": ""
                      },
                      {
                        "name": "Auto approved (Radio Button)",
                        "desc": "Automatically confirms online bookings.",
                        "helper": ""
                      },
                      {
                        "name": "Require approval (Radio Button)",
                        "desc": "Places online bookings into a pending state. Access to approve these can be configured by specific staff roles.",
                        "helper": ""
                      },
                      {
                        "name": "Auto-Close Appointments (Dropdown/Input)",
                        "desc": "Automatically ends a consultation, finalizes the bill, and completes the appointment after a configured number of days of inactivity.",
                        "helper": ""
                      },
                      {
                        "name": "Allow Edit Consultations (Toggle)",
                        "desc": "Controls whether staff can edit EMR records and consultation details after a consultation has been officially closed. Access to edit can be restricted by role.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "2. Entities & Operating Hours",
                    "items": [
                      {
                        "name": "Entity Selection (Tabs)",
                        "desc": "Choose between Clinic, Pharmacy, or Pet Shop to configure specific settings for that entity.",
                        "helper": ""
                      },
                      {
                        "name": "Entity Name (Text Field)",
                        "desc": "Enter the specific name of the entity.",
                        "helper": ""
                      },
                      {
                        "name": "Tax ID (Text Field)",
                        "desc": "Enter the applicable Tax ID for this entity.",
                        "helper": ""
                      },
                      {
                        "name": "Address (Text Field)",
                        "desc": "Enter the physical location of this entity.",
                        "helper": ""
                      },
                      {
                        "name": "Consultation Types Offered (Checkboxes)",
                        "desc": "Select which services this entity provides (Virtual, Physical, Home Visit).",
                        "helper": ""
                      },
                      {
                        "name": "Day (Checkbox)",
                        "desc": "Enable or disable specific days of the week.",
                        "helper": ""
                      },
                      {
                        "name": "Start Time / End Time (Time Pickers)",
                        "desc": "Set the operational hours for the selected day.",
                        "helper": ""
                      },
                      {
                        "name": "+ (Button)",
                        "desc": "Add an additional time slot to the same day (e.g., to accommodate a lunch break: 08:00–12:00 and 13:00–20:00).",
                        "helper": ""
                      },
                      {
                        "name": "- (Button)",
                        "desc": "Remove an existing time slot.",
                        "helper": ""
                      },
                      {
                        "name": "Slot Duration (Dropdown)",
                        "desc": "Determines the standard length (in minutes) of an appointment slot for this entity.",
                        "helper": ""
                      },
                      {
                        "name": "Holiday Calendar (List/Input)",
                        "desc": "Add recognized holidays to block out the schedule.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "3. EMR/Case Summary Sharing with Customer",
                    "items": [
                      {
                        "name": "EMR Visibility Toggles",
                        "desc": "Enable or disable customer visibility for: Admission Remarks, Red Flags / Inpatient Alerts, Inpatient Instructions, SOAP Notes, Procedures, Administered, Memo, Preventive, Diagnostics, and Prescriptions.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "4. Unit Measurement Settings",
                    "items": [
                      {
                        "name": "Temperature (Radio Buttons)",
                        "desc": "Choose between °C or °F.",
                        "helper": ""
                      },
                      {
                        "name": "Weight (Radio Buttons)",
                        "desc": "Choose between kg or lb.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "5. Saving Actions",
                    "items": [
                      {
                        "name": "Save Changes (Button)",
                        "desc": "Located at the top-right of the page. Click to apply all configurations made across the Consultation Settings page.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-inventory-settings-kb",
        "title": "Inventory Settings",
        "description": "Configure approval gates for master items, transfers, and stock adjustments.",
        "groups": [
          {
            "id": "group-inventory-settings-kb",
            "title": "Inventory Approvals",
            "description": "Master item creation, transfers, and adjustments.",
            "items": [
              {
                "id": "inventory-global-settings-kb",
                "title": "Inventory Approvals",
                "summary": "Configure approval gates for master data creation, stock transfers, and manual stock adjustments.",
                "workflow": "Profile → View Account → Global Settings → Inventory",
                "details": [
                  {
                    "type": "heading",
                    "title": "Inventory"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-inventory.png",
                    "alt": "Inventory Approval Settings"
                  },
                  {
                    "type": "text",
                    "content": "The Inventory section allows administrators to configure strict approval gates for master data creation, stock transfers, and manual stock adjustments to maintain control over clinical supplies and services."
                  },
                  {
                    "type": "fields",
                    "title": "Master Item Creation",
                    "items": [
                      {
                        "name": "New inventory item (Radio Buttons)",
                        "desc": "Determines if a newly created stock-keeping item (e.g., medicine, consumable) requires administrative sign-off before it becomes usable. Choose between Auto approve or Requires approval.",
                        "helper": ""
                      },
                      {
                        "name": "New service item (Radio Buttons)",
                        "desc": "Determines if a newly created billable service line requires administrative sign-off before it can be added to customer invoices. Choose between Auto approve or Requires approval.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Stock Transfers",
                    "items": [
                      {
                        "name": "Within the same branch (Radio Buttons)",
                        "desc": "Sets the approval rule for stock moved between internal locations inside a single branch (e.g., from the Store Room to the Dispensary). Choose between Auto approve or Requires approval.",
                        "helper": ""
                      },
                      {
                        "name": "Between branches (Radio Buttons)",
                        "desc": "Sets the approval rule for stock transferred from one clinic branch to another. Choose between Auto approve or Requires approval.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "Stock Adjustments",
                    "items": [
                      {
                        "name": "Manual stock count correction (Radio Buttons)",
                        "desc": "Sets the approval rule for manual adjustments that change the system-of-record inventory count, serving as the main control point against stock shrinkage.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "Saving Actions",
                    "items": [
                      {
                        "name": "Save changes (Button)",
                        "desc": "Click to apply and enforce the configured inventory approval settings across the platform.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "topic-roles-permissions-kb",
        "title": "Roles & Permissions",
        "description": "Assign module access levels for predefined staff roles.",
        "groups": [
          {
            "id": "group-roles-permissions-kb",
            "title": "Role Access Control",
            "description": "Standard roles and access levels.",
            "items": [
              {
                "id": "roles-permissions-settings-kb",
                "title": "Roles & Permissions",
                "summary": "Adjust module access for predefined staff roles using No access, Read only, or Full control.",
                "workflow": "Profile → View Account → Global Settings → Roles & Permissions",
                "details": [
                  {
                    "type": "heading",
                    "title": "Roles and Permissions"
                  },
                  {
                    "type": "image",
                    "src": "/assets/images/knowledgebase/general-settings-roles-permissions.png",
                    "alt": "Roles & Permissions Settings"
                  },
                  {
                    "type": "text",
                    "content": "The Roles & Permissions section allows administrators to adjust specific module access for predefined staff roles within the branch. The standard roles and their defaults are fixed platform-wide to maintain consistency."
                  },
                  {
                    "type": "fields",
                    "title": "1. Standard Roles (Selection List)",
                    "items": [
                      {
                        "name": "Billing",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Front Desk",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Groomer",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Inventory Manager",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Lab/Radiology Technician",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Paravet/Nurse",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Pharmacist",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Practice Manager",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      },
                      {
                        "name": "Veterinarian",
                        "desc": "Select this role to view or adjust its module permissions.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "2. Access Level Options",
                    "items": [
                      {
                        "name": "No access [ — ] (Radio Button)",
                        "desc": "The role cannot see, access, or interact with the module.",
                        "helper": ""
                      },
                      {
                        "name": "Read only [ R ] (Radio Button)",
                        "desc": "The role can view the data within the module but cannot make any edits, additions, or deletions.",
                        "helper": ""
                      },
                      {
                        "name": "Full control [ FC ] (Radio Button)",
                        "desc": "The role has complete administrative permissions to view, create, modify, and delete data within the module.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "fields",
                    "title": "3. Configurable Modules",
                    "items": [
                      {
                        "name": "Dashboard/Appointments (Radio Buttons)",
                        "desc": "Controls access to the scheduling and main overview screens.",
                        "helper": ""
                      },
                      {
                        "name": "Pets (Radio Buttons)",
                        "desc": "Controls access to patient profiles and medical histories.",
                        "helper": ""
                      },
                      {
                        "name": "Vets (Radio Buttons)",
                        "desc": "Controls access to veterinarian profiles and schedules.",
                        "helper": ""
                      },
                      {
                        "name": "Billing (Radio Buttons)",
                        "desc": "Controls access to invoicing, payments, and financial generation.",
                        "helper": ""
                      },
                      {
                        "name": "Inventory (Radio Buttons)",
                        "desc": "Controls access to stock management, transfers, and adjustments.",
                        "helper": ""
                      },
                      {
                        "name": "Diagnostics (Radio Buttons)",
                        "desc": "Controls access to lab orders and radiology results.",
                        "helper": ""
                      },
                      {
                        "name": "Pharmacy (Radio Buttons)",
                        "desc": "Controls access to the dispensary and medical stock.",
                        "helper": ""
                      },
                      {
                        "name": "Plan (Radio Buttons)",
                        "desc": "Controls access to care plans and memberships.",
                        "helper": ""
                      },
                      {
                        "name": "Reports (Radio Buttons)",
                        "desc": "Controls access to operational, clinical, and financial analytics.",
                        "helper": ""
                      }
                    ]
                  },
                  {
                    "type": "buttons",
                    "title": "4. Saving Actions",
                    "items": [
                      {
                        "name": "Save changes (Button)",
                        "desc": "Click to apply and enforce the newly configured role permissions for the branch.",
                        "helper": ""
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
]
